import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { estimateDuration, calculateNextTime, buildItinerary, buildPhotoContext, isMajorTouristCity, recalculateTimes } from './utils';
import { CityDiscoveryPanel, AttrDiscoveryPanel, IDLE_CITY, IDLE_ATTR } from './DiscoveryPanels';
import { Plus, MapPin, Calendar, Clock, Edit2, Save, Trash2, ChevronDown, ChevronUp, GripVertical, Zap, User, LogOut, X, Check, Copy, Bookmark, Images, Share2, MessageSquare, Sparkles, ArrowRight } from 'lucide-react';
import { getLocale, makeT } from './locales';
import track, { trackPageView, setAnalyticsEnabled, analyticsEnabledInitial, setCookieConsent, getCookieConsent } from './analytics';
import { fetchTopAttractions } from './placesCache';
import { ReactSortable } from 'react-sortablejs';

// mode='search': fuzzy search (good for cities). mode='direct': exact title lookup (good for attractions).
const DAY_GRADIENTS = [
  'from-slate-300 to-slate-500',
  'from-purple-300 to-purple-500',
  'from-teal-300 to-emerald-400',
  'from-orange-300 to-amber-400',
  'from-rose-300 to-rose-500',
  'from-sky-300 to-cyan-400',
  'from-violet-300 to-indigo-400',
];

const ATTRACTION_FILTERS = [
  { label: 'All',           types: null,                                                                                                                                                  query: city => `top attractions ${city}` },
  { label: 'Culture',       types: ['museum','art_gallery','church','hindu_temple','mosque','synagogue','cemetery','library'],                                                             query: city => `museums historic sites ${city}` },
  { label: 'Nature',        types: ['park','natural_feature','zoo','aquarium','campground'],                                                                                              query: city => `parks nature outdoors ${city}` },
  { label: 'Entertainment', types: ['amusement_park','movie_theater','stadium','casino','night_club','bowling_alley'],                                                                    query: city => `entertainment activities ${city}` },
  { label: 'Shopping',      types: ['shopping_mall','store','clothing_store','department_store','market'],                                                                                query: city => `shopping ${city}` },
  { label: 'Food & Drink',  types: ['restaurant','cafe','bar','bakery','food','meal_takeaway'],                                                                                           query: city => `restaurants cafes bars ${city}` },
  { label: 'Kids',          types: ['amusement_park','aquarium','zoo','bowling_alley','movie_theater','stadium','campground','playground','toy_store','park','tourist_attraction','natural_feature'], query: city => `family friendly attractions things to do with kids ${city}` },
  { label: 'Seniors',       types: ['museum','art_gallery','park','spa','church','hindu_temple','mosque','synagogue','cemetery','library','tourist_attraction','natural_feature'],         query: city => `senior friendly accessible attractions ${city}` },
];

const HARDCODED_DESTINATIONS = [
  { name: 'Bangkok',      country: 'Thailand',      key: 'dest_bangkok',      tags: ['Culture', 'Food & Drink'] },
  { name: 'Paris',        country: 'France',         key: 'dest_paris',        tags: ['Culture', 'Food & Drink'] },
  { name: 'London',       country: 'UK',             key: 'dest_london',       tags: ['Culture', 'Entertainment'] },
  { name: 'Dubai',        country: 'UAE',            key: 'dest_dubai',        tags: ['Shopping', 'Entertainment'] },
  { name: 'Singapore',    country: 'Singapore',      key: 'dest_singapore',    tags: ['Food & Drink', 'Nature'], wikiTitle: 'Marina Bay, Singapore' },
  { name: 'Tokyo',        country: 'Japan',          key: 'dest_tokyo',        tags: ['Culture', 'Food & Drink'] },
  { name: 'New York',     country: 'USA',            key: 'dest_new_york',     tags: ['Culture', 'Entertainment'] },
  { name: 'Istanbul',     country: 'Turkey',         key: 'dest_istanbul',     tags: ['Culture', 'Food & Drink'] },
  { name: 'Kuala Lumpur', country: 'Malaysia',       key: 'dest_kuala_lumpur', tags: ['Food & Drink', 'Shopping'] },
  { name: 'Seoul',        country: 'South Korea',    key: 'dest_seoul',        tags: ['Food & Drink', 'Culture'] },
];

const WIKI_SUBDOMAIN = { 'zh-TW': 'zh', 'zh-CN': 'zh' };
const fetchWikipediaDescription = async (query, mode = 'search', lang = 'en') => {
  const cleanQuery = query.includes(',') ? query.split(',')[0].trim() : query;
  // Build search query from full string: "Lake City, MN, USA" → "Lake City MN"
  // Replace all commas with spaces, strip trailing country name, collapse spaces.
  // This avoids disambiguation (bare "Lake City") and double-spaces from ", MN" → " MN".
  const searchQuery = mode === 'search' && query.includes(',')
    ? query.replace(/,\s*/g, ' ').replace(/\s+USA$/i, '').trim()
    : cleanQuery;
  const subdomain = WIKI_SUBDOMAIN[lang] ?? lang;
  const host = `https://${subdomain}.wikipedia.org`;
  try {
    const url = mode === 'direct'
      ? `${host}/w/api.php?action=query&prop=extracts&exintro&explaintext&titles=${encodeURIComponent(cleanQuery)}&format=json&origin=*&redirects=1`
      : `${host}/w/api.php?action=query&generator=search&gsrsearch=${encodeURIComponent(searchQuery)}&prop=extracts&exintro&explaintext&format=json&origin=*&gsrlimit=5`;
    const r = await fetch(url);
    const data = await r.json();
    const pages = data.query?.pages;
    if (!pages) {
      if (mode === 'direct') return fetchWikipediaDescription(query, 'search', lang);
      if (lang !== 'en') return fetchWikipediaDescription(query, mode, 'en');
      return null;
    }
    if (mode === 'search') {
      const candidates = Object.values(pages).sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
      const nameLC = cleanQuery.toLowerCase();
      const goodExtract = (page) => {
        const e = page?.extract?.trim();
        return e && !e.toLowerCase().includes('may refer to') ? e : null;
      };
      // Priority 1: exact title match ("Chengdu" → "Chengdu", not "Chengdu Airlines")
      for (const page of candidates) {
        if (page.title?.toLowerCase() === nameLC) {
          const e = goodExtract(page); if (e) return e;
        }
      }
      // Priority 2: "CityName, Region" — clearly the place article
      for (const page of candidates) {
        if (page.title?.toLowerCase().startsWith(nameLC + ',')) {
          const e = goodExtract(page); if (e) return e;
        }
      }
      // Priority 3: any result starting with the name (original fallback)
      for (const page of candidates) {
        if (lang === 'en' && !page.title?.toLowerCase().startsWith(nameLC)) continue;
        const e = goodExtract(page); if (e) return e;
      }
      if (lang !== 'en') return fetchWikipediaDescription(query, mode, 'en');
      return null;
    }
    const page = Object.values(pages)[0];
    if (page.missing !== undefined) return fetchWikipediaDescription(query, 'search', lang);
    const extract = page?.extract?.trim();
    if (!extract || extract.toLowerCase().includes('may refer to')) return fetchWikipediaDescription(query, 'search', lang);
    return extract;
  } catch {
    if (lang !== 'en') return fetchWikipediaDescription(query, mode, 'en');
    return null;
  }
};

const fetchWikipediaThumbnail = async (cityName, wikiTitle) => {
  const title = wikiTitle || (cityName.includes(',') ? cityName.split(',')[0].trim() : cityName);
  try {
    const r = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(title)}`);
    if (!r.ok) return null;
    const data = await r.json();
    return data.thumbnail?.source || data.originalimage?.source || null;
  } catch {
    return null;
  }
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="max-w-md mx-auto bg-gray-50 min-h-screen flex items-center justify-center p-6">
          <div className="card p-6 text-center">
            <h2 className="text-lg font-bold text-red-600 mb-2">Something went wrong</h2>
            <p className="text-sm text-gray-500 mb-1">Error: {this.state.error?.message}</p>
            <p className="text-xs text-gray-400 mb-4">{this.state.error?.stack?.split('\n')[1]?.trim()}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium"
            >
              Reload
            </button>
            {/* Error boundary renders outside the component tree, so t() is not available here */}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// AttractionCard — shared media block used in Plan, Itinerary, and Snips tabs
//
// Renders: header slot → photo carousel → rating + YouTube → description → actions slot
// Prop       | Used by
// -----------|---------------------------------------------------------------
// header     | Plan tab (name/duration/Snip/Remove row above the photo)
// actions    | Itinerary (Edit/Move/Remove) and Snips (Add to Plan/Delete)
// onMorePhotoRefs | callback to persist additional fetched photo refs;
//              pass null to disable lazy-loading (Snips single-photo)
// photoKey   | key into shared photoIndices/fetchingPhotos/detailsFetched state
// loading    | show pulse while description + photos are both absent
// ─────────────────────────────────────────────────────────────────────────────
function AttractionCard({
  name, cityName, placeId, rating, ratingCount, description, photoRefs = [],
  photoKey, photoIndices, setPhotoIndices,
  fetchingPhotos, setFetchingPhotos, detailsFetched, setDetailsFetched,
  expandedDescriptions, setExpandedDescriptions,
  onMorePhotoRefs, loading = false, t, source,
  header, actions, mediaRowEnd,
}) {
  const carouselTouchRef = React.useRef(null);
  const hasTrackedSwipe = React.useRef(false);
  const staleRefetchAttempted = React.useRef(false);

  const handlePhotoError = async () => {
    if (staleRefetchAttempted.current || !placeId || !onMorePhotoRefs) return;
    staleRefetchAttempted.current = true;
    try {
      const r = await fetch(`/api/place-photo?placeId=${encodeURIComponent(placeId)}`);
      const { photoRefs: freshRefs } = await r.json();
      if (freshRefs?.length > 0) {
        onMorePhotoRefs(freshRefs);
        setPhotoIndices(prev => ({ ...prev, [photoKey]: 0 }));
        setDetailsFetched(prev => ({ ...prev, [photoKey]: true }));
      }
    } catch {}
  };
  const photoIdx = photoIndices[photoKey] || 0;
  const isFetchingPhoto = !!fetchingPhotos[photoKey];
  const isDetailsFetched = !!detailsFetched[photoKey];
  const isDescExpanded = !!expandedDescriptions[photoKey];

  const fetchMorePhotos = async () => {
    if (!placeId || isDetailsFetched || isFetchingPhoto || !onMorePhotoRefs) return;
    setFetchingPhotos(prev => ({ ...prev, [photoKey]: true }));
    try {
      const r = await fetch(`/api/place-photo?placeId=${encodeURIComponent(placeId)}`);
      const { photoRefs: newRefs } = await r.json();
      setDetailsFetched(prev => ({ ...prev, [photoKey]: true }));
      if (newRefs?.length > photoRefs.length) {
        onMorePhotoRefs(newRefs);
        setPhotoIndices(prev => ({ ...prev, [photoKey]: photoIdx + 1 }));
      }
    } catch { setDetailsFetched(prev => ({ ...prev, [photoKey]: true })); }
    finally { setFetchingPhotos(prev => ({ ...prev, [photoKey]: false })); }
  };

  const cityCtx = cityName || '';
  const ytUrl = `https://www.youtube.com/results?search_query=${encodeURIComponent(cityCtx ? `${name} ${cityCtx}` : name)}`;

  return (
    <>
      {header}
      {loading && !description && photoRefs.length === 0 && (
        <p className="text-xs text-gray-400 px-3 py-3 text-center animate-pulse">{t('loading_details')}</p>
      )}
      {photoRefs.length > 0 && (
        <div
          className="relative w-full h-48 lg:h-auto lg:aspect-[5/2] bg-gray-200 select-none"
          onTouchStart={e => { carouselTouchRef.current = e.touches[0].clientX; }}
          onTouchEnd={async e => {
            if (carouselTouchRef.current === null) return;
            const dx = e.changedTouches[0].clientX - carouselTouchRef.current;
            carouselTouchRef.current = null;
            if (Math.abs(dx) < 30) return;
            if (!hasTrackedSwipe.current) {
              track('carousel_first_swipe', { attraction_name: name, source });
              hasTrackedSwipe.current = true;
            }
            if (dx > 0) {
              if (photoIdx > 0) setPhotoIndices(prev => ({ ...prev, [photoKey]: photoIdx - 1 }));
            } else {
              if (photoIdx < photoRefs.length - 1) setPhotoIndices(prev => ({ ...prev, [photoKey]: photoIdx + 1 }));
              else await fetchMorePhotos();
            }
          }}
        >
          <img src={`/api/place-photo?ref=${encodeURIComponent(photoRefs[photoIdx])}&maxwidth=600`} alt={name} className="w-full h-full object-cover" onError={handlePhotoError} />
          {(() => {
            const canGoLeft = photoIdx > 0;
            const hasMoreKnown = photoIdx < photoRefs.length - 1;
            const mightHaveMore = !isDetailsFetched && !!placeId && !!onMorePhotoRefs;
            if (!canGoLeft && !hasMoreKnown && !mightHaveMore) return null;
            return (
              <>
                <button disabled={!canGoLeft} onClick={() => setPhotoIndices(prev => ({ ...prev, [photoKey]: photoIdx - 1 }))} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold disabled:opacity-25">‹</button>
                <button
                  disabled={isFetchingPhoto}
                  onClick={async () => { if (hasMoreKnown) setPhotoIndices(prev => ({ ...prev, [photoKey]: photoIdx + 1 })); else await fetchMorePhotos(); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold disabled:opacity-25"
                >{isFetchingPhoto ? '…' : '›'}</button>
                {photoRefs.length > 1 && (
                  <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
                    {photoRefs.map((_, i) => <div key={i} className={`w-2 h-2 rounded-full ${i === photoIdx ? 'bg-white' : 'bg-white/40'}`} />)}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
      <div className="px-3 py-2 flex items-center gap-3">
        {rating != null && (() => {
          const inner = <>
            <span className="text-xs text-amber-500">★ {rating.toFixed(1)}</span>
            {ratingCount != null && <span className="text-xs text-gray-400">({ratingCount.toLocaleString()})</span>}
          </>;
          return placeId
            ? <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name)}&query_place_id=${placeId}`} target="_blank" rel="noopener noreferrer" onClick={() => track('maps_clicked', { attraction_name: name, source })} className="flex items-center gap-1.5 hover:opacity-70 transition-opacity">{inner}</a>
            : <div className="flex items-center gap-1.5">{inner}</div>;
        })()}
        <a href={ytUrl} target="_blank" rel="noopener noreferrer" onClick={() => track('youtube_clicked', { attraction_name: name, source })} className="flex items-center gap-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 py-2 px-2.5 rounded-lg transition-colors flex-shrink-0">
          ▶ YouTube
        </a>
        {mediaRowEnd}
      </div>
      {description && (
        <div className="px-3 pb-2">
          <p className={`text-sm text-gray-600 ${isDescExpanded ? '' : 'line-clamp-3'}`}>{description}</p>
          <button
            onClick={() => {
              const next = !isDescExpanded;
              if (next) track('description_expanded', { attraction_name: name, source });
              setExpandedDescriptions(prev => ({ ...prev, [photoKey]: next }));
            }}
            className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-600 mt-1"
          >
            {isDescExpanded ? <><ChevronUp className="w-3 h-3" /> {t('less')}</> : <><ChevronDown className="w-3 h-3" /> {t('more')}</>}
          </button>
        </div>
      )}
      {actions}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TABLE OF CONTENTS — SniptripApp.js
//
//  MODULE LEVEL
//    Constants & helpers          DAY_GRADIENTS, ATTRACTION_FILTERS, WIKI_SUBDOMAIN
//    fetchWikipediaDescription    Wikipedia REST API helper (multilingual)
//    ErrorBoundary                Class component wrapping the whole app
//
//  SniptripApp() FUNCTION
//    State — core UI              screen, cities, attraction editing fields, notes
//    State — itinerary editing    editingActivity, pendingNewActivity, edit field states
//    State — auth & persistence   user, JWT, trips, snips, saved-trips loading
//    State — photo mode           photoFeed, indices, tutorial
//    State — admin                change-password and delete-user modals
//    State — plan UI              filters, discovery panels, autocomplete
//    State — language             lang cookie/navigator detection, t() helper
//    State — discovery panels     cityDiscovery, attrDiscovery
//    Constants (inline)           MOCK_CITIES_DB, MOCK_ATTRACTIONS_DB
//    Search functions             searchCities, searchAttractions, autoPopulateAttractions
//    useEffect hooks              debounce, localStorage, URL params, auto-save,
//                                 cross-device sync (visibility + 30s poll), banners, auth
//    Auth functions               mapAuthError, handleAuth, handleLogout
//    Trip/Snip persistence        loadTrips, loadSnips, snipItem, deleteSnip, addSnipTo…
//    Photo mode                   fetchPhotoAt, enterPhotoMode, photoFeedNext, touch handlers
//    Share / legal / trip mgmt    handleSave, handleShare, openLegal, clone/load/delete/new trip
//    Admin functions              openChangePassword, handleChangePassword, openDeleteUser
//    Plan helpers                 addCity, addAttraction, discovery context helpers
//    Empty-day discovery          patchEdd, selectEmptyDayDest, addEmptyDayAttraction
//    Remove helpers               removeCity, removeAttraction, fetchAttractionsForCity
//    Generate itinerary           generateItinerary
//    Chat mode                    handleChatSend, generateWithClaude, widget handlers
//    Itinerary editing            toggleDay, removeActivity, moveActivityToDay,
//                                 recalculateTimes calls, saveActivityEdit (with pinnedTime),
//                                 cancelEdit (removes pendingNewActivity on cancel),
//                                 addTransportation/meal/hotel, removeDay, addDay, openPrintView
//
//  JSX RENDER
//    Header + nav tabs            ~3532
//    Plan Screen                  ~3594
//    Itinerary Screen             ~4481
//    Saved Screen                 ~5868
//    User Menu + New Trip modal   ~6103
//    Auth Modal                   ~6325
//    Change Password Modal        ~6397
//    Delete User Modal            ~6442
//    Photo Mode Overlay           ~6488
//    Footer                       ~6672
//    Legal Page Overlay           ~6719
//    Feedback button + modal      ~6749
//    Cookie consent banner        ~6779
// ─────────────────────────────────────────────────────────────────────────────

// Approximate centroids for US states — used to bias city autocomplete toward the trip's region.
const US_STATE_CENTROIDS = {
  AL:[32.8,-86.8],AK:[64.2,-153.4],AZ:[34.3,-111.1],AR:[34.9,-92.4],CA:[36.8,-119.4],
  CO:[39.0,-105.5],CT:[41.6,-72.7],DE:[39.0,-75.5],FL:[27.8,-81.6],GA:[32.7,-83.2],
  HI:[20.8,-156.3],ID:[44.4,-114.6],IL:[40.0,-89.2],IN:[39.8,-86.1],IA:[42.0,-93.2],
  KS:[38.5,-98.4],KY:[37.5,-85.3],LA:[31.2,-91.8],ME:[45.4,-69.0],MD:[39.0,-76.8],
  MA:[42.2,-71.5],MI:[44.3,-85.6],MN:[46.4,-93.1],MS:[32.7,-89.7],MO:[38.5,-92.5],
  MT:[47.0,-110.5],NE:[41.5,-99.9],NV:[39.5,-116.9],NH:[43.7,-71.6],NJ:[40.1,-74.5],
  NM:[34.5,-106.0],NY:[42.9,-75.5],NC:[35.5,-79.4],ND:[47.5,-100.5],OH:[40.4,-82.8],
  OK:[35.6,-96.9],OR:[44.1,-120.5],PA:[40.9,-77.8],RI:[41.7,-71.5],SC:[33.9,-80.9],
  SD:[44.4,-100.2],TN:[35.9,-86.7],TX:[31.5,-99.3],UT:[39.3,-111.1],VT:[44.0,-72.7],
  VA:[37.5,-79.5],WA:[47.4,-120.5],WV:[38.6,-80.6],WI:[44.3,-89.6],WY:[43.0,-107.5],
};

// Places Autocomplete types that indicate a region/area rather than a specific city.
// When a user adds one of these in non-chat mode, we auto-expand it to individual cities via AI.
const REGION_TYPES = new Set(['administrative_area_level_1', 'country', 'colloquial_area', 'natural_feature']);

// Types that block city adds specifically in chat mode. Narrower than REGION_TYPES:
// natural_feature is intentionally excluded so places like Grand Canyon, Iguazu Falls,
// Yosemite, Niagara Falls are accepted — they're valid travel destinations, not admin regions.
const CHAT_BLOCK_TYPES = new Set(['country', 'administrative_area_level_1', 'colloquial_area']);

// Types that indicate transport infrastructure — should never be added as a city destination.
const TRANSPORT_TYPES = new Set(['airport', 'transit_station', 'train_station', 'bus_station', 'subway_station', 'light_rail_station']);

// City-states and micro-nations that return 'country' from Places but are single destinations.
// These must be excluded from region expansion despite matching REGION_TYPES.
const CITY_STATE_EXCEPTIONS = new Set([
  // City-states and micro-nations
  'singapore', 'hong kong', 'macau', 'macao', 'monaco', 'vatican', 'vatican city',
  'luxembourg', 'liechtenstein', 'san marino', 'andorra', 'maldives', 'bahrain',
  'djibouti', 'kuwait', 'malta', 'brunei', 'qatar',
  // Major cities that are also their own administrative region (would wrongly trigger expansion)
  'tokyo', 'seoul', 'beijing', 'shanghai', 'chongqing', 'tianjin',
  'bangkok', 'vienna', 'berlin', 'hamburg', 'bremen', 'brussels',
  'moscow', 'istanbul', 'mexico city', 'buenos aires', 'lima',
]);

// True if two city name strings refer to the same area (containment match, comma-stripped).
// "Tahoe" matches "South Lake Tahoe, CA"; "Yosemite" matches "Yosemite Valley, CA".
const citiesMatch = (a, b) => {
  const norm = s => s.split(',')[0].toLowerCase().trim();
  const na = norm(a), nb = norm(b);
  return na === nb || na.includes(nb) || nb.includes(na);
};

// True if the Google Places result's formatted_address is plausibly in the same US state as
// the expected city name (e.g., "South Lake Tahoe, CA" → state "CA"). For non-US or ambiguous
// cities (no 2-letter state code found), always returns true to avoid false negatives.
// Extract "City, ST" from a Google Places formatted_address string.
const extractCityFromAddress = (fullName) => {
  const parts = (fullName || '').split(',').map(s => s.trim());
  for (let i = 0; i < parts.length - 1; i++) {
    const next = parts[i + 1];
    if (/^[A-Z]{2}(\s+\d{5})?$/.test(next)) {
      const cityPart = /county/i.test(parts[i]) && i > 0 ? parts[i - 1] : parts[i];
      return `${cityPart}, ${next.replace(/\s+\d.*/, '').trim()}`;
    }
  }
  return null;
};

const isGeographicallyRelevant = (place, expectedCity) => {
  const stateMatch = (expectedCity || '').match(/\b([A-Z]{2})\b/);
  if (!stateMatch) return true;
  const state = stateMatch[1];
  const addr = (place.formatted_address || place.vicinity || '').toUpperCase();
  return new RegExp(`\\b${state}\\b`).test(addr);
};

export default function SniptripApp() {
  // ── Core UI state ─────────────────────────────────────────────────────────
  const [screen, setScreen] = useState(() => {
    try { return sessionStorage.getItem('sniptrip_session_screen') || 'input'; } catch { return 'input'; }
  });
  const [cities, setCities] = useState([]); // Array of {name: string, attractions: []}
  const [currentCity, setCurrentCity] = useState('');
  const [selectedCityIndex, setSelectedCityIndex] = useState(null);
  const [currentAttraction, setCurrentAttraction] = useState('');
  const [itinerary, setItinerary] = useState(() => {
    try {
      const s = sessionStorage.getItem('sniptrip_session_itinerary');
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  });
  // ── Itinerary editing state ───────────────────────────────────────────────
  // editingActivity: which activity the edit dialog is open for
  // pendingNewActivity: when a meal/hotel is added and the dialog opens before the user saves,
  //   this tracks the position so cancelEdit can remove the uncommitted activity
  const [editingActivity, setEditingActivity] = useState(null); // {dayIndex, activityIndex}
  const [pendingNewActivity, setPendingNewActivity] = useState(null); // {dayIndex, activityIndex}
  const [editTime, setEditTime] = useState('');
  const [editDuration, setEditDuration] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editActivityNote, setEditActivityNote] = useState('');
  const [editNameQuery, setEditNameQuery] = useState('');
  const [editNameResults, setEditNameResults] = useState([]);
  const [editNameLoading, setEditNameLoading] = useState(false);
  const [editHotelNights, setEditHotelNights] = useState(1);
  const [dayAddDiscovery, setDayAddDiscovery] = useState({}); // { [dayId]: AttrDiscovery shape }
  const [addingDayAttr, setAddingDayAttr] = useState(null); // { dayId, name }
  const [editingNoteDay, setEditingNoteDay] = useState(null);
  const [noteText, setNoteText] = useState('');


  // ── Auth & persistence state ─────────────────────────────────────────────
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sniptrip_user')); } catch { return null; }
  });
  const [currentTripId, setCurrentTripId] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authReason, setAuthReason] = useState(null); // 'snip' | null
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [showNewTripConfirm, setShowNewTripConfirm] = useState(false);
  const [showNewTripTabConfirm, setShowNewTripTabConfirm] = useState(false);
  const [pendingLoadTrip, setPendingLoadTrip] = useState(null);

  // ── Chat mode state ──────────────────────────────────────────────────────────
  const [chatMode, setChatMode] = useState(false);
  const [chatDisplayMessages, setChatDisplayMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [showChatPlanSummary, setShowChatPlanSummary] = useState(false);
  const [chatWidgetStates, setChatWidgetStates] = useState({}); // widgetId → disc state

  const [renamingTripId, setRenamingTripId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [editingItineraryName, setEditingItineraryName] = useState(false);
  const [dayBannerPhotos, setDayBannerPhotos] = useState({});
  // cityName → [{ref, placeId}] — all results with photos, for teaser dedup fallback
  const [dayBannerAlternatives, setDayBannerAlternatives] = useState({});
  const [bannerErrDays, setBannerErrDays] = useState(new Set()); // day.ids whose saved photoRef failed
  const [savedTrips, setSavedTrips] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [snips, setSnips] = useState([]);
  const [expandedSnips, setExpandedSnips] = useState({});
  const [snipDetails, setSnipDetails] = useState({}); // id → { loading, photoRef, description }
  const [expandedFolders, setExpandedFolders] = useState({});
  const [photoMode, setPhotoMode] = useState(false);
  const [photoFeed, setPhotoFeed] = useState([]);
  const [photoFeedIdx, setPhotoFeedIdx] = useState(0);
  const [photoFeedLoading, setPhotoFeedLoading] = useState(false);
  const [photoFeedLoadingMsgIdx, setPhotoFeedLoadingMsgIdx] = useState(0);
  const [photoFeedFetchingPhoto, setPhotoFeedFetchingPhoto] = useState(false);
  const [photoFeedFetchingMore, setPhotoFeedFetchingMore] = useState(false);
  const photoFeedCtxRef = useRef({ cities: [], filters: [] }); // ref so fetchMorePhotoFeed always reads current context, no stale closure
  const photoFeedRef = useRef([]); // mirrors photoFeed state; read by async closures so they always see current value
  const photoFeedFetchingMoreRef = useRef(false); // mirrors photoFeedFetchingMore state; same reason
  const [showPhotoTutorial, setShowPhotoTutorial] = useState(false);
  const autoSaveTimerRef = useRef(null);
  const sessionTripRestored = useRef(false); // prevent double-restore after tab eviction
  const justRefreshedRef = useRef(false); // skip auto-save/hasUnsavedChanges when itinerary was set by a background refresh
  const hasUnsavedChangesRef = useRef(false); // mirror of hasUnsavedChanges for use inside stable callbacks
  const refreshCurrentTripRef = useRef(null); // stable ref so interval/listener don't need to re-register on every save cycle
  const dayBannerFetchingRef = useRef(new Set());
  const editSearchTimerRef = useRef(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [trackingEnabled, setTrackingEnabled] = useState(analyticsEnabledInitial);
  const [showCookieBanner, setShowCookieBanner] = useState(() => getCookieConsent() === null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [cpNewPassword, setCpNewPassword] = useState('');
  const [cpError, setCpError] = useState('');
  const [cpSuccess, setCpSuccess] = useState('');
  const [cpLoading, setCpLoading] = useState(false);
  const [cpUsers, setCpUsers] = useState([]);
  const [cpSelectedUserId, setCpSelectedUserId] = useState('');
  const [showDeleteUser, setShowDeleteUser] = useState(false);
  const [duUsers, setDuUsers] = useState([]);
  const [duSelectedEmail, setDuSelectedEmail] = useState('');
  const [duError, setDuError] = useState('');
  const [duSuccess, setDuSuccess] = useState('');
  const [duLoading, setDuLoading] = useState(false);

  // ── Popular destinations carousel ───────────────────────────────────────
  const [popularCities, setPopularCities] = useState([]);
  const [destImages, setDestImages] = useState({});
  const [heroCarouselIdx, setHeroCarouselIdx] = useState(0);

  // ── Plan UI state ────────────────────────────────────────────────────────
  const [pendingCityAdd, setPendingCityAdd] = useState(null); // { cityData, source } while new-trip prompt is showing
  const [autoAddLoading, setAutoAddLoading] = useState({}); // cityIdx → bool, true while Claude generates attractions
  const [autoAddMsgIdx, setAutoAddMsgIdx] = useState(0);
  const [dayToast, setDayToast] = useState(null); // { message } — shown briefly after adding to a day
  const dayToastTimerRef = useRef(null);
  const [filterOnboardingCity, setFilterOnboardingCity] = useState(null); // cityIdx showing first-time filter prompt
  const [filterOnboardingPending, setFilterOnboardingPending] = useState([]); // chips toggled inside onboarding modal
  const filterOnboardingDoneRef = useRef((() => { try { return localStorage.getItem('sniptrip_filter_onboarding') === '1'; } catch { return false; } })());
  const [showFiltersExpanded, setShowFiltersExpanded] = useState(false);
  const [pendingFilterRefresh, setPendingFilterRefresh] = useState(false);
  const scrollContainerRef = useRef(null);
  const [collapsedCities, setCollapsedCities] = useState({}); // { [cityIdx]: true } — collapsed city sections in Plan tab
  const [addedAttractionCity, setAddedAttractionCity] = useState(null); // cityIdx of last add, for flash
  const [lastAddedKey, setLastAddedKey] = useState(null); // "cityIdx-attrIdx" of just-added card, for green flash
  const [attrAddToast, setAttrAddToast] = useState(null); // { message } shown briefly after add
  const [dayChipPicker, setDayChipPicker] = useState(null); // dayId showing inline city-add search
  const [dayAutoOpenKey, setDayAutoOpenKey] = useState({}); // { [dayId]: timestamp } forces AttrDiscoveryPanel remount+autoOpen
  const [dayCityOverride, setDayCityOverride] = useState({}); // { [dayId]: cityName } — alternate city for that day's discovery panel
  const [dayInlineCityQuery, setDayInlineCityQuery] = useState('');
  const [dayInlineCitySuggestions, setDayInlineCitySuggestions] = useState([]);
  const [pendingAutoPopulate, setPendingAutoPopulate] = useState(null); // { cityIndex, cityToAdd } — prompt before auto-populate
  const [photoIndices, setPhotoIndices] = useState({}); // key: "cityIdx-attrIdx" → current photo index
  const [detailsFetched, setDetailsFetched] = useState({}); // photoKey → bool: Place Details already fetched
  const [fetchingPhotos, setFetchingPhotos] = useState({}); // photoKey → bool: fetch in progress
  const [expandedDescriptions, setExpandedDescriptions] = useState({}); // key → bool: description expanded
  const [expandedActivities, setExpandedActivities] = useState({}); // "act-{id}" → bool: activity detail expanded
  const [activityDescriptions, setActivityDescriptions] = useState({}); // expandKey → description (fallback when not in cities)
  const [fetchingDescriptions, setFetchingDescriptions] = useState({}); // expandKey → bool
  const [attractionFilters, setAttractionFilters] = useState(() => {
    try { const s = localStorage.getItem('sniptrip_filters'); return s ? JSON.parse(s) : []; } catch { return []; }
  }); // global active filter labels, persisted to localStorage
  const [showAddMorePanel, setShowAddMorePanel] = useState(false);
  const [addMoreCityIdx, setAddMoreCityIdx] = useState(null); // selected city in the Add More panel
  const [expandingRegion, setExpandingRegion] = useState(null); // region name being AI-expanded to cities
  const photoTouchStartRef = useRef(null);
  const heroTouchXRef = useRef(null); // tracks carousel swipe start; 'swipe' after gesture ends
  const chatAPIHistoryRef = useRef([]); // raw Anthropic-format messages for chat API calls
  const chatScrollRef = useRef(null);

  // ── Empty-day discovery state ────────────────────────────────────────────
  // Per-empty-day discovery: { [dayId]: { phase, questions, suggestions, lastQuestion, selectedDest, freeformVisible, freeformText } }
  // phase: 'idle' | 'city-loading' | 'city-q' | 'city-fl' | 'city-s' | 'city-err' | 'attr-loading' | 'attr-q' | 'attr-fl' | 'attr-s' | 'attr-err'
  const [emptyDayDisc, setEmptyDayDisc] = useState({});
  const [addingEmptyDayAttr, setAddingEmptyDayAttr] = useState(null); // { dayId, name }
  const [shareLoading, setShareLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [legalPage, setLegalPage] = useState(null); // 'terms' | 'privacy' | null
  const [legalHtml, setLegalHtml] = useState('');
  const [termsAgreed, setTermsAgreed] = useState(true);
  const [lang, setLang] = useState(() => {
    try {
      const cookie = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith('sniptrip_lang='));
      if (cookie) return cookie.split('=')[1];
    } catch { /* ignore */ }
    const nav = navigator.language || '';
    if (/^zh-(TW|HK|Hant)/i.test(nav)) return 'zh-TW';
    if (/^zh/i.test(nav)) return 'zh-CN';
    const base = nav.split('-')[0].toLowerCase();
    if (['de','fr','es','ja','ko'].includes(base)) return base;
    return 'en';
  });
  const locale = getLocale(lang);
  const t = makeT(locale);

  // ── Discovery panel state ─────────────────────────────────────────────────
  // Conversational discovery state
  // cityDiscovery: { phase: 'idle'|'loading'|'questions'|'followup-loading'|'suggestions', questions, suggestions, freeformVisible, freeformText }
  const [cityDiscovery, setCityDiscovery] = useState(IDLE_CITY);
  // attrDiscovery: { [cityIdx]: same shape as cityDiscovery, plus mode: 'filtered'|'unfiltered' }
  const [attrDiscovery, setAttrDiscovery] = useState({});
  const [addingDiscoveredAttr, setAddingDiscoveredAttr] = useState(null); // { cityIdx, name }
  const [generatingItinerary, setGeneratingItinerary] = useState(false);
  const [generatingMsgIdx, setGeneratingMsgIdx] = useState(0);

  // ── Autocomplete state ───────────────────────────────────────────────────
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [attractionSuggestions, setAttractionSuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [showAttractionSuggestions, setShowAttractionSuggestions] = useState(false);
  const cityInputRef = useRef(null);
  const attractionInputRefs = useRef({});

  // ── Inline constants ──────────────────────────────────────────────────────
  // Fallback data used when Google Places API is unavailable.
  const MOCK_CITIES_DB = [
    { name: "San Francisco, CA, USA", placeId: "sf1", location: { latitude: 37.7749, longitude: -122.4194 } },
    { name: "Los Angeles, CA, USA", placeId: "la1", location: { latitude: 34.0522, longitude: -118.2437 } },
    { name: "New York, NY, USA", placeId: "ny1", location: { latitude: 40.7128, longitude: -74.0060 } },
    { name: "Chicago, IL, USA", placeId: "chi1", location: { latitude: 41.8781, longitude: -87.6298 } },
    { name: "Seattle, WA, USA", placeId: "sea1", location: { latitude: 47.6062, longitude: -122.3321 } },
    { name: "Boston, MA, USA", placeId: "bos1", location: { latitude: 42.3601, longitude: -71.0589 } },
    { name: "Miami, FL, USA", placeId: "mia1", location: { latitude: 25.7617, longitude: -80.1918 } },
    { name: "Austin, TX, USA", placeId: "aus1", location: { latitude: 30.2672, longitude: -97.7431 } },
    { name: "Portland, OR, USA", placeId: "por1", location: { latitude: 45.5152, longitude: -122.6784 } },
    { name: "Denver, CO, USA", placeId: "den1", location: { latitude: 39.7392, longitude: -104.9903 } },
    { name: "Paris, France", placeId: "par1", location: { latitude: 48.8566, longitude: 2.3522 } },
    { name: "London, UK", placeId: "lon1", location: { latitude: 51.5074, longitude: -0.1278 } },
    { name: "Tokyo, Japan", placeId: "tok1", location: { latitude: 35.6762, longitude: 139.6503 } },
    { name: "Rome, Italy", placeId: "rom1", location: { latitude: 41.9028, longitude: 12.4964 } },
    { name: "Barcelona, Spain", placeId: "bar1", location: { latitude: 41.3851, longitude: 2.1734 } },
    { name: "Amsterdam, Netherlands", placeId: "ams1", location: { latitude: 52.3676, longitude: 4.9041 } },
    { name: "Berlin, Germany", placeId: "ber1", location: { latitude: 52.5200, longitude: 13.4050 } },
    { name: "Sydney, Australia", placeId: "syd1", location: { latitude: -33.8688, longitude: 151.2093 } },
    { name: "Toronto, Canada", placeId: "tor1", location: { latitude: 43.6532, longitude: -79.3832 } },
    { name: "Dubai, UAE", placeId: "dub1", location: { latitude: 25.2048, longitude: 55.2708 } }
  ];

  // Mock database of attractions by city
  const MOCK_ATTRACTIONS_DB = {
    "San Francisco, CA, USA": [
      { name: "Golden Gate Bridge", fullName: "Golden Gate Bridge, San Francisco, CA", placeId: "ggb1", types: ["tourist_attraction"], duration: "1-2 hours" },
      { name: "Alcatraz Island", fullName: "Alcatraz Island, San Francisco, CA", placeId: "alc1", types: ["museum", "tourist_attraction"], duration: "2-3 hours" },
      { name: "Fisherman's Wharf", fullName: "Fisherman's Wharf, San Francisco, CA", placeId: "fw1", types: ["tourist_attraction"], duration: "2-3 hours" },
      { name: "Chinatown", fullName: "Chinatown, San Francisco, CA", placeId: "ct1", types: ["neighborhood"], duration: "1-2 hours" },
      { name: "Cable Car Museum", fullName: "Cable Car Museum, San Francisco, CA", placeId: "ccm1", types: ["museum"], duration: "1-2 hours" },
      { name: "Pier 39", fullName: "Pier 39, San Francisco, CA", placeId: "p39", types: ["tourist_attraction"], duration: "1-2 hours" },
      { name: "Palace of Fine Arts", fullName: "Palace of Fine Arts, San Francisco, CA", placeId: "pofa", types: ["tourist_attraction"], duration: "1 hour" },
      { name: "Ferry Building", fullName: "Ferry Building Marketplace, San Francisco, CA", placeId: "fb1", types: ["shopping_mall", "restaurant"], duration: "1-2 hours" },
      { name: "Coit Tower", fullName: "Coit Tower, San Francisco, CA", placeId: "coit1", types: ["tourist_attraction"], duration: "1 hour" },
      { name: "Lombard Street", fullName: "Lombard Street, San Francisco, CA", placeId: "lomb1", types: ["tourist_attraction"], duration: "30 minutes" },
      { name: "Union Square", fullName: "Union Square, San Francisco, CA", placeId: "us1", types: ["shopping_mall"], duration: "1-2 hours" },
      { name: "Golden Gate Park", fullName: "Golden Gate Park, San Francisco, CA", placeId: "ggp1", types: ["park"], duration: "2-4 hours" },
      { name: "de Young Museum", fullName: "de Young Museum, San Francisco, CA", placeId: "dey1", types: ["museum", "art_gallery"], duration: "2-3 hours" },
      { name: "California Academy of Sciences", fullName: "California Academy of Sciences, San Francisco, CA", placeId: "cas1", types: ["museum"], duration: "2-3 hours" },
      { name: "Exploratorium", fullName: "Exploratorium, San Francisco, CA", placeId: "exp1", types: ["museum"], duration: "2-3 hours" },
      { name: "Twin Peaks", fullName: "Twin Peaks, San Francisco, CA", placeId: "tp1", types: ["tourist_attraction"], duration: "1 hour" },
      { name: "Painted Ladies", fullName: "Painted Ladies, San Francisco, CA", placeId: "pl1", types: ["tourist_attraction"], duration: "30 minutes" },
      { name: "Haight-Ashbury", fullName: "Haight-Ashbury, San Francisco, CA", placeId: "ha1", types: ["neighborhood"], duration: "1-2 hours" },
      { name: "Mission District", fullName: "Mission District, San Francisco, CA", placeId: "md1", types: ["neighborhood"], duration: "2-3 hours" },
      { name: "Japanese Tea Garden", fullName: "Japanese Tea Garden, San Francisco, CA", placeId: "jtg1", types: ["park"], duration: "1 hour" },
      { name: "Ghirardelli Square", fullName: "Ghirardelli Square, San Francisco, CA", placeId: "gh1", types: ["shopping_mall"], duration: "1 hour" },
      { name: "Legion of Honor", fullName: "Legion of Honor, San Francisco, CA", placeId: "loh1", types: ["museum", "art_gallery"], duration: "2 hours" },
      { name: "San Francisco Museum of Modern Art", fullName: "SFMOMA, San Francisco, CA", placeId: "sfm1", types: ["museum", "art_gallery"], duration: "2-3 hours" },
      { name: "Muir Woods", fullName: "Muir Woods National Monument, Mill Valley, CA", placeId: "mw1", types: ["park", "natural_feature"], duration: "2-3 hours" },
      { name: "Sausalito", fullName: "Sausalito, CA", placeId: "sau1", types: ["locality"], duration: "2-3 hours" }
    ],
    "Los Angeles, CA, USA": [
      { name: "Griffith Observatory", fullName: "Griffith Observatory, Los Angeles, CA", placeId: "go1", types: ["tourist_attraction"], duration: "2-3 hours" },
      { name: "Santa Monica Pier", fullName: "Santa Monica Pier, Santa Monica, CA", placeId: "smp1", types: ["amusement_park"], duration: "2-3 hours" },
      { name: "Hollywood Walk of Fame", fullName: "Hollywood Walk of Fame, Los Angeles, CA", placeId: "hwf1", types: ["tourist_attraction"], duration: "1-2 hours" },
      { name: "Getty Center", fullName: "Getty Center, Los Angeles, CA", placeId: "gc1", types: ["art_gallery", "museum"], duration: "2-3 hours" },
      { name: "Universal Studios", fullName: "Universal Studios Hollywood, Universal City, CA", placeId: "us1", types: ["amusement_park"], duration: "6-8 hours" },
      { name: "Venice Beach", fullName: "Venice Beach, Los Angeles, CA", placeId: "vb1", types: ["beach"], duration: "2-3 hours" },
      { name: "The Broad", fullName: "The Broad, Los Angeles, CA", placeId: "broad1", types: ["museum", "art_gallery"], duration: "2 hours" },
      { name: "Rodeo Drive", fullName: "Rodeo Drive, Beverly Hills, CA", placeId: "rd1", types: ["shopping_mall"], duration: "1-2 hours" },
      { name: "Disneyland", fullName: "Disneyland Park, Anaheim, CA", placeId: "dl1", types: ["amusement_park"], duration: "8+ hours" },
      { name: "Hollywood Sign", fullName: "Hollywood Sign, Los Angeles, CA", placeId: "hs1", types: ["tourist_attraction"], duration: "1-2 hours" },
      { name: "Sunset Boulevard", fullName: "Sunset Boulevard, Los Angeles, CA", placeId: "sb1", types: ["route"], duration: "1-2 hours" },
      { name: "La Brea Tar Pits", fullName: "La Brea Tar Pits, Los Angeles, CA", placeId: "lbt1", types: ["museum"], duration: "1-2 hours" },
      { name: "Getty Villa", fullName: "Getty Villa, Pacific Palisades, CA", placeId: "gv1", types: ["museum"], duration: "2-3 hours" },
      { name: "Runyon Canyon", fullName: "Runyon Canyon Park, Los Angeles, CA", placeId: "rc1", types: ["park"], duration: "1-2 hours" }
    ],
    "New York, NY, USA": [
      { name: "Statue of Liberty", fullName: "Statue of Liberty, New York, NY", placeId: "sol1", types: ["tourist_attraction"], duration: "2-3 hours" },
      { name: "Central Park", fullName: "Central Park, New York, NY", placeId: "cp1", types: ["park"], duration: "2-3 hours" },
      { name: "Empire State Building", fullName: "Empire State Building, New York, NY", placeId: "esb1", types: ["tourist_attraction"], duration: "1-2 hours" },
      { name: "Metropolitan Museum of Art", fullName: "The Met, New York, NY", placeId: "met1", types: ["museum", "art_gallery"], duration: "3-4 hours" },
      { name: "Times Square", fullName: "Times Square, New York, NY", placeId: "ts1", types: ["tourist_attraction"], duration: "1-2 hours" },
      { name: "Brooklyn Bridge", fullName: "Brooklyn Bridge, New York, NY", placeId: "bb1", types: ["tourist_attraction"], duration: "1 hour" },
      { name: "9/11 Memorial & Museum", fullName: "9/11 Memorial & Museum, New York, NY", placeId: "mem1", types: ["museum"], duration: "2-3 hours" },
      { name: "MoMA", fullName: "Museum of Modern Art, New York, NY", placeId: "moma1", types: ["museum", "art_gallery"], duration: "2-3 hours" },
      { name: "Rockefeller Center", fullName: "Rockefeller Center, New York, NY", placeId: "rock1", types: ["tourist_attraction"], duration: "1-2 hours" },
      { name: "High Line", fullName: "High Line, New York, NY", placeId: "hl1", types: ["park"], duration: "1-2 hours" },
      { name: "One World Observatory", fullName: "One World Observatory, New York, NY", placeId: "owo1", types: ["tourist_attraction"], duration: "1-2 hours" },
      { name: "Grand Central Terminal", fullName: "Grand Central Terminal, New York, NY", placeId: "gct1", types: ["transit_station"], duration: "30-60 minutes" },
      { name: "Fifth Avenue", fullName: "Fifth Avenue, New York, NY", placeId: "5av1", types: ["route", "shopping_mall"], duration: "2-3 hours" },
      { name: "Chelsea Market", fullName: "Chelsea Market, New York, NY", placeId: "cm1", types: ["shopping_mall"], duration: "1-2 hours" },
      { name: "American Museum of Natural History", fullName: "American Museum of Natural History, New York, NY", placeId: "amnh1", types: ["museum"], duration: "3-4 hours" },
      { name: "The Vessel", fullName: "The Vessel, New York, NY", placeId: "ves1", types: ["tourist_attraction"], duration: "30-60 minutes" },
      { name: "Guggenheim Museum", fullName: "Solomon R. Guggenheim Museum, New York, NY", placeId: "gug1", types: ["museum", "art_gallery"], duration: "2 hours" },
      { name: "Broadway Show", fullName: "Broadway Theater District, New York, NY", placeId: "bway1", types: ["tourist_attraction"], duration: "2-3 hours" }
    ],
    "Paris, France": [
      { name: "Eiffel Tower", fullName: "Eiffel Tower, Paris, France", placeId: "et1", types: ["tourist_attraction"], duration: "2-3 hours" },
      { name: "Louvre Museum", fullName: "Louvre Museum, Paris, France", placeId: "lm1", types: ["museum", "art_gallery"], duration: "3-4 hours" },
      { name: "Arc de Triomphe", fullName: "Arc de Triomphe, Paris, France", placeId: "adt1", types: ["tourist_attraction"], duration: "1 hour" },
      { name: "Notre-Dame Cathedral", fullName: "Notre-Dame Cathedral, Paris, France", placeId: "ndc1", types: ["church"], duration: "1 hour" },
      { name: "Sacré-Cœur", fullName: "Sacré-Cœur Basilica, Paris, France", placeId: "sc1", types: ["church"], duration: "1 hour" },
      { name: "Champs-Élysées", fullName: "Champs-Élysées, Paris, France", placeId: "ce1", types: ["route"], duration: "1-2 hours" },
      { name: "Musée d'Orsay", fullName: "Musée d'Orsay, Paris, France", placeId: "mo1", types: ["museum", "art_gallery"], duration: "2-3 hours" },
      { name: "Versailles", fullName: "Palace of Versailles, Versailles, France", placeId: "ver1", types: ["museum", "tourist_attraction"], duration: "4-5 hours" },
      { name: "Sainte-Chapelle", fullName: "Sainte-Chapelle, Paris, France", placeId: "stc1", types: ["church"], duration: "45 minutes" },
      { name: "Montmartre", fullName: "Montmartre, Paris, France", placeId: "mont1", types: ["neighborhood"], duration: "2-3 hours" },
      { name: "Latin Quarter", fullName: "Latin Quarter, Paris, France", placeId: "lq1", types: ["neighborhood"], duration: "2 hours" },
      { name: "Seine River Cruise", fullName: "Seine River, Paris, France", placeId: "sein1", types: ["tourist_attraction"], duration: "1-2 hours" }
    ],
    "London, UK": [
      { name: "Tower of London", fullName: "Tower of London, London, UK", placeId: "tol1", types: ["museum", "tourist_attraction"], duration: "2-3 hours" },
      { name: "British Museum", fullName: "British Museum, London, UK", placeId: "bm1", types: ["museum"], duration: "3-4 hours" },
      { name: "Big Ben", fullName: "Big Ben, London, UK", placeId: "bb1", types: ["tourist_attraction"], duration: "30 minutes" },
      { name: "London Eye", fullName: "London Eye, London, UK", placeId: "le1", types: ["tourist_attraction"], duration: "1 hour" },
      { name: "Buckingham Palace", fullName: "Buckingham Palace, London, UK", placeId: "bp1", types: ["tourist_attraction"], duration: "1-2 hours" },
      { name: "Westminster Abbey", fullName: "Westminster Abbey, London, UK", placeId: "wa1", types: ["church"], duration: "1-2 hours" },
      { name: "Tower Bridge", fullName: "Tower Bridge, London, UK", placeId: "tb1", types: ["tourist_attraction"], duration: "1 hour" },
      { name: "Tate Modern", fullName: "Tate Modern, London, UK", placeId: "tm1", types: ["museum", "art_gallery"], duration: "2-3 hours" },
      { name: "Natural History Museum", fullName: "Natural History Museum, London, UK", placeId: "nhm1", types: ["museum"], duration: "2-3 hours" },
      { name: "Camden Market", fullName: "Camden Market, London, UK", placeId: "cam1", types: ["market"], duration: "2-3 hours" },
      { name: "Covent Garden", fullName: "Covent Garden, London, UK", placeId: "cg1", types: ["shopping_mall"], duration: "1-2 hours" },
      { name: "Hyde Park", fullName: "Hyde Park, London, UK", placeId: "hp1", types: ["park"], duration: "1-2 hours" },
      { name: "St. Paul's Cathedral", fullName: "St. Paul's Cathedral, London, UK", placeId: "spc1", types: ["church"], duration: "1-2 hours" },
      { name: "Shakespeare's Globe", fullName: "Shakespeare's Globe Theatre, London, UK", placeId: "sg1", types: ["tourist_attraction"], duration: "2-3 hours" }
    ],
    "Tokyo, Japan": [
      { name: "Senso-ji Temple", fullName: "Senso-ji Temple, Tokyo, Japan", placeId: "sj1", types: ["temple", "tourist_attraction"], duration: "1-2 hours" },
      { name: "Tokyo Tower", fullName: "Tokyo Tower, Tokyo, Japan", placeId: "tt1", types: ["tourist_attraction"], duration: "1-2 hours" },
      { name: "Meiji Shrine", fullName: "Meiji Shrine, Tokyo, Japan", placeId: "ms1", types: ["shrine", "place_of_worship"], duration: "1 hour" },
      { name: "Tsukiji Outer Market", fullName: "Tsukiji Outer Market, Tokyo, Japan", placeId: "tom1", types: ["market"], duration: "2 hours" },
      { name: "Tokyo Skytree", fullName: "Tokyo Skytree, Tokyo, Japan", placeId: "ts2", types: ["tourist_attraction"], duration: "1-2 hours" },
      { name: "Shibuya Crossing", fullName: "Shibuya Crossing, Tokyo, Japan", placeId: "shib1", types: ["tourist_attraction"], duration: "30 minutes" },
      { name: "Shinjuku Gyoen", fullName: "Shinjuku Gyoen National Garden, Tokyo, Japan", placeId: "shgy1", types: ["park"], duration: "1-2 hours" },
      { name: "Imperial Palace", fullName: "Imperial Palace, Tokyo, Japan", placeId: "imp1", types: ["tourist_attraction"], duration: "1-2 hours" },
      { name: "Akihabara", fullName: "Akihabara, Tokyo, Japan", placeId: "aki1", types: ["neighborhood"], duration: "2-3 hours" },
      { name: "Harajuku", fullName: "Harajuku, Tokyo, Japan", placeId: "har1", types: ["neighborhood"], duration: "1-2 hours" },
      { name: "TeamLab Borderless", fullName: "teamLab Borderless, Tokyo, Japan", placeId: "tlb1", types: ["museum"], duration: "2-3 hours" },
      { name: "Ueno Park", fullName: "Ueno Park, Tokyo, Japan", placeId: "uen1", types: ["park"], duration: "2-3 hours" }
    ]
  };

  // ── Search functions ──────────────────────────────────────────────────────
  const getPopularAttractions = (cityName) => {
    return MOCK_ATTRACTIONS_DB[cityName] || [];
  };

  // Search cities with mock data
  const searchCities = async (input) => {
    if (!input || input.length < 2) {
      setCitySuggestions([]);
      return;
    }

    console.log('Searching cities for:', input);

    try {
      let locationParam = '';
      if (cities.length > 0) {
        const stateMatch = cities[0].name.match(/,\s+([A-Z]{2})(?:\s|,|$)/);
        if (stateMatch) {
          const centroid = US_STATE_CENTROIDS[stateMatch[1]];
          if (centroid) locationParam = `&location=${centroid[0]},${centroid[1]}&radius=300000`;
        }
      }
      const response = await fetch(`/api/search-cities?input=${encodeURIComponent(input)}&language=${encodeURIComponent(lang)}${locationParam}`);
      const data = await response.json();
      
      if (data.predictions) {
        setCitySuggestions(data.predictions.slice(0, 10).map(p => ({
          name: p.description,
          placeId: p.place_id,
          location: null,
          types: p.types || [],
        })));
      } else {
        setCitySuggestions([{ name: input, placeId: null, location: null }]);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      // Fallback to mock data on error
      const results = MOCK_CITIES_DB.filter(city => 
        city.name.toLowerCase().includes(input.toLowerCase())
      ).slice(0, 10);
      setCitySuggestions(results.length > 0 ? results : [{ name: input, placeId: null, location: null }]);
    }
  };

  const searchDayInlineCity = async (input) => {
    if (!input || input.length < 2) { setDayInlineCitySuggestions([]); return; }
    try {
      const res = await fetch(`/api/search-cities?input=${encodeURIComponent(input)}&language=${encodeURIComponent(lang)}`);
      const data = await res.json();
      if (data.predictions) {
        setDayInlineCitySuggestions(data.predictions.slice(0, 8).map(p => ({
          name: p.description, placeId: p.place_id, location: null, types: p.types || [],
        })));
      }
    } catch { setDayInlineCitySuggestions([]); }
  };

  // Search attractions with Google Places API via Vercel serverless function
  const searchAttractions = async (input, cityIndex, filterArg) => {
    const city = cities[cityIndex];
    if (!city) return;

    // Normalize filterArg to a string[] — empty means 'All'
    let activeLabels = filterArg ?? attractionFilters;
    if (!Array.isArray(activeLabels)) activeLabels = activeLabels === 'All' ? [] : [activeLabels];
    activeLabels = activeLabels.filter(l => l !== 'All');

    const activeFilters = activeLabels.length > 0
      ? activeLabels.map(l => ATTRACTION_FILTERS.find(f => f.label === l)).filter(Boolean)
      : [ATTRACTION_FILTERS[0]]; // 'All'

    const combinedTypes = activeFilters[0].types
      ? [...new Set(activeFilters.flatMap(f => f.types || []))]
      : null;

    // If no input, auto-populate using the active filter's query
    if (!input || input.length === 0) {
      try {
        const searchQuery = activeLabels.length > 0
          ? `${activeLabels.join(' ')} attractions ${city.name}`
          : activeFilters[0].query(city.name);
        const response = await fetch(`/api/search-attractions?query=${encodeURIComponent(searchQuery)}&language=${encodeURIComponent(lang)}`);
        const data = await response.json();

        if (data.results && data.results.length > 0) {
          let results = data.results;

          if (combinedTypes) {
            // Filter to the union of all selected categories' types; fall back to unfiltered if nothing matches
            const filtered = results.filter(place =>
              (place.types || []).some(t => combinedTypes.includes(t))
            );
            results = filtered.length > 0 ? filtered : results;
          } else {
            // 'All': keep tourist-relevant types
            const filtered = results.filter(place => {
              const types = place.types || [];
              return types.includes('tourist_attraction') ||
                     types.includes('museum') ||
                     types.includes('point_of_interest') ||
                     types.includes('park') ||
                     types.includes('natural_feature') ||
                     types.includes('church') ||
                     types.includes('art_gallery') ||
                     types.includes('amusement_park');
            });
            results = filtered.length > 0 ? filtered : data.results;
          }

          const existingNames = new Set(city.attractions.map(a => a.name.toLowerCase()));
          results = results.filter(place => !existingNames.has(place.name.toLowerCase()));

          const suggestions = results.slice(0, 10).map(place => ({
            name: place.name,
            fullName: place.formatted_address,
            placeId: place.place_id,
            types: place.types || [],
            duration: estimateDuration(place.types || []),
            photoRefs: (place.photos || []).slice(0, 4).map(p => p.photo_reference).filter(Boolean),
            rating: place.rating ?? null,
            ratingCount: place.user_ratings_total ?? null,
          }));
          setAttractionSuggestions(suggestions);
        } else {
          setAttractionSuggestions(getPopularAttractions(city.name));
        }
      } catch (error) {
        console.error('Error fetching popular attractions:', error);
        setAttractionSuggestions(getPopularAttractions(city.name));
      }
      return;
    }

    if (input.length < 2) {
      setAttractionSuggestions([]);
      return;
    }

    try {
      const searchQuery = `${input} in ${city.name}`;
      const response = await fetch(`/api/search-attractions?query=${encodeURIComponent(searchQuery)}&language=${encodeURIComponent(lang)}`);
      const data = await response.json();

      if (data.results && data.results.length > 0) {
        const suggestions = data.results.slice(0, 10).map(place => ({
          name: place.name,
          fullName: place.formatted_address,
          placeId: place.place_id,
          types: place.types || [],
          duration: estimateDuration(place.types || []),
          photoRefs: (place.photos || []).slice(0, 4).map(p => p.photo_reference).filter(Boolean),
          rating: place.rating ?? null,
          ratingCount: place.user_ratings_total ?? null,
        }));
        setAttractionSuggestions(suggestions);
      } else {
        setAttractionSuggestions([{ name: input, placeId: null, duration: '2-3 hours' }]);
      }
    } catch (error) {
      console.error('Error fetching attractions:', error);
      // Fallback to mock data on error
      const cityAttractions = MOCK_ATTRACTIONS_DB[city.name] || [];
      const results = cityAttractions.filter(attr => 
        attr.name.toLowerCase().includes(input.toLowerCase()) ||
        attr.fullName.toLowerCase().includes(input.toLowerCase())
      ).slice(0, 10);
      setAttractionSuggestions(results.length > 0 ? results : [{ name: input, placeId: null, duration: '2-3 hours' }]);
    }
  };

  // Determine if city is a major tourist destination and how many attractions to auto-add

  // Auto-populate attractions with "I'm Feeling Lucky"
  const autoPopulateAttractions = async (cityIndex, cityObject = null, clearFirst = false) => {
    const city = cityObject || cities[cityIndex];
    if (!city || autoAddLoading[cityIndex]) return;

    const targetDays = isMajorTouristCity(city.name) ? 2 : 1;
    if (clearFirst) {
      setCities(prev => prev.map((c, i) => i === cityIndex ? { ...c, attractions: [] } : c));
    }
    const existingNames = clearFirst ? new Set() : new Set([
      ...(city.attractions || []).map(a => a.name.toLowerCase()),
      ...(itinerary?.days || []).flatMap(d => d.activities.map(a => a.name.toLowerCase())),
    ]);

    setAutoAddLoading(prev => ({ ...prev, [cityIndex]: true }));
    try {
      // Fetch Google Places top attractions to give Claude a popularity signal.
      // Claude is not limited to this list but uses it to anchor toward well-known picks.
      let topPlacesData = [];
      let googlePlaces = [];
      try {
        topPlacesData = await fetchTopAttractions(city.name, lang);
        googlePlaces = topPlacesData.map(r => r.name).filter(Boolean);
      } catch { /* non-fatal — Claude still runs without it */ }

      const claudeDays = await generateWithClaude(city, targetDays, googlePlaces, topPlacesData);
      if (!claudeDays) return;

      // Flatten activities across all days, skip duplicates
      const selectedAttractions = claudeDays
        .flatMap(day => day.activities)
        .filter(act => !existingNames.has(act.name.toLowerCase()))
        .map(act => ({
          name: act.name,
          fullName: act.fullName,
          placeId: act.placeId,
          types: act.types || [],
          duration: act.duration || estimateDuration(act.types || []),
          photoRefs: act.photoRefs || [],
        }));

      if (selectedAttractions.length === 0) return;

      setCities(prev => prev.map((c, idx) =>
        idx === cityIndex ? { ...c, attractions: [...c.attractions, ...selectedAttractions] } : c
      ));

      // Fetch Wikipedia descriptions for each auto-added attraction in parallel
      selectedAttractions.forEach(attr => {
        fetchWikipediaDescription(attr.name, 'direct', lang).then(description => {
          if (!description) return;
          setCities(prev => prev.map((c, idx) => idx !== cityIndex ? c : {
            ...c,
            attractions: c.attractions.map(a =>
              a.name === attr.name && !a.description ? { ...a, description } : a
            ),
          }));
        }).catch(() => {});
      });

      // Slot into matching itinerary day using the same citiesMatch logic as addAttraction
      if (itinerary) {
        selectedAttractions.forEach(attr => {
          insertAttractionIntoItinerary({ ...attr, cityName: city.name }, city.name);
        });
      }
    } catch { /* silent */ }
    finally {
      setAutoAddLoading(prev => ({ ...prev, [cityIndex]: false }));
      // Show first-time filter onboarding if user has never set filters
      if (attractionFilters.length === 0 && !filterOnboardingDoneRef.current) {
        setFilterOnboardingCity(cityIndex);
        setFilterOnboardingPending([]);
      }
    }
  };

  // ── useEffect hooks ───────────────────────────────────────────────────────
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (el && typeof el.scrollTo === 'function') el.scrollTo(0, 0);
    else window.scrollTo(0, 0);
  }, [screen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentCity && showCitySuggestions) {
        searchCities(currentCity);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [currentCity, showCitySuggestions]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentAttraction && selectedCityIndex !== null && showAttractionSuggestions) {
        searchAttractions(currentAttraction, selectedCityIndex);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [currentAttraction, selectedCityIndex, showAttractionSuggestions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityInputRef.current && !cityInputRef.current.contains(event.target)) {
        setShowCitySuggestions(false);
      }
      
      // Check if click is outside any attraction input
      const clickedInsideAttractionInput = Object.values(attractionInputRefs.current).some(
        ref => ref && ref.contains(event.target)
      );
      
      if (!clickedInsideAttractionInput) {
        setShowAttractionSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Persist itinerary and cities to localStorage
  useEffect(() => {
    if (itinerary) localStorage.setItem('sniptrip_itinerary', JSON.stringify(itinerary));
    else localStorage.removeItem('sniptrip_itinerary');
  }, [itinerary]);

  useEffect(() => {
    if (cities.length > 0) localStorage.setItem('sniptrip_cities', JSON.stringify(cities));
    else localStorage.removeItem('sniptrip_cities');
  }, [cities]);

  // Restore from localStorage on first load
  useEffect(() => {
    try {
      const savedItinerary = localStorage.getItem('sniptrip_itinerary');
      const savedCities = localStorage.getItem('sniptrip_cities');
      if (savedItinerary) setItinerary(JSON.parse(savedItinerary));
      if (savedCities) setCities(JSON.parse(savedCities));
    } catch { /* ignore parse errors */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load shared itinerary or legal page from URL on first load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('share');
    const legal = params.get('legal');
    if (token) {
      window.history.replaceState({}, '', window.location.pathname);
      fetch(`/api/share?token=${encodeURIComponent(token)}`)
        .then(r => r.json())
        .then(data => {
          if (data.itinerary) {
            setItinerary(data.itinerary);
            setCurrentTripId(null);
            setHasUnsavedChanges(false);
            setScreen('itinerary');
            track('shared_itinerary_opened', { itinerary_name: data.itinerary.name });
          }
        })
        .catch(() => {});
    } else if (legal === 'terms' || legal === 'privacy') {
      window.history.replaceState({}, '', window.location.pathname);
      openLegal(legal);
    } else {
      const sample = params.get('sample');
      if (sample) {
        window.history.replaceState({}, '', window.location.pathname);
        fetch(`/api/itineraries/${encodeURIComponent(sample)}?format=json`)
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data?.itinerary) {
              setItinerary(data.itinerary);
              setCurrentTripId(null);
              setHasUnsavedChanges(false);
              setScreen('itinerary');
              track('sample_itinerary_opened', { city: sample });
            }
          })
          .catch(() => {});
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist active tab and current trip ID so tab eviction (mobile browser unloading a background
  // tab from memory) can be recovered instantly on next mount without waiting for API calls.
  useEffect(() => {
    try { sessionStorage.setItem('sniptrip_session_screen', screen); } catch {}
  }, [screen]);

  useEffect(() => {
    try {
      if (itinerary) sessionStorage.setItem('sniptrip_session_itinerary', JSON.stringify(itinerary));
      else sessionStorage.removeItem('sniptrip_session_itinerary');
    } catch {}
  }, [itinerary]);

  useEffect(() => {
    try {
      if (currentTripId) sessionStorage.setItem('sniptrip_session_trip', currentTripId);
      else sessionStorage.removeItem('sniptrip_session_trip');
    } catch {}
  }, [currentTripId]);

  // After savedTrips loads, wire up currentTripId if itinerary was already restored from
  // sessionStorage (instant path), or fall back to a full handleLoadTrip if it wasn't.
  useEffect(() => {
    if (sessionTripRestored.current) return;
    if (!user || savedTrips.length === 0) return;
    const storedId = sessionStorage.getItem('sniptrip_session_trip');
    if (!storedId) return;
    sessionTripRestored.current = true;
    if (itinerary) {
      // Already restored from sessionStorage — just reconnect the trip ID so saves/updates work
      setCurrentTripId(storedId);
      return;
    }
    const trip = savedTrips.find(t => t._id === storedId);
    if (trip) handleLoadTrip(trip);
    else sessionStorage.removeItem('sniptrip_session_trip');
  }, [savedTrips]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep hasUnsavedChangesRef mirrored for use in stable callbacks/intervals
  useEffect(() => { hasUnsavedChangesRef.current = hasUnsavedChanges; }, [hasUnsavedChanges]);

  // Mark unsaved changes when itinerary changes (but not on initial load from a saved trip)
  useEffect(() => {
    if (justRefreshedRef.current) {
      // Itinerary was set by a background refresh — not a user edit, don't mark dirty
      setShareUrl(null);
      return;
    }
    if (itinerary && currentTripId) {
      setHasUnsavedChanges(true);
    }
    setShareUrl(null);
  }, [itinerary]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save debounce when editing a saved trip
  const doAutoSave = useCallback(async (itineraryToSave, tripId, token) => {
    try {
      const res = await fetch(`/api/trips/update?id=${tripId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ itinerary: itineraryToSave })
      });
      if (!res.ok) return; // leave hasUnsavedChanges true so next edit retries
      setHasUnsavedChanges(false);
      // Keep in-memory savedTrips in sync so opening the trip card again
      // doesn't reload a stale (pre-edit) version and overwrite the saved data.
      setSavedTrips(prev => prev.map(t =>
        t._id === tripId ? { ...itineraryToSave, _id: tripId, savedAt: t.savedAt } : t
      ));
    } catch { /* silent fail, will retry on next change */ }
  }, []);

  useEffect(() => {
    if (!itinerary || !currentTripId || !user) return;
    // Consume the justRefreshed flag here (auto-save effect runs after the hasUnsavedChanges
    // effect above, so the flag is still set when we get here)
    if (justRefreshedRef.current) {
      justRefreshedRef.current = false;
      clearTimeout(autoSaveTimerRef.current);
      return;
    }
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      doAutoSave(itinerary, currentTripId, user.token);
    }, 2000);
    return () => clearTimeout(autoSaveTimerRef.current);
  }, [itinerary, currentTripId, user, doAutoSave]);

  // Background refresh: fetch latest trip from MongoDB without triggering auto-save.
  // Called on tab visibility change and on a 30s poll so edits on another device appear.
  const refreshCurrentTrip = useCallback(async () => {
    if (!user || !currentTripId || hasUnsavedChangesRef.current) return;
    try {
      const res = await fetch(`/api/trips/get?id=${currentTripId}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (!res.ok) return;
      const freshTrip = await res.json();
      justRefreshedRef.current = true;
      setItinerary(freshTrip);
      setSavedTrips(prev => prev.map(t => t._id === currentTripId ? freshTrip : t));
    } catch {}
  }, [user, currentTripId]);

  useEffect(() => { refreshCurrentTripRef.current = refreshCurrentTrip; }, [refreshCurrentTrip]);

  // Re-fetch on tab focus (phone/desktop switching between devices)
  useEffect(() => {
    if (!currentTripId || !user) return;
    const handler = () => {
      if (document.visibilityState === 'visible') refreshCurrentTripRef.current?.();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [currentTripId, user]);

  // Poll every 30 s while a trip is open (handles both devices active simultaneously)
  useEffect(() => {
    if (!currentTripId || !user) return;
    const id = setInterval(() => refreshCurrentTripRef.current?.(), 30_000);
    return () => clearInterval(id);
  }, [currentTripId, user]);

  // Cycle through loading messages while generating
  const AUTO_ADD_MESSAGES = ['finding_attractions_1', 'finding_attractions_2', 'finding_attractions_3'];
  const anyAutoAddLoading = Object.values(autoAddLoading).some(Boolean);
  useEffect(() => {
    if (!anyAutoAddLoading) { setAutoAddMsgIdx(0); return; }
    const id = setInterval(() => setAutoAddMsgIdx(i => (i + 1) % AUTO_ADD_MESSAGES.length), 2500);
    return () => clearInterval(id);
  }, [anyAutoAddLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const GENERATING_MESSAGES = ['generating_msg_1', 'generating_msg_2', 'generating_msg_3'];
  useEffect(() => {
    if (!generatingItinerary) { setGeneratingMsgIdx(0); return; }
    const id = setInterval(() => setGeneratingMsgIdx(i => (i + 1) % GENERATING_MESSAGES.length), 2500);
    return () => clearInterval(id);
  }, [generatingItinerary]); // eslint-disable-line react-hooks/exhaustive-deps

  const PHOTO_LOADING_MESSAGES = ['photo_loading_msg_1', 'photo_loading_msg_2', 'photo_loading_msg_3'];
  useEffect(() => {
    if (!photoFeedLoading) { setPhotoFeedLoadingMsgIdx(0); return; }
    const id = setInterval(() => setPhotoFeedLoadingMsgIdx(i => (i + 1) % PHOTO_LOADING_MESSAGES.length), 2500);
    return () => clearInterval(id);
  }, [photoFeedLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Returns "Mon, Jun 15" style label for dayIdx when trip has a start date set
  const formatDayDate = (dayIdx) => {
    if (!itinerary?.startDate) return null;
    const [y, m, d] = itinerary.startDate.split('-').map(Number);
    const date = new Date(y, m - 1, d + dayIdx);
    return date.toLocaleDateString(lang, { weekday: 'short', month: 'short', day: 'numeric' });
  };

  // Shared helper: fetch + cache a banner photo ref for a city name.
  // Writes into dayBannerPhotos (cityName → photoRef | null). Used by both
  // the itinerary day-banner effect and the Plan tab city-card effect below.
  const fetchCityBannerPhoto = (cityName) => {
    if (!cityName) return;
    if (dayBannerPhotos[cityName] !== undefined) return;
    if (dayBannerFetchingRef.current.has(cityName)) return;
    dayBannerFetchingRef.current.add(cityName);
    fetchTopAttractions(cityName, lang)
      .then(results => {
        const alts = (results || [])
          .filter(r => r.photos?.[0]?.photo_reference)
          .map(r => ({ ref: r.photos[0].photo_reference, placeId: r.place_id || null }));
        const primaryRef = alts[0]?.ref || null;
        setDayBannerPhotos(prev => ({ ...prev, [cityName]: primaryRef }));
        setDayBannerAlternatives(prev => ({ ...prev, [cityName]: alts }));
      })
      .catch(() => {
        setDayBannerPhotos(prev => ({ ...prev, [cityName]: null }));
        setDayBannerAlternatives(prev => ({ ...prev, [cityName]: [] }));
      })
      .finally(() => dayBannerFetchingRef.current.delete(cityName));
  };

  // Fetch cover photos for all itinerary cities — needed for day banners AND the teaser strip.
  // Runs for every city regardless of whether activities already have photoRefs, so that
  // the teaser strip always has a stable, freshly-fetched ref for each city.
  useEffect(() => {
    if (!itinerary?.days) return;
    for (const day of itinerary.days) {
      const cityName = day.city || day.activities.find(a => a.cityName)?.cityName;
      fetchCityBannerPhoto(cityName);
    }
  }, [itinerary, dayBannerPhotos]); // eslint-disable-line

  // Lazy-fetch cover photos for Plan tab city cards (shares dayBannerPhotos state)
  useEffect(() => {
    cities.forEach(c => fetchCityBannerPhoto(c.name));
  }, [cities, dayBannerPhotos]); // eslint-disable-line


  // Load saved trips and snips when user logs in
  useEffect(() => {
    if (user) { loadTrips(); loadSnips(); }
    else { setSavedTrips([]); setSnips([]); }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch popular cities from backend on mount
  useEffect(() => {
    fetch('/api/trips/popular')
      .then(r => r.ok ? r.json() : { cities: [] })
      .then(data => setPopularCities(data.cities || []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch Wikipedia thumbnails for all carousel destinations
  useEffect(() => {
    const all = [...HARDCODED_DESTINATIONS, ...popularCities];
    all.forEach(dest => {
      setDestImages(prev => {
        if (prev[dest.name] !== undefined) return prev;
        fetchWikipediaThumbnail(dest.name, dest.wikiTitle).then(url => {
          // null = confirmed no photo; string = has photo
          setDestImages(p => ({ ...p, [dest.name]: url || null }));
        });
        return { ...prev, [dest.name]: 'loading' }; // 'loading' while in-flight
      });
    });
  }, [popularCities]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-advance hero carousel every 4 s while on the landing page
  useEffect(() => {
    if (cities.length > 0) return;
    const timer = setInterval(() => setHeroCarouselIdx(prev => prev + 1), 9000);
    return () => clearInterval(timer);
  }, [cities.length]); // eslint-disable-line



  // ── Auth functions ────────────────────────────────────────────────────────
  const AUTH_ERROR_KEYS = {
    'An account with this email already exists': 'err_email_exists',
    'Invalid email or password': 'err_invalid_credentials',
    'Email and password are required': 'err_email_password_required',
    'Password must be at least 6 characters': 'err_password_too_short',
  };
  const mapAuthError = (error) => {
    const key = AUTH_ERROR_KEYS[error];
    return key ? t(key) : error;
  };

  const handleAuth = async (mode) => {
    setAuthError('');
    setAuthLoading(true);
    try {
      const res = await fetch(`/api/auth/${mode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: authEmail, password: authPassword })
      });
      const data = await res.json();
      if (!res.ok) { setAuthError(mapAuthError(data.error)); return; }
      const userData = { email: data.email, token: data.token };
      localStorage.setItem('sniptrip_user', JSON.stringify(userData));
      setUser(userData);
      track(mode === 'register' ? 'sign_up' : 'login');
      setShowAuthModal(false);
      setAuthEmail('');
      setAuthPassword('');
    } catch {
      setAuthError(t('network_error'));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleExpiredToken = () => {
    localStorage.removeItem('sniptrip_user');
    setUser(null);
  };

  const handleLogout = () => {
    track('sign_out');
    localStorage.removeItem('sniptrip_user');
    localStorage.removeItem('sniptrip_itinerary');
    localStorage.removeItem('sniptrip_cities');
    try {
      sessionStorage.removeItem('sniptrip_session_screen');
      sessionStorage.removeItem('sniptrip_session_itinerary');
      sessionStorage.removeItem('sniptrip_session_trip');
    } catch {}
    setUser(null);
    setItinerary(null);
    setCities([]);
    setCurrentTripId(null);
    setHasUnsavedChanges(false);
    setSnips([]);
    setScreen('input');
  };

  // ── Trip/Snip persistence ─────────────────────────────────────────────────
  const loadTrips = async () => {
    if (!user) return;
    setTripsLoading(true);
    try {
      const res = await fetch('/api/trips/load', {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (res.status === 401) { handleExpiredToken(); return; }
      const data = await res.json();
      if (res.ok) setSavedTrips(data.trips || []);
    } catch { /* silent */ }
    finally { setTripsLoading(false); }
  };

  const loadSnips = async () => {
    if (!user) return;
    try {
      const res = await fetch('/api/snips/load', { headers: { Authorization: `Bearer ${user.token}` } });
      if (res.status === 401) { handleExpiredToken(); return; }
      const data = await res.json();
      if (res.ok) setSnips(data.snips || []);
    } catch { /* silent */ }
  };

  const snipItem = async (type, item) => {
    if (!user) { setShowAuthModal(true); setAuthMode('register'); setAuthReason('snip'); return; }
    track(type === 'city' ? 'city_snipped' : 'attraction_snipped', { name: item.name });
    try {
      await fetch('/api/snips/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({ type, ...item }),
      });
      await loadSnips();
    } catch { /* silent */ }
  };

  const deleteSnip = async (id) => {
    if (!user) return;
    try {
      await fetch(`/api/snips/delete?id=${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${user.token}` } });
      setSnips(prev => prev.filter(s => s._id !== id));
    } catch { /* silent */ }
  };

  const toggleSnipExpand = async (snip) => {
    const id = snip._id;
    const nowExpanded = !expandedSnips[id];
    setExpandedSnips(prev => ({ ...prev, [id]: nowExpanded }));
    if (nowExpanded && snip.type === 'attraction' && !snipDetails[id]) {
      setSnipDetails(prev => ({ ...prev, [id]: { loading: true } }));
      let photoRef = null;
      let description = null;
      try {
        const query = snip.name + (snip.cityName ? ' ' + snip.cityName : '');
        const res = await fetch(`/api/search-attractions?query=${encodeURIComponent(query)}&language=${encodeURIComponent(lang)}`);
        const { results } = await res.json();
        photoRef = results?.[0]?.photos?.[0]?.photo_reference || null;
      } catch {}
      try {
        const wRes = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(snip.name)}`);
        if (wRes.ok) { const w = await wRes.json(); description = w.extract || null; }
      } catch {}
      setSnipDetails(prev => ({ ...prev, [id]: { loading: false, photoRef, description } }));
    }
  };

  // Base: insert an attraction into a specific itinerary day by index.
  const insertAttractionIntoDayIdx = (attractionData, targetDayIdx) => {
    if (!itinerary || targetDayIdx < 0) return;
    setItinerary(prev => {
      const day = prev.days[targetDayIdx];
      const lastTimed = [...day.activities].reverse().find(a => a.time && a.type !== 'hotel');
      const activity = {
        id: `act-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name: attractionData.name,
        fullName: attractionData.fullName,
        placeId: attractionData.placeId,
        types: attractionData.types || [],
        duration: attractionData.duration || estimateDuration(attractionData.types || []),
        photoRefs: attractionData.photoRefs || [],
        cityName: attractionData.cityName || day.city,
        time: lastTimed ? calculateNextTime(lastTimed.time, lastTimed.duration) : '9:00 AM',
        ...(attractionData.rating != null && { rating: attractionData.rating }),
        ...(attractionData.ratingCount != null && { ratingCount: attractionData.ratingCount }),
      };
      return { ...prev, days: prev.days.map((d, i) => i !== targetDayIdx ? d : { ...d, activities: [...d.activities, activity] }) };
    });
    setHasUnsavedChanges(true);
    // Show a brief toast so the user knows which day the attraction landed on
    const day = itinerary?.days[targetDayIdx];
    if (day && screen === 'itinerary') {
      if (dayToastTimerRef.current) clearTimeout(dayToastTimerRef.current);
      setDayToast(`Added to Day ${day.day}${day.city ? ` · ${day.city.split(',')[0]}` : ''}`);
      dayToastTimerRef.current = setTimeout(() => setDayToast(null), 3000);
    }
  };

  // Convenience: find the first day matching cityName; if none, create a new day.
  const insertAttractionIntoItinerary = (attractionData, cityName) => {
    if (!itinerary) return;
    const matchDayIdx = itinerary.days.findIndex(d => d.city && citiesMatch(d.city, cityName || ''));
    if (matchDayIdx >= 0) {
      insertAttractionIntoDayIdx({ ...attractionData, cityName }, matchDayIdx);
      return;
    }
    // No matching day — create one at the end
    setItinerary(prev => {
      const lastDay = prev.days[prev.days.length - 1];
      const activity = {
        id: `act-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name: attractionData.name,
        fullName: attractionData.fullName,
        placeId: attractionData.placeId,
        types: attractionData.types || [],
        duration: attractionData.duration || estimateDuration(attractionData.types || []),
        photoRefs: attractionData.photoRefs || [],
        cityName,
        time: '9:00 AM',
        ...(attractionData.rating != null && { rating: attractionData.rating }),
        ...(attractionData.ratingCount != null && { ratingCount: attractionData.ratingCount }),
      };
      const newDay = { id: `day-${Date.now()}`, day: (lastDay?.day || 0) + 1, city: cityName, activities: [activity], expanded: true, note: '' };
      return { ...prev, days: [...prev.days, newDay] };
    });
    setHasUnsavedChanges(true);
  };

  // Shared Places lookup used by addDiscoveredAttraction and addDiscoveredAttractionToDay.
  const fetchAttractionData = async (name, cityName) => {
    try {
      const res = await fetch(`/api/search-attractions?query=${encodeURIComponent(name + ' ' + cityName)}&language=${encodeURIComponent(lang)}`);
      const { results } = await res.json();
      const place = results?.[0];
      if (!place || !isGeographicallyRelevant(place, cityName)) return null;
      return {
        name, // keep the AI-suggested name; place.name may be a wrong result
        fullName: place.formatted_address,
        placeId: place.place_id,
        types: place.types || [],
        duration: estimateDuration(place.types || []),
        photoRefs: place.photos?.[0]?.photo_reference ? [place.photos[0].photo_reference] : [],
        rating: place.rating ?? null,
        ratingCount: place.user_ratings_total ?? null,
      };
    } catch {
      return null;
    }
  };

  const addSnipToItinerary = async (snip) => {
    if (!itinerary) return;
    track('snip_added_to_itinerary', { snip_name: snip.name, snip_type: snip.type });
    if (snip.type === 'city') {
      const lastDay = itinerary.days[itinerary.days.length - 1];
      const newDay = {
        id: `day-${Date.now()}`,
        day: (lastDay?.day || 0) + 1,
        city: snip.name.split(',')[0].trim(),
        activities: [],
        expanded: true,
        note: '',
      };
      setItinerary(prev => ({ ...prev, days: [...prev.days, newDay] }));
      setHasUnsavedChanges(true);
      setScreen('itinerary');
      return;
    }
    let attractionData = { name: snip.name, types: snip.types || [], photoRefs: [], duration: '2-3 hours' };
    try {
      const query = snip.name + (snip.cityName ? ' ' + snip.cityName : '');
      const res = await fetch(`/api/search-attractions?query=${encodeURIComponent(query)}&language=${encodeURIComponent(lang)}`);
      const { results } = await res.json();
      const place = results?.[0];
      if (place && isGeographicallyRelevant(place, snip.cityName)) {
        attractionData = {
          name: snip.name, // keep AI-suggested name; place.name may be a wrong result
          fullName: place.formatted_address,
          placeId: place.place_id,
          types: place.types || snip.types || [],
          duration: estimateDuration(place.types || snip.types || []),
          photoRefs: place.photos?.[0]?.photo_reference ? [place.photos[0].photo_reference] : [],
          rating: place.rating ?? null,
          ratingCount: place.user_ratings_total ?? null,
        };
      }
    } catch {}
    insertAttractionIntoItinerary(attractionData, snip.cityName);
    setScreen('itinerary');
  };

  const addSnipToPlan = async (snip) => {
    track('snip_added_to_plan', { snip_name: snip.name, snip_type: snip.type });
    if (snip.type === 'city') {
      addDiscoveredCity(snip.name);
    } else {
      const matchIdx = cities.findIndex(c => citiesMatch(c.name, snip.cityName || ''));
      if (matchIdx >= 0) {
        await addDiscoveredAttraction(matchIdx, { name: snip.name, city: snip.cityName });
      } else if (snip.cityName) {
        addDiscoveredCity(snip.cityName);
        // city state won't be updated yet; user will see the city in Plan and can add from there
      }
    }
    setScreen('input');
  };

  // ── Photo mode ────────────────────────────────────────────────────────────
  const fetchPhotoAt = async (idx, feedItems) => {
    const item = (feedItems || photoFeed)[idx];
    if (!item || item.photoFetched) return;
    setPhotoFeedFetchingPhoto(true);
    try {
      const res = await fetch(`/api/search-attractions?query=${encodeURIComponent(item.name + ' ' + item.cityName)}&language=${encodeURIComponent(lang)}`);
      if (res.ok) {
        const d = await res.json();
        const r = d.results?.[0];
        // Only use the photo if the result is geographically in the right region; otherwise show
        // no photo rather than a photo of a completely different place (e.g. Horseshoe Bend AZ
        // returned for an Eastern Sierras search).
        const geo = r && isGeographicallyRelevant(r, item.cityName);
        const updatedFeed = photoFeedRef.current.map((it, i) => i === idx ? {
          ...it,
          photoRef: geo ? (r.photos?.[0]?.photo_reference || null) : null,
          placeId: geo ? (r.place_id || null) : null,
          photoFetched: true,
        } : it);
        photoFeedRef.current = updatedFeed;
        setPhotoFeed(updatedFeed);
      } else {
        const updatedFeed = photoFeedRef.current.map((it, i) => i === idx ? { ...it, photoFetched: true } : it);
        photoFeedRef.current = updatedFeed;
        setPhotoFeed(updatedFeed);
      }
    } catch {
      const updatedFeed = photoFeedRef.current.map((it, i) => i === idx ? { ...it, photoFetched: true } : it);
      photoFeedRef.current = updatedFeed;
      setPhotoFeed(updatedFeed);
    } finally {
      setPhotoFeedFetchingPhoto(false);
    }
  };

  const enterPhotoMode = async ({ cityOverride, filtersOverride, sourceOverride } = {}) => {
    const photoSource = screen === 'itinerary' ? 'itinerary' : screen === 'saved' ? 'saved' : 'plan';
    setPhotoMode(true);
    photoFeedRef.current = [];
    setPhotoFeed([]);
    setPhotoFeedIdx(0);
    setPhotoFeedLoading(true);
    photoFeedFetchingMoreRef.current = false;
    setPhotoFeedFetchingMore(false);
    trackPageView('Photo Mode', `/photo/${photoSource}`);
    track('photo_mode_entered', { source: sourceOverride || (cityOverride ? 'destination_card' : photoSource) });
    if (!localStorage.getItem('sniptrip_photo_tutorial_seen')) setShowPhotoTutorial(true);

    const { ctxCities, ctxAttractions, ctxFilters } = buildPhotoContext({
      screen, cityOverride, filtersOverride, cities, itinerary, snips, savedTrips, attractionFilters,
    });

    // Write to ref (not state) so fetchMorePhotoFeed always reads the current
    // value regardless of React closure timing.
    // attractions = itinerary/plan items the user already has — excluded from every batch.
    photoFeedCtxRef.current = { cities: ctxCities, filters: ctxFilters, attractions: ctxAttractions };

    try {
      const res = await fetch('/api/suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'photo-feed', cities: ctxCities, attractions: ctxAttractions, filters: ctxFilters, language: lang, staged: true }),
      });
      const data = await res.json();
      const suggestions = data.suggestions;
      if (Array.isArray(suggestions) && suggestions.length > 0) {
        // Deduplicate: remove attractions already in the itinerary or plan
        const existingNames = new Set(ctxAttractions.map(n => n.toLowerCase()));
        const deduped = suggestions.filter(s => !existingNames.has(s.name.toLowerCase()));
        const items = (deduped.length > 0 ? deduped : suggestions).map((s, i) => ({
          id: `feed-${i}`, name: s.name, cityName: s.city || ctxCities[0] || '',
          types: s.types || [],
          photoRef: s.photoRef || null,     // server pre-validates and pre-fetches
          placeId: s.placeId || null,
          photoFetched: !!s.photoRef,       // skip client fetch if server already provided
          reason: s.reason || '',
        }));
        photoFeedRef.current = items;
        setPhotoFeed(items);
        if (items[0] && !items[0].photoFetched) fetchPhotoAt(0, items);
        if (items[1] && !items[1].photoFetched) setTimeout(() => fetchPhotoAt(1, items), 800);
        // Preload first few card images so they're browser-cached before first swipe
        items.slice(0, 4).forEach(it => { if (it.photoRef) { const img = new Image(); img.src = `/api/place-photo?ref=${it.photoRef}&maxwidth=800`; } });
        // Immediately background-fill to reach a full batch of 10
        setTimeout(() => fetchMorePhotoFeed(), 0);
      } else {
        console.error('photo-feed: empty or malformed suggestions', data);
        photoFeedRef.current = [{ id: 'err', _error: true }];
        setPhotoFeed([{ id: 'err', _error: true }]);
      }
    } catch (err) {
      console.error('photo feed:', err);
      photoFeedRef.current = [{ id: 'err', _error: true }];
      setPhotoFeed([{ id: 'err', _error: true }]);
    } finally {
      setPhotoFeedLoading(false);
    }
  };

  const photoFeedNext = () => {
    const nextIdx = photoFeedIdx + 1;
    setPhotoFeedIdx(nextIdx);
    const nextItem = photoFeed[nextIdx];
    if (nextItem && !nextItem.photoFetched) fetchPhotoAt(nextIdx);
    const nextNextItem = photoFeed[nextIdx + 1];
    if (nextNextItem && !nextNextItem.photoFetched) setTimeout(() => fetchPhotoAt(nextIdx + 1), 600);
    // Preload the next 2 card images so they're browser-cached before the user swipes to them
    [nextIdx + 1, nextIdx + 2].forEach(i => {
      const it = photoFeed[i];
      if (it?.photoRef) { const img = new Image(); img.src = `/api/place-photo?ref=${it.photoRef}&maxwidth=800`; }
    });
    if (nextIdx >= photoFeedRef.current.length - 2 && photoFeedRef.current.length < 50 && !photoFeedFetchingMoreRef.current) {
      fetchMorePhotoFeed();
    }
  };

  const fetchMorePhotoFeed = async () => {
    // Read from refs — not closure-captured state — so this is always current even when called
    // via setTimeout or from a stale closure (e.g. the background-fill triggered in enterPhotoMode).
    if (photoFeedFetchingMoreRef.current || photoFeedRef.current.length >= 50) return;
    photoFeedFetchingMoreRef.current = true;
    setPhotoFeedFetchingMore(true);
    // Reuse the exact context (cities + filters + itinerary attractions) from when photo mode was opened
    const ctxCities = photoFeedCtxRef.current.cities;
    const ctxFilters = photoFeedCtxRef.current.filters;
    const itineraryAttractions = photoFeedCtxRef.current.attractions || []; // already-planned items — always exclude
    const feedAttractions = photoFeedRef.current.map(i => i.name); // already-shown feed items — exclude repeats
    const ctxAttractions = [...new Set([...itineraryAttractions, ...feedAttractions].map(n => n.toLowerCase()))].map(n => n); // deduplicated combined list
    // Build exclusion set for client-side filtering after the API call
    const excludeSet = new Set(ctxAttractions.map(n => n.toLowerCase()));
    try {
      const res = await fetch('/api/suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'photo-feed', cities: ctxCities, attractions: ctxAttractions, filters: ctxFilters, language: lang }),
      });
      const data = await res.json();
      if (Array.isArray(data.suggestions) && data.suggestions.length > 0) {
        // Client-side dedup: drop any suggestion that's already on the itinerary/plan or already shown
        const newSuggestions = data.suggestions.filter(s => !excludeSet.has(s.name.toLowerCase()));
        const suggestionsToUse = newSuggestions.length > 0 ? newSuggestions : data.suggestions;
        const startIdx = photoFeedRef.current.length; // use ref — closure photoFeed is stale
        const newItems = suggestionsToUse.map((s, i) => ({
          id: `feed-${startIdx + i}`, name: s.name, cityName: s.city || ctxCities[0] || '',
          types: s.types || [],
          photoRef: s.photoRef || null,
          placeId: s.placeId || null,
          photoFetched: !!s.photoRef,
          reason: s.reason || '',
        }));
        const combined = [...photoFeedRef.current, ...newItems].slice(0, 50); // use ref
        photoFeedRef.current = combined;
        setPhotoFeed(combined);
        if (combined[startIdx] && !combined[startIdx].photoFetched) fetchPhotoAt(startIdx, combined);
        if (combined[startIdx + 1] && !combined[startIdx + 1].photoFetched) setTimeout(() => fetchPhotoAt(startIdx + 1, combined), 800);
      }
    } catch {}
    photoFeedFetchingMoreRef.current = false;
    setPhotoFeedFetchingMore(false);
  };

  const handlePhotoSnip = async (item) => {
    if (!user) { setAuthMode('register'); setShowAuthModal(true); setAuthReason('snip'); return; }
    track('photo_attraction_snipped', { attraction_name: item.name });
    await snipItem('attraction', { name: item.name, cityName: item.cityName, types: item.types, placeId: item.placeId, photoRef: item.photoRef });
    photoFeedNext();
  };

  const handlePhotoAddToPlan = async (item) => {
    let rating = item.rating ?? null;
    let ratingCount = item.ratingCount ?? null;
    if ((rating === null) && item.name) {
      try {
        const query = item.cityName ? `${item.name} ${item.cityName}` : item.name;
        const r = await fetch(`/api/search-attractions?query=${encodeURIComponent(query)}&language=${encodeURIComponent(lang)}`);
        const { results } = await r.json();
        if (results?.[0]) {
          rating = results[0].rating ?? null;
          ratingCount = results[0].user_ratings_total ?? null;
        }
      } catch {}
    }
    track('photo_attraction_added_to_plan', { attraction_name: item.name });
    addSnipToPlan({ type: 'attraction', name: item.name, cityName: item.cityName, types: item.types, placeId: item.placeId, rating, ratingCount });
    if (chatMode) {
      const label = item.cityName ? `${item.name} (${item.cityName})` : item.name;
      setChatDisplayMessages(prev => [...prev, { role: 'assistant', content: `Added ${label} to your plan ✓`, streaming: false }]);
    }
    photoFeedNext();
  };

  const handlePhotoTouchStart = (e) => {
    photoTouchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const handlePhotoTouchEnd = (e) => {
    if (!photoTouchStartRef.current) return;
    const dx = e.changedTouches[0].clientX - photoTouchStartRef.current.x;
    const dy = e.changedTouches[0].clientY - photoTouchStartRef.current.y;
    photoTouchStartRef.current = null;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    if (Math.max(absDx, absDy) < 40) return;
    // Prevent the browser from synthesising a click on whatever element the finger lands on
    e.preventDefault();
    if (absDy > absDx && dy < -60) {
      photoFeedNext();
    } else if (absDx >= absDy) {
      photoFeedNext();
    }
  };

  const dismissPhotoTutorial = () => {
    track('photo_tutorial_dismissed');
    localStorage.setItem('sniptrip_photo_tutorial_seen', '1');
    setShowPhotoTutorial(false);
  };

  // ── Share / legal / trip management ──────────────────────────────────────
  const handleSave = async () => {
    if (!user) { setShowAuthModal(true); setAuthMode('register'); return; }
    track('itinerary_saved', { itinerary_name: itinerary?.name });
    try {
      if (currentTripId) {
        clearTimeout(autoSaveTimerRef.current);
        await doAutoSave(itinerary, currentTripId, user.token);
        await loadTrips();
      } else {
        const res = await fetch('/api/trips/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
          body: JSON.stringify({ itinerary })
        });
        const data = await res.json();
        if (res.ok) {
          setCurrentTripId(data.id);
          setHasUnsavedChanges(false);
          await loadTrips();
        }
      }
    } catch { /* silent */ }
  };

  const handleShare = async () => {
    if (shareUrl) {
      track('share_link_copied', { itinerary_name: itinerary?.name });
      navigator.clipboard.writeText(shareUrl).then(() => {
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      });
      return;
    }
    setShareLoading(true);
    try {
      const res = await fetch('/api/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itinerary }),
      });
      const data = await res.json();
      if (res.ok && data.url) {
        track('itinerary_shared', { itinerary_name: itinerary?.name });
        setShareUrl(data.url);
        navigator.clipboard.writeText(data.url).then(() => {
          track('share_link_copied', { itinerary_name: itinerary?.name });
          setShareCopied(true);
          setTimeout(() => setShareCopied(false), 2000);
        });
      }
    } catch { /* silent */ } finally {
      setShareLoading(false);
    }
  };

  const openLegal = (page) => {
    setLegalHtml('');
    setLegalPage(page);
    trackPageView(page === 'terms' ? 'Terms' : 'Privacy', `/${page}`);
    const tryLang = lang !== 'en' ? lang : null;
    const load = tryLang
      ? fetch(`/legal/${page}-${tryLang}.html`)
          .then(r => r.ok ? r.text() : null)
          .catch(() => null)
          .then(html => html ?? fetch(`/legal/${page}.html`).then(r => r.text()))
      : fetch(`/legal/${page}.html`).then(r => r.text());
    load
      .then(html => setLegalHtml(html))
      .catch(() => setLegalHtml('<p>Content could not be loaded.</p>'));
  };

  const handleCloneTrip = async (trip) => {
    if (!user) return;
    track('trip_cloned', { trip_name: trip.name });
    try {
      const cloned = { ...trip, name: t('copy_of', trip.name || t('untitled_trip')) };
      await fetch('/api/trips/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({ itinerary: cloned })
      });
      await loadTrips();
    } catch { /* silent */ }
  };

  const doLoadTrip = (trip) => {
    track('trip_opened', { trip_name: trip.name });
    setCities([]);
    localStorage.removeItem('sniptrip_cities');
    setEditingActivity(null);
    setEditingNoteDay(null);
    setEditTime(''); setEditDuration('');
    setEditAddress(''); setEditActivityNote(''); setEditNameQuery(''); setEditNameResults([]);
    setItinerary(trip);
    setCurrentTripId(trip._id);
    setHasUnsavedChanges(false);
    setChatMode(false);
    setChatDisplayMessages([]);
    setChatWidgetStates({});
    chatAPIHistoryRef.current = [];
    setDayBannerPhotos({});
    setDayBannerAlternatives({});
    setBannerErrDays(new Set());
    setScreen('itinerary');
  };

  const handleLoadTrip = (trip) => {
    if (cities.length > 0 || hasUnsavedChanges) {
      setPendingLoadTrip(trip);
      return;
    }
    doLoadTrip(trip);
  };

  const handleDeleteTrip = async (tripId) => {
    if (!user) return;
    track('trip_deleted');
    try {
      await fetch(`/api/trips/delete?id=${tripId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (currentTripId === tripId) { setCurrentTripId(null); setHasUnsavedChanges(false); }
      await loadTrips();
    } catch { /* silent */ }
  };

  const handleRenameTrip = async (tripId) => {
    if (!user || !renameValue.trim()) return;
    track('trip_renamed', { trip_name: renameValue.trim() });
    try {
      await fetch(`/api/trips/update?id=${tripId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({ name: renameValue.trim() })
      });
      setRenamingTripId(null);
      setRenameValue('');
      await loadTrips();
    } catch { /* silent */ }
  };

  const handleNewTrip = () => {
    if (itinerary && hasUnsavedChanges) { setShowNewTripConfirm(true); return; }
    clearCurrentTrip();
  };

  const clearCurrentTrip = () => {
    try {
      sessionStorage.removeItem('sniptrip_session_screen');
      sessionStorage.removeItem('sniptrip_session_itinerary');
      sessionStorage.removeItem('sniptrip_session_trip');
    } catch {}
    setItinerary(null);
    setCities([]);
    setCurrentTripId(null);
    setHasUnsavedChanges(false);
    setShowNewTripConfirm(false);
    setCityDiscovery(IDLE_CITY);
    setAttrDiscovery({});
    setChatMode(false);
    setChatDisplayMessages([]);
    setChatWidgetStates({});
    chatAPIHistoryRef.current = [];
    setScreen('input');
  };

  // ── Admin functions ───────────────────────────────────────────────────────
  const openChangePassword = async () => {
    setShowChangePassword(true);
    setShowUserMenu(false);
    setCpError('');
    setCpSuccess('');
    setCpNewPassword('');
    setCpSelectedUserId('');
    try {
      const res = await fetch('/api/admin/users', {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      const data = await res.json();
      if (res.ok) setCpUsers(data.users || []);
    } catch { /* silent */ }
  };

  const handleChangePassword = async () => {
    if (!cpSelectedUserId) { setCpError(t('select_a_user')); return; }
    setCpError('');
    setCpSuccess('');
    setCpLoading(true);
    try {
      const res = await fetch('/api/admin/set-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({ userId: cpSelectedUserId, newPassword: cpNewPassword })
      });
      const data = await res.json();
      if (!res.ok) { setCpError(data.error); return; }
      setCpSuccess(t('password_updated'));
      setCpNewPassword('');
      setCpSelectedUserId('');
    } catch {
      setCpError(t('network_error_short'));
    } finally {
      setCpLoading(false);
    }
  };

  const openDeleteUser = async () => {
    setShowDeleteUser(true);
    setShowUserMenu(false);
    setDuError('');
    setDuSuccess('');
    setDuSelectedEmail('');
    try {
      const res = await fetch('/api/admin/users', {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      const data = await res.json();
      if (res.ok) setDuUsers(data.users || []);
    } catch { /* silent */ }
  };

  const handleDeleteUser = async () => {
    if (!duSelectedEmail) { setDuError(t('select_a_user')); return; }
    setDuError('');
    setDuSuccess('');
    setDuLoading(true);
    try {
      const res = await fetch(`/api/admin/delete-user?email=${encodeURIComponent(duSelectedEmail)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${user.token}` }
      });
      const data = await res.json();
      if (!res.ok) { setDuError(data.error); return; }
      setDuSuccess(t('deleted_user', duSelectedEmail));
      setDuSelectedEmail('');
      setDuUsers(duUsers.filter(u => u.email !== duSelectedEmail));
    } catch {
      setDuError(t('network_error_short'));
    } finally {
      setDuLoading(false);
    }
  };

  // ── Plan helpers ──────────────────────────────────────────────────────────

  // Core city-add logic — no intercept. Uses functional setCities so it batches
  // correctly with clearCurrentTrip() when called from confirmStartNewTrip.
  const doAddCity = async (cityToAdd, source) => {
    const normalizedName = cityToAdd.name.toLowerCase().split(',')[0].trim();
    // Skip adding if city already in plan (deduplication)
    const alreadyExists = cities.some(c => {
      const a = c.name.toLowerCase().split(',')[0].trim();
      const b = normalizedName;
      return a === b || a.includes(b) || b.includes(a);
    });
    if (alreadyExists) return;

    const isRegion = (cityToAdd.types || []).some(t => REGION_TYPES.has(t))
      && !CITY_STATE_EXCEPTIONS.has(normalizedName);
    // In chat mode, skip region expansion — the LLM adds specific cities directly
    if (isRegion && !chatMode) {
      setExpandingRegion(cityToAdd.name);
      setCurrentCity(''); setCitySuggestions([]); setShowCitySuggestions(false);
      try {
        const res = await fetch('/api/suggest', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'region-cities', region: cityToAdd.name, language: lang }),
        });
        const data = await res.json();
        if (Array.isArray(data.cities) && data.cities.length > 0) {
          const startIdx = cities.length;
          const newCities = data.cities.map(name => ({ name, placeId: null, location: null, attractions: [] }));
          setCities(prev => [...prev, ...newCities]);
          track('city_added', { city_name: cityToAdd.name, source: 'region_expansion', expanded_count: newCities.length });
          newCities.forEach((city, i) => {
            fetchWikipediaDescription(city.name, 'search', lang).then(description => {
              if (description) setCities(prev => prev.map(c => c.name === city.name && !c.description ? { ...c, description } : c));
            });
            if (!chatMode) autoPopulateAttractions(startIdx + i, city);
          });
        }
      } catch { /* silent */ }
      finally { setExpandingRegion(null); }
      return;
    }

    track('city_added', { city_name: cityToAdd.name, source });
    const newCityIndex = cities.length;
    setCities(prev => [...prev, { ...cityToAdd, attractions: [] }]);
    setCurrentCity(''); setCitySuggestions([]); setShowCitySuggestions(false);
    fetchWikipediaDescription(cityToAdd.name, 'search', lang).then(description => {
      if (description) setCities(prev => prev.map(c => c.name === cityToAdd.name && !c.description ? { ...c, description } : c));
    });
    // Skip auto-populate in chat mode (widget handles discovery) or when source is 'chat'
    if (source !== 'chat' && !chatMode) {
      if (screen === 'itinerary') {
        setPendingAutoPopulate({ cityIndex: newCityIndex, cityToAdd });
      } else {
        autoPopulateAttractions(newCityIndex, cityToAdd);
      }
    }
  };

  const addCity = async (cityData = null, source = 'manual') => {
    const cityToAdd = cityData || { name: currentCity.trim(), placeId: null, location: null };
    if (!cityToAdd.name) return;

    // When the plan is empty and an itinerary is already loaded, intercept and ask
    // the user whether they're adding to the existing trip or starting a new one.
    // pendingCityAdd being non-null means we're already showing the prompt — let it through.
    if (itinerary && cities.length === 0 && !pendingCityAdd) {
      setPendingCityAdd({ cityData: cityToAdd, source });
      return;
    }

    await doAddCity(cityToAdd, source);
  };

  // User chose to add the city to their existing loaded itinerary.
  const confirmAddToExisting = async () => {
    const { cityData, source } = pendingCityAdd;
    setPendingCityAdd(null);
    await doAddCity(cityData, source);
  };

  // User chose to start fresh — clear the loaded itinerary then add the city.
  // Functional setCities inside doAddCity means the clear and add batch correctly.
  const confirmStartNewTrip = async () => {
    const { cityData, source } = pendingCityAdd;
    setPendingCityAdd(null);
    clearCurrentTrip();
    await doAddCity(cityData, source);
  };

  const addAttraction = (cityIndex, attractionData = null) => {
    const attractionToAdd = attractionData || { 
      name: currentAttraction.trim(), 
      placeId: null,
      duration: '2-3 hours'
    };
    
    if (!attractionToAdd.name) return;

    // Determine real target city from formatted_address if available
    let targetCityIndex = cityIndex;
    let targetCityName = cities[cityIndex]?.name || '';
    let isNewCity = false;

    if (attractionToAdd.fullName) {
      const extracted = extractCityFromAddress(attractionToAdd.fullName);
      if (extracted) {
        const stem = extracted.split(',')[0].toLowerCase().trim();
        const currentStem = targetCityName.split(',')[0].toLowerCase().trim();
        if (stem !== currentStem) {
          const matchIdx = cities.findIndex(c => {
            const cStem = c.name.split(',')[0].toLowerCase().trim();
            return cStem === stem || cStem.includes(stem) || stem.includes(cStem);
          });
          if (matchIdx !== -1) {
            targetCityIndex = matchIdx;
            targetCityName = cities[matchIdx].name;
          } else {
            // Attraction's city not in plan — will auto-add it
            targetCityName = extracted;
            isNewCity = true;
          }
        }
      }
    }

    const attrWithCity = { ...attractionToAdd, cityName: attractionToAdd.cityName || targetCityName };
    track('attraction_added', { attraction_name: attrWithCity.name, city_name: targetCityName });

    const toastCity = targetCityName.split(',')[0];
    const toastMsg = isNewCity
      ? `${attrWithCity.name} added · ${toastCity} (new stop)`
      : `${attrWithCity.name} added · ${toastCity}`;
    setAttrAddToast(toastMsg);
    setTimeout(() => setAttrAddToast(null), 3000);

    if (isNewCity) {
      const newCityIndex = cities.length;
      setCities(prev => [...prev, { name: targetCityName, placeId: null, location: null, attractions: [attrWithCity] }]);
      setAddedAttractionCity(newCityIndex);
    } else {
      setCities(prev => prev.map((c, i) => i !== targetCityIndex ? c : {
        ...c, attractions: [...c.attractions, attrWithCity]
      }));
      setAddedAttractionCity(targetCityIndex);
      const newAttrIdx = cities[targetCityIndex].attractions.length;
      setLastAddedKey(`${targetCityIndex}-${newAttrIdx}`);
    }

    setCurrentAttraction('');
    setAttractionSuggestions([]);
    setShowAttractionSuggestions(false);
    setTimeout(() => { setAddedAttractionCity(null); setLastAddedKey(null); }, 2000);

    fetchWikipediaDescription(attrWithCity.name, 'direct', lang).then(description => {
      if (description) {
        setCities(prev => {
          const idx = prev.findIndex(c => c.name.split(',')[0].toLowerCase() === targetCityName.split(',')[0].toLowerCase());
          if (idx < 0) return prev;
          return prev.map((c, i) => i !== idx ? c : {
            ...c, attractions: c.attractions.map(a => a.name === attrWithCity.name ? { ...a, description } : a)
          });
        });
      }
    });

    insertAttractionIntoItinerary(attrWithCity, targetCityName);
  };

  // --- Conversational discovery helpers ---

  const discoveryContext = () => ({
    cities: cities.map(c => c.name),
    attractions: cities.flatMap(c => c.attractions.map(a => typeof a === 'string' ? a : a.name)),
  });

  // When a saved trip is open and cities state is empty, derive context from the itinerary instead.
  const itineraryDiscoveryContext = () => {
    if (itinerary?.days?.length && cities.length === 0) {
      return {
        cities: [...new Set(itinerary.days.map(d => d.city).filter(Boolean))],
        attractions: itinerary.days.flatMap(d => d.activities.map(a => a.name)),
      };
    }
    return discoveryContext();
  };

  const addDiscoveredCity = (cityName) => {
    addCity({ name: cityName, placeId: null, location: null }, 'city_discovery');
  };

  const handleDestinationTap = (dest) => {
    track('destination_card_tapped', { city: dest.name });
    const cityName = dest.key ? t(dest.key) : dest.name;
    addCity({ name: cityName, placeId: null, location: null }, 'destination_card');
    if (dest.tags?.length) {
      try { localStorage.setItem('sniptrip_filters', JSON.stringify(dest.tags)); } catch {}
      setAttractionFilters(dest.tags);
    }
    enterPhotoMode({ cityOverride: cityName, filtersOverride: dest.tags || [] });
  };

  const patchCityDiscovery = (patch) => setCityDiscovery(prev => ({ ...prev, ...patch }));
  const patchAttrDiscovery = (cityIdx, patch) => setAttrDiscovery(prev => ({ ...prev, [cityIdx]: { ...prev[cityIdx], ...patch } }));

  const addDiscoveredAttraction = async (cityIdx, suggestion) => {
    const cityName = cities[cityIdx]?.name;
    if (!cityName) return;
    setAddingDiscoveredAttr({ cityIdx, name: suggestion.name });
    try {
      const data = await fetchAttractionData(suggestion.name, cityName);
      addAttraction(cityIdx, data
        ? { ...data, cityName }
        : { name: suggestion.name, duration: '2-3 hours', cityName });
    } catch {
      addAttraction(cityIdx, { name: suggestion.name, duration: '2-3 hours', cityName });
    } finally {
      setAddingDiscoveredAttr(null);
    }
  };

  const patchDayAddDisc = (dayId, patch) =>
    setDayAddDiscovery(prev => ({ ...prev, [dayId]: { ...prev[dayId], ...patch } }));

  const addDiscoveredAttractionToDay = async (dayIdx, suggestion) => {
    const day = itinerary?.days[dayIdx];
    if (!day) return;
    const cityName = day.city;
    setAddingDayAttr({ dayId: day.id, name: suggestion.name });
    try {
      const data = await fetchAttractionData(suggestion.name, cityName);
      insertAttractionIntoDayIdx(
        data ? { ...data, cityName } : { name: suggestion.name, duration: '2-3 hours', cityName },
        dayIdx
      );
    } catch {
      insertAttractionIntoDayIdx({ name: suggestion.name, duration: '2-3 hours', cityName }, dayIdx);
    } finally {
      setAddingDayAttr(null);
    }
  };

  // --- Empty-day discovery helpers ---

  const patchEdd = (dayId, key, patch) =>
    setEmptyDayDisc(prev => ({ ...prev, [dayId]: { ...prev[dayId], [key]: { ...prev[dayId]?.[key], ...patch } } }));

  const selectEmptyDayDest = async (dayId, dayIdx, destName) => {
    const shortName = destName.split(',')[0].trim();
    setItinerary(cur => ({ ...cur, days: cur.days.map((d, i) => i !== dayIdx ? d : { ...d, city: shortName }) }));
    setHasUnsavedChanges(true);
    setEmptyDayDisc(prev => ({
      ...prev,
      [dayId]: { ...prev[dayId], selectedDest: destName, attrDisc: { phase: 'loading', questions: [], suggestions: [], mode: null, freeformVisible: false, freeformText: '' } },
    }));
    try {
      const allCities = [...new Set([...cities.map(c => c.name), destName])];
      const allAttr = cities.flatMap(c => c.attractions.map(a => typeof a === 'string' ? a : a.name));
      const res = await fetch('/api/suggest', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'attraction-questions', cities: allCities, attractions: allAttr, filters: [], language: lang }) });
      const data = await res.json();
      patchEdd(dayId, 'attrDisc', { phase: 'questions', questions: data.questions || [], suggestions: data.suggestions || [], mode: data.mode, freeform: data.freeform });
    } catch {
      patchEdd(dayId, 'attrDisc', { phase: 'error', questions: [], suggestions: [] });
    }
  };

  const addEmptyDayAttraction = async (dayId, dayIdx, suggestion) => {
    const disc = emptyDayDisc[dayId];
    const cityName = disc?.selectedDest?.split(',')[0].trim() || itinerary.days[dayIdx]?.city || '';
    setAddingEmptyDayAttr({ dayId, name: suggestion.name });
    let activity;
    try {
      const res = await fetch(`/api/search-attractions?query=${encodeURIComponent(suggestion.name + (cityName ? ' ' + cityName : ''))}&language=${encodeURIComponent(lang)}`);
      const { results } = await res.json();
      if (results?.length > 0) {
        const place = results[0];
        const photoRef = place.photos?.[0]?.photo_reference || null;
        activity = {
          id: `activity-${Date.now()}`,
          name: place.name || suggestion.name,
          fullName: place.formatted_address,
          placeId: place.place_id,
          types: place.types || [],
          duration: estimateDuration(place.types || []),
          photoRefs: photoRef ? [photoRef] : [],
          cityName,
          time: '9:00 AM',
          rating: place.rating ?? null,
          ratingCount: place.user_ratings_total ?? null,
        };
      }
    } catch {}
    if (!activity) activity = { id: `activity-${Date.now()}`, name: suggestion.name, duration: '2-3 hours', cityName, time: '9:00 AM' };
    setItinerary(cur => ({ ...cur, days: cur.days.map((d, i) => i !== dayIdx ? d : { ...d, activities: [...d.activities, activity] }) }));
    setHasUnsavedChanges(true);
    setAddingEmptyDayAttr(null);
    // Panel closes naturally since day.activities.length > 0 after this
  };

  // ── Remove helpers ────────────────────────────────────────────────────────
  const removeCity = (index) => {
    track('city_removed', { city_name: cities[index]?.name });
    const newCities = cities.filter((_, i) => i !== index);
    setCities(newCities);
    if (selectedCityIndex === index) setSelectedCityIndex(null);
    setAttrDiscovery({});
    if (newCities.length === 0 && chatMode) {
      const greeting = "Where would you like to go? Name a destination, a region, or describe a vibe — I'll help you build the trip.";
      setChatDisplayMessages([{ role: 'assistant', content: greeting, streaming: false }]);
      setChatWidgetStates({});
      chatAPIHistoryRef.current = [
        { role: 'user', content: 'Hi' },
        { role: 'assistant', content: greeting },
      ];
    }
  };

  const removeAttraction = (cityIndex, attractionIndex) => {
    const attr = cities[cityIndex]?.attractions[attractionIndex];
    track('attraction_removed', { attraction_name: typeof attr === 'string' ? attr : attr?.name, source: 'plan' });
    setCities(prev => prev.map((c, ci) => ci !== cityIndex ? c : {
      ...c,
      attractions: c.attractions.map((a, ai) => ai !== attractionIndex ? a : (
        typeof a === 'string' ? { name: a, removed: true } : { ...a, removed: true }
      )),
    }));
  };

  const restoreAttraction = (cityIndex, attractionIndex) => {
    setCities(prev => prev.map((c, ci) => ci !== cityIndex ? c : {
      ...c,
      attractions: c.attractions.map((a, ai) => ai !== attractionIndex ? a : (
        typeof a === 'object' ? { ...a, removed: false } : a
      )),
    }));
  };

  const fetchAttractionsForCity = async (city, cityIndex, overrideDays = null) => {
    const isMajor = isMajorTouristCity(city.name);
    const targetDays = overrideDays ?? (isMajor ? 2 : 1);
    let activeLabels = attractionFilters;
    if (!Array.isArray(activeLabels)) activeLabels = activeLabels === 'All' ? [] : [activeLabels];
    activeLabels = activeLabels.filter(l => l !== 'All');
    const activeFilterDefs = activeLabels.length > 0
      ? activeLabels.map(l => ATTRACTION_FILTERS.find(f => f.label === l)).filter(Boolean)
      : null;
    const combinedTypes = activeFilterDefs
      ? [...new Set(activeFilterDefs.flatMap(f => f.types || []))]
      : null;
    const searchQuery = activeLabels.length > 0
      ? `${activeLabels.join(' ')} attractions ${city.name}`
      : `top attractions ${city.name}`;

    const response = await fetch(`/api/search-attractions?query=${encodeURIComponent(searchQuery)}&language=${encodeURIComponent(lang)}`);
    const data = await response.json();
    if (!data.results || data.results.length === 0) return [];

    const filtered = combinedTypes
      ? data.results.filter(place => (place.types || []).some(t => combinedTypes.includes(t)))
      : data.results.filter(place => {
          const types = place.types || [];
          return types.includes('tourist_attraction') || types.includes('museum') ||
                 types.includes('point_of_interest') || types.includes('park') ||
                 types.includes('natural_feature') || types.includes('church') ||
                 types.includes('art_gallery') || types.includes('amusement_park');
        });

    const attractionsToUse = filtered.length > 0 ? filtered : data.results;
    const existingNames = new Set(city.attractions.map(a => a.name.toLowerCase()));
    const newAttractions = attractionsToUse.filter(p => !existingNames.has(p.name.toLowerCase()));

    let totalHours = 0;
    const maxHours = targetDays * 8;
    const selected = [];
    for (const place of newAttractions) {
      const duration = estimateDuration(place.types || []);
      const hours = parseFloat(duration.split('-')[0]);
      if (totalHours + hours <= maxHours) {
        selected.push({ name: place.name, fullName: place.formatted_address, placeId: place.place_id, types: place.types || [], duration });
        totalHours += hours;
      }
      if (totalHours >= maxHours) break;
    }
    return selected;
  };

  // ── Generate itinerary ────────────────────────────────────────────────────
  // topPlacesData: full Place objects pre-fetched by autoPopulateAttractions — used to match
  // Claude's suggestions without extra API calls. Falls back to individual calls when no match.
  const generateWithClaude = async (city, targetDays, googlePlaces = [], topPlacesData = []) => {
    try {
      const res = await fetch('/api/suggest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'generate-itinerary',
          cities: [city.name],
          filters: attractionFilters,
          days: targetDays,
          language: lang,
          googlePlaces,
        }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data.days || !Array.isArray(data.days)) return null;

      const enrichedDays = await Promise.all(
        data.days.map(async (day) => {
          const activities = await Promise.all(
            (day.activities || []).map(async (act) => {
              let placeId = null, fullName = act.name, types = [], photoRefs = [];
              try {
                // Try to match against the pre-fetched top-attractions data first (free)
                const actLower = act.name.toLowerCase();
                const cached = topPlacesData.find(p => {
                  const pLower = (p.name || '').toLowerCase();
                  return pLower === actLower || pLower.includes(actLower) || actLower.includes(pLower);
                });
                if (cached) {
                  placeId = cached.place_id || null;
                  fullName = cached.formatted_address || act.name;
                  types = cached.types || [];
                  photoRefs = cached.photos?.map(p => p.photo_reference) || [];
                } else {
                  // Fall back to individual Places call only when no pre-fetched match
                  const q = encodeURIComponent(`${act.name} ${city.name}`);
                  const pRes = await fetch(`/api/search-attractions?query=${q}&language=${encodeURIComponent(lang)}`);
                  const pData = await pRes.json();
                  const match = pData.results?.[0];
                  if (match) {
                    placeId = match.place_id || null;
                    fullName = match.formatted_address || act.name;
                    types = match.types || [];
                    photoRefs = match.photos?.map(p => p.photo_reference) || [];
                  }
                }
              } catch { /* leave defaults */ }
              return {
                id: `act-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                name: act.name,
                time: act.time,
                duration: act.duration || estimateDuration(types),
                description: act.note,
                cityName: city.name,
                fullName,
                placeId,
                types,
                photoRefs,
              };
            })
          );
          return {
            id: `day-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            city: city.name,
            activities,
            expanded: true,
            note: '',
          };
        })
      );
      return enrichedDays;
    } catch {
      return null;
    }
  };

  const generateItinerary = async (requestedDays = null) => {
    track('generate_itinerary', { city_count: cities.length });

    const finishItinerary = () => {
      setScreen('itinerary');
    };

    const hasExisting = itinerary?.days?.length > 0;

    if (hasExisting) {
      // Incremental mode: only add what's new to the existing itinerary
      const existingCityShorts = new Set(
        itinerary.days.map(d => (d.city || '').split(',')[0].toLowerCase().trim())
      );

      // Batch-insert Plan tab attractions for existing cities not yet in the itinerary
      const toInsert = [];
      for (const city of cities) {
        const cs = city.name.split(',')[0].toLowerCase().trim();
        if (!existingCityShorts.has(cs)) continue;
        const existingNames = new Set(
          itinerary.days
            .filter(d => (d.city || '').split(',')[0].toLowerCase().trim() === cs)
            .flatMap(d => d.activities.map(a => a.name.toLowerCase()))
        );
        for (const attr of city.attractions.filter(a => !a.removed)) {
          if (!existingNames.has(attr.name.toLowerCase())) {
            toInsert.push({ attr, cityName: city.name });
          }
        }
      }

      if (toInsert.length > 0) {
        setItinerary(prev => {
          let days = prev.days;
          for (const { attr, cityName } of toInsert) {
            const cs = cityName.split(',')[0].toLowerCase().trim();
            const idx = days.findIndex(d => (d.city || '').split(',')[0].toLowerCase().trim() === cs);
            if (idx < 0) continue;
            const lastTimed = [...days[idx].activities].reverse().find(a => a.time && a.type !== 'hotel');
            const activity = {
              id: `act-${Date.now()}-${Math.random().toString(36).slice(2)}`,
              name: attr.name,
              fullName: attr.fullName,
              placeId: attr.placeId,
              types: attr.types || [],
              duration: attr.duration || estimateDuration(attr.types || []),
              photoRefs: attr.photoRefs || [],
              cityName,
              time: lastTimed ? calculateNextTime(lastTimed.time, lastTimed.duration) : '9:00 AM',
            };
            days = days.map((d, i) => i !== idx ? d : { ...d, activities: [...d.activities, activity] });
          }
          return { ...prev, days };
        });
        setHasUnsavedChanges(true);
      }

      // Generate days for cities not yet in the itinerary
      const newCities = cities.filter(c =>
        !existingCityShorts.has(c.name.split(',')[0].toLowerCase().trim())
      );

      if (newCities.length === 0) {
        finishItinerary();
        return;
      }

      setGeneratingItinerary(true);
      try {
        const cityDaysResults = await Promise.all(newCities.map(async (city) => {
          const activeAttrs = city.attractions.filter(a => !a.removed);
          if (activeAttrs.length > 0) return buildItinerary([{ ...city, attractions: activeAttrs }]).days;
          const targetDays = requestedDays || (isMajorTouristCity(city.name) ? 2 : 1);
          const claudeDays = await generateWithClaude(city, targetDays);
          if (claudeDays) return claudeDays;
          const fetched = await fetchAttractionsForCity(city, 0);
          return buildItinerary([{ ...city, attractions: fetched }]).days;
        }));
        const allNewDays = cityDaysResults.flat();

        setItinerary(prev => {
          const lastDay = prev.days[prev.days.length - 1];
          let dayNum = lastDay?.day || 0;
          const renumbered = allNewDays.map(d => ({ ...d, day: ++dayNum }));
          return { ...prev, days: [...prev.days, ...renumbered] };
        });
        setHasUnsavedChanges(true);
      } catch (err) {
        console.error('Error generating new city days:', err);
      } finally {
        setGeneratingItinerary(false);
      }

      finishItinerary();
      return;
    }

    // Fresh generation (no existing itinerary)
    const emptyCities = cities.filter(c => c.attractions.filter(a => !a.removed).length === 0);
    if (emptyCities.length === 0) {
      setGeneratingItinerary(true);
      try {
        const res = await fetch('/api/suggest', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'schedule-itinerary',
            cities: cities.map(c => ({
              name: c.name,
              attractions: c.attractions.filter(a => !a.removed).map(a => ({ name: a.name, duration: a.duration || estimateDuration(a.types || []) })),
            })),
            language: lang,
            ...(requestedDays && { maxDays: requestedDays }),
          }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.days?.length > 0) {
            const attrByName = {};
            cities.forEach(c => c.attractions.filter(a => !a.removed).forEach(a => { attrByName[a.name.toLowerCase()] = { ...a, cityName: c.name }; }));
            let dayNum = 0;
            const days = data.days.map(day => ({
              id: `day-${Date.now()}-${++dayNum}`,
              day: dayNum,
              city: day.city || cities[0]?.name,
              theme: day.theme,
              activities: (day.activities || []).map(act => {
                const stored = attrByName[act.name.toLowerCase()] || {};
                return {
                  id: `act-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                  name: act.name,
                  time: act.time,
                  duration: act.duration || estimateDuration(stored.types || []),
                  description: act.note,
                  cityName: stored.cityName || day.city || cities[0]?.name,
                  fullName: stored.fullName || act.name,
                  placeId: stored.placeId || null,
                  types: stored.types || [],
                  photoRefs: stored.photoRefs || [],
                  ...(stored.rating != null && { rating: stored.rating }),
                  ...(stored.ratingCount != null && { ratingCount: stored.ratingCount }),
                };
              }),
              expanded: true,
              note: day.theme || '',
            }));
            const name = cities.length === 1 ? `${cities[0].name} Trip` : `${cities[0].name.split(',')[0]} & More`;
            setItinerary({ name, days });
            setHasUnsavedChanges(true);
            finishItinerary();
            return;
          }
        }
      } catch { /* fall through to buildItinerary */ }
      finally { setGeneratingItinerary(false); }
      setItinerary(buildItinerary(cities.map(c => ({ ...c, attractions: c.attractions.filter(a => !a.removed) }))));
      setHasUnsavedChanges(true);
      finishItinerary();
      return;
    }

    setGeneratingItinerary(true);
    try {
      // Distribute requested days across cities proportionally
      const daysForCity = (city) => {
        if (!requestedDays) return isMajorTouristCity(city.name) ? 2 : 1;
        // Split days across empty cities; major cities get a larger share
        const weights = cities.map(c => isMajorTouristCity(c.name) ? 2 : 1);
        const totalWeight = weights.reduce((a, b) => a + b, 0);
        const cityWeight = isMajorTouristCity(city.name) ? 2 : 1;
        return Math.max(1, Math.round((cityWeight / totalWeight) * requestedDays));
      };

      const cityResults = await Promise.all(
        cities.map(async (city) => {
          const activeAttrs = city.attractions.filter(a => !a.removed);
          if (activeAttrs.length > 0) return { city: { ...city, attractions: activeAttrs }, claudeDays: null };
          const targetDays = daysForCity(city);
          const claudeDays = await generateWithClaude(city, targetDays);
          if (claudeDays) return { city, claudeDays };
          const fetched = await fetchAttractionsForCity(city, 0, targetDays);
          return { city: { ...city, attractions: [...city.attractions, ...fetched] }, claudeDays: null };
        })
      );

      const allDays = [];
      let dayNum = 0;
      for (const { city, claudeDays } of cityResults) {
        const sourceDays = claudeDays || buildItinerary([city]).days;
        for (const d of sourceDays) {
          allDays.push({ ...d, day: ++dayNum });
        }
      }

      const updatedCities = cityResults.map(({ city }) => city);
      if (updatedCities.some((c, i) => c !== cities[i])) setCities(updatedCities);

      setItinerary({ name: buildItinerary(cities).name, days: allDays });
      finishItinerary();
    } catch (err) {
      console.error('Error generating itinerary:', err);
      setItinerary(buildItinerary(cities.map(c => ({ ...c, attractions: c.attractions.filter(a => !a.removed) }))));
      finishItinerary();
    } finally {
      setGeneratingItinerary(false);
    }
  };

  // ── Chat mode ─────────────────────────────────────────────────────────────────

  const buildTripState = () => ({
    cities: cities.map(c => ({
      name: c.name,
      attractions: c.attractions.filter(a => !a.removed).map(a => a.name),
    })),
    filters: attractionFilters,
  });

  const chatAddCity = async (cityName) => {
    try {
      // Bias search toward the country already in the plan to avoid wrong-country results
      // (e.g. "Cordoba" → Córdoba Argentina, not Córdoba Spain, when other cities are Argentine)
      const inferCountry = () => {
        for (const c of cities) {
          const parts = c.name.split(',').map(s => s.trim());
          const last = parts[parts.length - 1];
          if (last && last.length > 2) return last;
        }
        return null;
      };
      const country = inferCountry();
      const biasedQuery = country && !cityName.toLowerCase().includes(country.toLowerCase())
        ? `${cityName}, ${country}` : cityName;
      const res = await fetch(`/api/search-cities?input=${encodeURIComponent(biasedQuery)}&language=${encodeURIComponent(lang)}`);
      const data = await res.json();
      // Pick the first prediction that isn't transport infrastructure (airports, stations, etc.)
      const predictions = data.predictions || [];
      const usablePrediction = predictions.find(p => !(p.types || []).some(t => TRANSPORT_TYPES.has(t)));
      const p = usablePrediction || predictions[0];
      const cityToAdd = p
        ? { name: p.description, placeId: p.place_id, location: null, types: p.types || [] }
        : { name: cityName, placeId: null, location: null, types: [] };
      // Reject transport infrastructure even if it was the only result
      if ((cityToAdd.types || []).some(t => TRANSPORT_TYPES.has(t))) {
        return { error: `Could not find a city called "${cityName}" — got a transport location instead. Try a more specific city name.` };
      }
      // Reject country/admin-region results — LLM should ask for specific cities instead.
      // natural_feature is NOT blocked here — Grand Canyon, Iguazu Falls, Yosemite etc. are valid.
      const isRegionResult = (cityToAdd.types || []).some(t => CHAT_BLOCK_TYPES.has(t))
        && !CITY_STATE_EXCEPTIONS.has(cityToAdd.name.toLowerCase().split(',')[0].trim());
      if (isRegionResult) {
        return { error: `"${cityName}" is a country or region, not a specific city. Ask the user which specific cities they want to visit there.` };
      }
      await doAddCity(cityToAdd, 'chat');
      return { success: true, city: cityToAdd.name };
    } catch {
      return { error: `Could not add city: ${cityName}` };
    }
  };

  const chatAddAttraction = (cityName, suggestionOrName) => {
    // AttrDiscoveryPanel calls onAdd(sug) with the full suggestion object; accept either
    const attractionName = typeof suggestionOrName === 'string' ? suggestionOrName : (suggestionOrName?.name || '');
    const sug = typeof suggestionOrName === 'object' && suggestionOrName !== null ? suggestionOrName : null;
    if (!attractionName) return { error: 'No attraction name provided' };
    const stem = cityName.toLowerCase().split(',')[0].trim();
    const newAttr = {
      name: attractionName,
      fullName: sug?.fullName || attractionName,
      placeId: sug?.placeId || null,
      types: [],
      duration: estimateDuration([]),
      photoRefs: sug?.photoRefs || [],
      rating: sug?.rating ?? null,
      ratingCount: sug?.ratingCount ?? null,
    };
    // Use functional update so the city lookup reads the latest state.
    // If the city isn't in the plan yet, add it now so "+" always works.
    setCities(prev => {
      const idx = prev.findIndex(c => {
        const cStem = c.name.toLowerCase().split(',')[0].trim();
        return cStem.includes(stem) || stem.includes(cStem);
      });
      if (idx !== -1) {
        return prev.map((c, i) => i === idx ? { ...c, attractions: [...c.attractions, newAttr] } : c);
      }
      return [...prev, { name: cityName, placeId: null, location: null, attractions: [newAttr] }];
    });
    // Fetch real Google Places types so estimateDuration is accurate (e.g. amusement_park = full day)
    const query = cityName ? `${attractionName} ${cityName}` : attractionName;
    fetch(`/api/search-attractions?query=${encodeURIComponent(query)}&language=${encodeURIComponent(lang)}`)
      .then(r => r.json())
      .then(({ results }) => {
        const place = results?.[0];
        if (!place) return;
        const placeTypes = place.types || [];
        const dur = estimateDuration(placeTypes);
        const photoRef = place.photos?.[0]?.photo_reference || null;
        setCities(prev => prev.map(c => {
          const cStem = c.name.toLowerCase().split(',')[0].trim();
          if (!cStem.includes(stem) && !stem.includes(cStem)) return c;
          return { ...c, attractions: c.attractions.map(a => a.name === attractionName ? {
            ...a,
            types: placeTypes,
            duration: dur,
            placeId: a.placeId || place.place_id || null,
            fullName: a.fullName === attractionName ? (place.formatted_address || attractionName) : a.fullName,
            photoRefs: a.photoRefs.length ? a.photoRefs : (photoRef ? [photoRef] : []),
            rating: a.rating ?? place.rating ?? null,
            ratingCount: a.ratingCount ?? place.user_ratings_total ?? null,
          } : a) };
        }));
      }).catch(() => {});
    fetchWikipediaDescription(attractionName, 'direct', lang).then(desc => {
      if (!desc) return;
      setCities(prev => prev.map(c => {
        const cStem = c.name.toLowerCase().split(',')[0].trim();
        if (!cStem.includes(stem) && !stem.includes(cStem)) return c;
        return { ...c, attractions: c.attractions.map(a => a.name === attractionName && !a.description ? { ...a, description: desc } : a) };
      }));
    }).catch(() => {});
    return { success: true, city: cityName, attraction: attractionName };
  };

  const chatRemoveAttraction = (cityName, attractionName) => {
    const stem = cityName.toLowerCase().split(',')[0].trim();
    const cityIdx = cities.findIndex(c => c.name.toLowerCase().split(',')[0].trim().includes(stem));
    if (cityIdx === -1) return { error: `${cityName} not in plan` };
    const attrIdx = cities[cityIdx].attractions.findIndex(a => a.name.toLowerCase().includes(attractionName.toLowerCase()));
    if (attrIdx === -1) return { error: `${attractionName} not found` };
    removeAttraction(cityIdx, attrIdx);
    return { success: true };
  };

  const chatRemoveCity = (cityName) => {
    const stem = cityName.toLowerCase().split(',')[0].trim();
    const cityIdx = cities.findIndex(c => c.name.toLowerCase().split(',')[0].trim().includes(stem));
    if (cityIdx === -1) return { error: `${cityName} not found in plan` };
    setCities(prev => prev.filter((_, i) => i !== cityIdx));
    return { success: true, removed: cityName };
  };

  const chatSetFilters = (filters) => {
    const valid = filters.filter(f => ['Culture','Nature','Entertainment','Shopping','Food & Drink','Kids','Seniors'].includes(f));
    try { localStorage.setItem('sniptrip_filters', JSON.stringify(valid)); } catch {}
    setAttractionFilters(valid);
    return { success: true, filters: valid };
  };

  const executeChatTools = async (toolCalls) => {
    const results = [];
    const deferredWidgets = [];
    for (const tool of toolCalls) {
      let result;
      track('chat_tool_called', { tool: tool.name });
      if (tool.name === 'add_city') {
        result = await chatAddCity(tool.input.name);
      } else if (tool.name === 'add_attraction') {
        result = chatAddAttraction(tool.input.city, tool.input.name);
      } else if (tool.name === 'remove_attraction') {
        result = chatRemoveAttraction(tool.input.city, tool.input.name);
      } else if (tool.name === 'remove_city') {
        result = chatRemoveCity(tool.input.name);
      } else if (tool.name === 'set_filters') {
        result = chatSetFilters(tool.input.filters);
      } else if (tool.name === 'suggest_attractions') {
        const cityKey = (tool.input.city || '').toLowerCase().split(',')[0].trim();
        const alreadyDeferred = deferredWidgets.some(w =>
          w.widget?.type === 'suggest_attractions' &&
          (w.widget.city || '').toLowerCase().split(',')[0].trim() === cityKey
        );
        if (alreadyDeferred) {
          result = { success: true, rendered: 'existing_widget', note: 'Attraction widget already shown in this response' };
        } else {
          const widgetId = `widget-${Date.now()}-${Math.random()}`;
          deferredWidgets.push({
            role: 'assistant', content: '',
            widget: { type: 'suggest_attractions', city: tool.input.city, context: tool.input.context, id: widgetId, count: tool.input.count },
          });
          result = { success: true, rendered: 'inline_widget' };
        }
      } else if (tool.name === 'suggest_region_cities') {
        const regionKey = (tool.input?.region || '').toLowerCase().trim();
        const regionAlreadyDeferred = deferredWidgets.some(w =>
          w.widget?.type === 'suggest_region_cities' &&
          (w.widget.region || '').toLowerCase().trim() === regionKey
        );
        const regionAlreadyVisible = chatDisplayMessages.some(m =>
          m.widget?.type === 'suggest_region_cities' &&
          (m.widget.region || '').toLowerCase().trim() === regionKey &&
          chatWidgetStates[m.widget.id]?.phase !== 'idle'
        );
        if (regionAlreadyDeferred || regionAlreadyVisible) {
          result = { success: true, rendered: 'existing_widget', note: 'Region city widget already visible' };
        } else {
          const widgetId = `widget-${Date.now()}-${Math.random()}`;
          deferredWidgets.push({
            role: 'assistant', content: '',
            widget: { type: 'suggest_region_cities', region: tool.input?.region, cities: tool.input?.cities || [], id: widgetId },
          });
          result = { success: true, rendered: 'inline_widget' };
        }
      } else if (tool.name === 'suggest_cities') {
        const widgetId = `widget-${Date.now()}-${Math.random()}`;
        deferredWidgets.push({
          role: 'assistant', content: '',
          widget: { type: 'suggest_cities', context: tool.input?.context, id: widgetId },
        });
        result = { success: true, rendered: 'inline_widget' };
      } else if (tool.name === 'enter_photo_mode') {
        enterPhotoMode();
        result = { success: true };
      } else if (tool.name === 'generate_itinerary') {
        track('chat_itinerary_built', { city_count: cities.length });
        generateItinerary(tool.input?.days || null);
        result = { success: true };
      } else {
        result = { error: 'unknown tool' };
      }
      results.push({ id: tool.id, result });
    }
    return { results, deferredWidgets };
  };

  const callChatAPI = async (apiMessages) => {
    const res = await fetch('/api/suggest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'chat', messages: apiMessages, tripState: buildTripState(), language: lang, userToken: user?.token || null }),
    });
    if (!res.ok) throw new Error('Chat API error');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let sseBuffer = '';
    let text = '';
    let toolUseBlocks = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      sseBuffer += decoder.decode(value, { stream: true });
      const lines = sseBuffer.split('\n');
      sseBuffer = lines.pop();
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const jsonStr = line.slice(6).trim();
        if (!jsonStr) continue;
        let event;
        try { event = JSON.parse(jsonStr); } catch { continue; }
        if (event.type === 'text') {
          text += event.delta;
          const snapshot = text;
          setChatDisplayMessages(prev => {
            const last = prev[prev.length - 1];
            if (last?.streaming) return [...prev.slice(0, -1), { ...last, content: snapshot }];
            return prev;
          });
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
          }
        } else if (event.type === 'tool_use') {
          toolUseBlocks = event.tools;
        }
      }
    }
    return { text, toolUseBlocks };
  };

  const sendChatMessage = async (inputText) => {
    const text = inputText.trim();
    if (!text || chatLoading) return;
    setChatInput('');
    setChatLoading(true);
    track('chat_message_sent', { turn: chatDisplayMessages.filter(m => m.role === 'user').length + 1 });

    setChatDisplayMessages(prev => [
      ...prev,
      { role: 'user', content: text },
      { role: 'assistant', content: '', streaming: true },
    ]);
    chatAPIHistoryRef.current = [...chatAPIHistoryRef.current, { role: 'user', content: text }];

    try {
      const { text: assistantText, toolUseBlocks } = await callChatAPI(chatAPIHistoryRef.current);

      // Finalize streaming placeholder
      setChatDisplayMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.streaming) return [...prev.slice(0, -1), { role: 'assistant', content: assistantText, streaming: false }];
        return prev;
      });

      if (toolUseBlocks.length > 0) {
        // Record assistant turn with tool_use in API history
        const assistantContent = [];
        if (assistantText) assistantContent.push({ type: 'text', text: assistantText });
        toolUseBlocks.forEach(t => assistantContent.push({ type: 'tool_use', id: t.id, name: t.name, input: t.input }));
        chatAPIHistoryRef.current = [...chatAPIHistoryRef.current, { role: 'assistant', content: assistantContent }];

        // Execute tools; widgets are deferred so they appear after follow-up text
        const { results: toolResults, deferredWidgets } = await executeChatTools(toolUseBlocks);

        // Record tool results in API history
        chatAPIHistoryRef.current = [...chatAPIHistoryRef.current, {
          role: 'user',
          content: toolResults.map(r => ({ type: 'tool_result', tool_use_id: r.id, content: JSON.stringify(r.result) })),
        }];

        // generate_itinerary auto-switches the screen — skip the second LLM round-trip so
        // the user doesn't see a "your itinerary is ready!" message before the switch happens.
        const itineraryFired = toolUseBlocks.some(t => t.name === 'generate_itinerary');
        if (itineraryFired) {
          setChatDisplayMessages(prev => {
            const last = prev[prev.length - 1];
            return last?.streaming ? prev.slice(0, -1) : prev;
          });
          if (deferredWidgets.length > 0) setChatDisplayMessages(prev => [...prev, ...deferredWidgets]);
          chatAPIHistoryRef.current = [...chatAPIHistoryRef.current, { role: 'assistant', content: '...' }];
          return;
        }

        // Second call for Claude's follow-up after tools, then append widgets below
        setChatDisplayMessages(prev => [...prev, { role: 'assistant', content: '', streaming: true }]);
        const { text: followupText, toolUseBlocks: followupTools } = await callChatAPI(chatAPIHistoryRef.current);

        // Execute any tool calls in the follow-up (e.g. suggest_region_cities, suggest_attractions)
        let allDeferredWidgets = [...deferredWidgets];
        if (followupTools.length > 0) {
          const followupAssistantContent = [];
          if (followupText) followupAssistantContent.push({ type: 'text', text: followupText });
          followupTools.forEach(t => followupAssistantContent.push({ type: 'tool_use', id: t.id, name: t.name, input: t.input }));
          chatAPIHistoryRef.current = [...chatAPIHistoryRef.current, { role: 'assistant', content: followupAssistantContent }];
          const { results: followupResults, deferredWidgets: followupWidgets } = await executeChatTools(followupTools);
          allDeferredWidgets = [...allDeferredWidgets, ...followupWidgets];
          chatAPIHistoryRef.current = [...chatAPIHistoryRef.current, {
            role: 'user',
            content: followupResults.map(r => ({ type: 'tool_result', tool_use_id: r.id, content: JSON.stringify(r.result) })),
          }];
        }

        setChatDisplayMessages(prev => {
          const last = prev[prev.length - 1];
          if (!last?.streaming) return prev;
          const withoutPlaceholder = prev.slice(0, -1);
          const withText = followupText
            ? [...withoutPlaceholder, { role: 'assistant', content: followupText, streaming: false }]
            : withoutPlaceholder;
          return allDeferredWidgets.length > 0 ? [...withText, ...allDeferredWidgets] : withText;
        });
        // Ensure history always ends with an assistant message.
        // Without this guard, consecutive user messages cause a 400 on the next turn:
        // user:[tool_results] → user:[next message] is invalid for the Anthropic API.
        const histLast = chatAPIHistoryRef.current[chatAPIHistoryRef.current.length - 1];
        if (histLast?.role !== 'assistant') {
          chatAPIHistoryRef.current = [...chatAPIHistoryRef.current,
            { role: 'assistant', content: followupText || (allDeferredWidgets.length > 0 ? '(widgets shown)' : '...') },
          ];
        }
      } else {
        // assistantText may be empty if LLM returned no text and no tools (rare degenerate case).
        // Anthropic rejects empty string content, so use a placeholder to keep history valid.
        chatAPIHistoryRef.current = [...chatAPIHistoryRef.current, { role: 'assistant', content: assistantText || '...' }];
      }
    } catch {
      setChatDisplayMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.streaming) return [...prev.slice(0, -1), { role: 'assistant', content: t('chat_error'), streaming: false }];
        return prev;
      });
    } finally {
      setChatLoading(false);
    }
  };

  const enterChatMode = () => {
    track('chat_mode_entered', { city_count: cities.length });
    setChatMode(true);
    chatAPIHistoryRef.current = [];
    const cityNames = cities.map(c => c.name.split(',')[0]).join(' and ');
    const greeting = cities.length > 0
      ? `You've already got ${cityNames} in the plan. What would you like to add next — more cities, or specific places to visit?`
      : "Where would you like to go? Name a destination, a region, or describe a vibe — I'll help you build the trip.";
    setChatDisplayMessages([{ role: 'assistant', content: greeting, streaming: false }]);
    // Seed history so Claude has the greeting as context
    chatAPIHistoryRef.current = [
      { role: 'user', content: 'Hi' },
      { role: 'assistant', content: greeting },
    ];
  };

  // ── Itinerary editing ─────────────────────────────────────────────────────
  const toggleDay = (dayIndex) => {
    const day = itinerary.days[dayIndex];
    track(day?.expanded ? 'day_collapsed' : 'day_expanded', { day_number: day?.day });
    setItinerary({
      ...itinerary,
      days: itinerary.days.map((d, idx) =>
        idx === dayIndex ? { ...d, expanded: !d.expanded } : d
      )
    });
  };

  const removeActivity = (dayIndex, activityIndex) => {
    const act = itinerary.days[dayIndex]?.activities[activityIndex];
    track('activity_removed', { activity_name: act?.name, source: 'itinerary' });
    setItinerary(prev => ({
      ...prev,
      days: prev.days.map((day, idx) => {
        if (idx !== dayIndex) return day;
        const remaining = day.activities.filter((_, i) => i !== activityIndex);
        return { ...day, activities: recalculateTimes(remaining) };
      })
    }));
    // Also remove from cities so it doesn't resurface in the unscheduled pool
    if (act?.name) {
      const nameLower = act.name.toLowerCase();
      setCities(prev => prev.map(city => ({
        ...city,
        attractions: city.attractions.filter(a =>
          (typeof a === 'string' ? a : a.name).toLowerCase() !== nameLower
        )
      })));
    }
  };

  const moveActivityToDay = (fromDayIdx, actIdx, toDayIdx) => {
    setItinerary(prev => {
      const days = prev.days.map(d => ({ ...d, activities: [...d.activities] }));
      const [activity] = days[fromDayIdx].activities.splice(actIdx, 1);
      // Clear pinnedTime when moving to a different day — the time was day-specific
      days[toDayIdx].activities.push({ ...activity, pinnedTime: false });
      days[fromDayIdx].activities = recalculateTimes(days[fromDayIdx].activities);
      days[toDayIdx].activities = recalculateTimes(days[toDayIdx].activities);
      return { ...prev, days };
    });
    setHasUnsavedChanges(true);
  };

  const addUnscheduledToDay = (attraction, toDayIdx) => {
    setItinerary(prev => {
      const days = prev.days.map(d => ({ ...d, activities: [...d.activities] }));
      const activity = {
        id: `activity-${Date.now()}`,
        name: attraction.name,
        duration: attraction.duration || '2-3 hours',
        types: attraction.types || [],
        placeId: attraction.placeId,
        fullName: attraction.fullName,
        photoRefs: attraction.photoRefs || [],
        cityName: attraction.cityName,
        time: '9:00 AM',
      };
      days[toDayIdx].activities = recalculateTimes([...days[toDayIdx].activities, activity]);
      return { ...prev, days };
    });
    setHasUnsavedChanges(true);
  };

  // Delete an entire day from the itinerary
  const removeDay = (dayIndex) => {
    track('day_removed', { day_number: itinerary.days[dayIndex]?.day });
    const updatedDays = itinerary.days.filter((_, idx) => idx !== dayIndex);
    // Renumber remaining days
    const renumbered = updatedDays.map((day, idx) => ({
      ...day,
      day: idx + 1
    }));
    setItinerary({
      ...itinerary,
      days: renumbered
    });
  };

  // Open a formatted print/copy view in a new tab
  const openPrintView = () => {
    track('itinerary_printed', { itinerary_name: itinerary?.name });
    const win = window.open('', '_blank');
    if (!win) return;
    const activityIcon = (type) => type === 'meal' ? '🍽️' : type === 'hotel' ? '🏨' : type === 'transportation' ? '🚗' : '📍';

    // Plain text version for Copy button
    const plainText = [
      itinerary.name,
      `${itinerary.days.length} days${itinerary.startDate ? ` · starting ${formatDayDate(0)}` : ''}`,
      '',
      ...itinerary.days.flatMap((day, i) => {
        const dateStr = formatDayDate(i);
        const header = `Day ${day.day}${day.city ? ` — ${day.city.split(',')[0]}` : ''}${dateStr ? ` (${dateStr})` : ''}`;
        const lines = day.activities.map(a => {
          const icon = activityIcon(a.type);
          const isHotel = a.type === 'hotel';
          const name = a.type === 'meal' ? (a.restaurant || a.name) : a.name;
          const dur = !isHotel && a.duration ? ` (~${a.duration})` : '';
          const time = !isHotel && a.time ? `${a.time}  ` : '         ';
          const sub = [a.fullName && a.fullName !== a.name ? a.fullName : null, a.address || null, a.note || null].filter(Boolean).join(' · ');
          return [`${time}${icon} ${name}${dur}`, sub ? `           ${sub}` : null].filter(Boolean).join('\n');
        });
        return [header, ...lines, day.note ? `📝 ${day.note}` : null, ''].filter(s => s !== null);
      }),
    ].join('\n');

    const daysHtml = itinerary.days.map((day, i) => {
      const dateStr = formatDayDate(i);
      const activitiesHtml = day.activities.map(a => {
        const icon = activityIcon(a.type);
        const isHotel = a.type === 'hotel';
        const isMeal = a.type === 'meal';
        const durationStr = isHotel || !a.duration ? '' : `<span class="dur">~${a.duration}</span>`;
        const name = isMeal ? (a.restaurant || a.name) : a.name;
        const sub = [
          a.fullName && a.fullName !== a.name ? a.fullName : null,
          a.address || null,
          a.note || null,
        ].filter(Boolean).join(' · ');
        return `<tr>
          <td class="time">${isHotel ? '' : (a.time || '')}</td>
          <td class="icon">${icon}</td>
          <td class="detail">
            <span class="name">${name}</span>${durationStr}
            ${sub ? `<div class="sub">${sub}</div>` : ''}
          </td>
        </tr>`;
      }).join('');
      return `<div class="day">
        <h2>Day ${day.day}${day.city ? ` — ${day.city.split(',')[0]}` : ''}${dateStr ? ` <span class="date">${dateStr}</span>` : ''}</h2>
        <table>${activitiesHtml}</table>
        ${day.note ? `<div class="note">📝 ${day.note}</div>` : ''}
      </div>`;
    }).join('');

    const plainTextEscaped = plainText.replace(/`/g, '\\`');

    win.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8">
      <title>${itinerary.name}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: Georgia, serif; font-size: 14px; color: #111; padding: 40px; max-width: 720px; margin: 0 auto; }
        h1 { font-size: 24px; margin-bottom: 4px; }
        .meta { color: #666; font-size: 12px; margin-bottom: 16px; }
        .toolbar { display: flex; gap: 8px; margin-bottom: 28px; }
        .btn { padding: 6px 14px; border-radius: 6px; border: 1px solid #ccc; background: #f5f5f5; font-size: 13px; cursor: pointer; font-family: sans-serif; }
        .btn:hover { background: #e8e8e8; }
        .btn.copied { background: #d4edda; border-color: #a8d5b5; color: #276749; }
        .day { margin-bottom: 32px; break-inside: avoid; }
        h2 { font-size: 16px; font-weight: bold; border-bottom: 1px solid #ccc; padding-bottom: 6px; margin-bottom: 10px; }
        table { width: 100%; border-collapse: collapse; }
        td { vertical-align: top; padding: 5px 6px; }
        td.time { width: 72px; color: #555; font-size: 13px; white-space: nowrap; padding-top: 6px; }
        td.icon { width: 24px; font-size: 14px; padding-top: 5px; }
        .name { font-weight: 600; }
        .dur { font-size: 12px; color: #777; margin-left: 8px; }
        .sub { font-size: 12px; color: #666; margin-top: 2px; }
        .note { margin-top: 10px; font-size: 13px; color: #555; font-style: italic; white-space: pre-wrap; }
        .date { font-weight: normal; font-size: 13px; color: #666; margin-left: 6px; }
        @media print {
          body { padding: 20px; }
          .toolbar { display: none; }
          .day { break-inside: avoid; }
        }
      </style>
    </head><body>
      <h1>${itinerary.name}</h1>
      <div class="meta">${itinerary.days.length} days${itinerary.startDate ? ` · starting ${formatDayDate(0)}` : ''} · printed ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
      <div class="toolbar">
        <button class="btn" onclick="window.print()">🖨️ Print</button>
        <button class="btn" id="copyBtn" onclick="
          navigator.clipboard.writeText(\`${plainTextEscaped}\`).then(() => {
            const b = document.getElementById('copyBtn');
            b.textContent = '${t('copied_check')}';
            b.className = 'btn copied';
            setTimeout(() => { b.textContent = '${t('copy_text')}'; b.className = 'btn'; }, 2000);
          });
        ">${t('copy_text')}</button>
      </div>
      ${daysHtml}
    </body></html>`);
    win.document.close();
  };

  // Add a new empty day to the itinerary
  const addDay = () => {
    track('day_added', { day_number: itinerary.days.length + 1 });
    const newDay = {
      id: `day-${itinerary.days.length + 1}`,
      day: itinerary.days.length + 1,
      city: '', // No city assigned yet
      activities: [],
      expanded: true
    };
    setItinerary({
      ...itinerary,
      days: [...itinerary.days, newDay]
    });
  };


  const clearEditState = () => {
    setEditTime(''); setEditDuration('');
    setEditAddress(''); setEditActivityNote(''); setEditNameQuery(''); setEditNameResults([]);
    setEditHotelNights(1);
  };

  const startEditingActivity = (dayIndex, activityIndex) => {
    const activity = itinerary.days[dayIndex].activities[activityIndex];
    setEditingActivity({ dayIndex, activityIndex });
    setEditTime(activity.time || '');
    setEditDuration(activity.duration || '');
    setEditAddress(activity.address || '');
    setEditActivityNote(activity.note || '');
    setEditNameQuery(activity.type === 'hotel' ? (activity.name || '') : (activity.restaurant || ''));
    setEditNameResults([]);
    setEditHotelNights(activity.nights || 1);
  };

  const searchEditName = (query, type) => {
    clearTimeout(editSearchTimerRef.current);
    if (query.length < 2) { setEditNameResults([]); return; }
    editSearchTimerRef.current = setTimeout(async () => {
      setEditNameLoading(true);
      try {
        const suffix = type === 'hotel' ? ' hotel' : ' restaurant';
        const res = await fetch(`/api/search-attractions?query=${encodeURIComponent(query + suffix)}&language=${encodeURIComponent(lang)}`);
        const { results } = await res.json();
        setEditNameResults(results || []);
      } catch { setEditNameResults([]); }
      finally { setEditNameLoading(false); }
    }, 350);
  };

  // Save edits to a meal, hotel, or attraction activity.
  // If the user changed the time, set pinnedTime:true so recalculateTimes treats it
  // as an anchor and never moves it. Recalculate the whole day when time or duration changed.
  const saveActivityEdit = () => {
    if (!editingActivity) return;
    const { dayIndex, activityIndex } = editingActivity;
    const nights = Math.max(1, editHotelNights || 1);
    setItinerary(prev => {
      const origActivity = prev.days[dayIndex].activities[activityIndex];
      const timeChanged = origActivity.time !== editTime;
      const durationChanged = origActivity.duration !== editDuration;

      const updatedDays = prev.days.map((day, idx) => {
        if (idx !== dayIndex) return day;
        const updatedActivities = day.activities.map((activity, aIdx) => {
          if (aIdx !== activityIndex) return activity;
          if (activity.type === 'hotel') {
            return { ...activity, name: editNameQuery.trim() || activity.name, address: editAddress, note: editActivityNote, nights };
          }
          if (activity.type === 'meal') {
            return { ...activity, restaurant: editNameQuery.trim(), address: editAddress, note: editActivityNote, time: editTime, duration: editDuration, ...(timeChanged && { pinnedTime: true }) };
          }
          return { ...activity, time: editTime, duration: editDuration, ...(timeChanged && { pinnedTime: true }) };
        });
        if (timeChanged || durationChanged) {
          return { ...day, activities: recalculateTimes(updatedActivities) };
        }
        return { ...day, activities: updatedActivities };
      });
      // Propagate hotel to subsequent days when nights > 1
      const editedActivity = prev.days[dayIndex].activities[activityIndex];
      if (editedActivity?.type === 'hotel' && nights > 1) {
        const hotelBase = { name: editNameQuery.trim() || editedActivity.name, address: editAddress, note: editActivityNote, nights, type: 'hotel' };
        for (let n = 1; n < nights; n++) {
          const tIdx = dayIndex + n;
          if (tIdx >= updatedDays.length) break;
          const existingHotelIdx = updatedDays[tIdx].activities.findIndex(a => a.type === 'hotel');
          const hotelEntry = { ...hotelBase, id: `hotel-${tIdx}-${Date.now()}-${n}` };
          if (existingHotelIdx >= 0) {
            updatedDays[tIdx] = { ...updatedDays[tIdx], activities: updatedDays[tIdx].activities.map((a, i) => i === existingHotelIdx ? hotelEntry : a) };
          } else {
            updatedDays[tIdx] = { ...updatedDays[tIdx], activities: [...updatedDays[tIdx].activities, hotelEntry] };
          }
        }
      }
      return { ...prev, days: updatedDays };
    });
    setHasUnsavedChanges(true);
    setPendingNewActivity(null);
    setEditingActivity(null);
    clearEditState();
  };

  // Cancel the edit dialog. If the activity was just added (pendingNewActivity is set),
  // remove it from the day — the user decided not to create it.
  const cancelEdit = () => {
    if (pendingNewActivity) {
      const { dayIndex, activityIndex } = pendingNewActivity;
      setItinerary(prev => ({
        ...prev,
        days: prev.days.map((d, i) => i !== dayIndex ? d : {
          ...d, activities: d.activities.filter((_, idx) => idx !== activityIndex)
        })
      }));
      setPendingNewActivity(null);
    }
    setEditingActivity(null);
    clearEditState();
  };

  const lastTimedActivity = (dayIndex) => {
    const acts = itinerary.days[dayIndex].activities;
    for (let i = acts.length - 1; i >= 0; i--) {
      if (acts[i].time && acts[i].type !== 'hotel') return acts[i];
    }
    return null;
  };

  const addTransportation = (dayIndex) => {
    const prev = lastTimedActivity(dayIndex);
    const startTime = prev ? calculateNextTime(prev.time, prev.duration) : '9:00 AM';
    
    const transportationBlock = {
      id: `transport-${dayIndex}-${Date.now()}`,
      name: t('transportation'),
      time: startTime,
      duration: '30 minutes',
      type: 'transportation'
    };

    setItinerary({
      ...itinerary,
      days: itinerary.days.map((d, idx) =>
        idx === dayIndex
          ? { ...d, activities: [...d.activities, transportationBlock] }
          : d
      )
    });
  };

  const addMeal = (dayIndex) => {
    cancelEdit();
    const prev = lastTimedActivity(dayIndex);
    const startTime = prev ? calculateNextTime(prev.time, prev.duration) : '12:00 PM';
    const mealBlock = {
      id: `meal-${dayIndex}-${Date.now()}`,
      name: t('meal'), restaurant: '', address: '', note: '',
      time: startTime, duration: '1 hour', type: 'meal'
    };
    setItinerary(cur => {
      const newIdx = cur.days[dayIndex].activities.length;
      const updated = { ...cur, days: cur.days.map((d, idx) => idx === dayIndex ? { ...d, activities: [...d.activities, mealBlock] } : d) };
      setEditingActivity({ dayIndex, activityIndex: newIdx });
      setPendingNewActivity({ dayIndex, activityIndex: newIdx });
      setEditTime(startTime); setEditDuration('1 hour');
      return updated;
    });
  };

  const addHotel = (dayIndex) => {
    cancelEdit();
    const hotelBlock = {
      id: `hotel-${dayIndex}-${Date.now()}`,
      name: '', address: '', note: '', type: 'hotel'
    };
    setItinerary(cur => {
      const newIdx = cur.days[dayIndex].activities.length;
      const updated = { ...cur, days: cur.days.map((d, idx) => idx === dayIndex ? { ...d, activities: [...d.activities, hotelBlock] } : d) };
      setEditingActivity({ dayIndex, activityIndex: newIdx });
      setPendingNewActivity({ dayIndex, activityIndex: newIdx });
      setEditHotelNights(1);
      return updated;
    });
  };

  // ── JSX ───────────────────────────────────────────────────────────────────
  const carouselCities = useMemo(() => {
    const hard = [...HARDCODED_DESTINATIONS];
    const user = [...popularCities];
    const combined = [];
    let h = 0, u = 0, pos = 0;
    while (h < hard.length || u < user.length) {
      // Every 4th slot (positions 3, 7, 11, …) gets a user city
      if (pos > 0 && pos % 4 === 3 && u < user.length) {
        combined.push(user[u++]);
      } else if (h < hard.length) {
        combined.push(hard[h++]);
      } else {
        combined.push(user[u++]);
      }
      pos++;
    }
    // Shuffle within windows of 3 so the every-4th pattern isn't obvious
    for (let i = combined.length - 1; i > 1; i--) {
      const j = Math.max(0, i - Math.floor(Math.random() * 3));
      [combined[i], combined[j]] = [combined[j], combined[i]];
    }
    return combined;
  }, [popularCities]);

  // Cities from carouselCities that have finished loading an image — used for hero carousel
  const heroCarouselDests = useMemo(() =>
    carouselCities.filter(d => destImages[d.name] && destImages[d.name] !== 'loading').slice(0, 8),
    [carouselCities, destImages]
  );

  return (
    <ErrorBoundary>
    <div ref={scrollContainerRef} className="max-w-md sm:max-w-lg md:max-w-2xl lg:max-w-3xl xl:max-w-4xl mx-auto bg-orange-50" style={{ height: '100dvh', overflowY: 'auto' }}>
      {/* ── Header + nav tabs ── */}
      <div className="bg-white px-4 pt-4 pb-2 shadow-sm">
        <div className="flex items-center justify-between">
          <img src="/sniptrip-logo.png" alt="Sniptrip" className="h-12 w-auto" />
          <div className="flex items-center gap-1">
            <button
              onClick={enterPhotoMode}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              title={t('explore_photos')}
            >
              <Images className="w-6 h-6 text-gray-600" />
            </button>
            <button
              onClick={() => {
                if (user) setShowUserMenu(!showUserMenu);
                else { setAuthMode('login'); setShowAuthModal(true); track('auth_modal_opened'); }
              }}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              title={user ? user.email : t('sign_in')}
            >
              <User className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex items-center gap-1 mt-2 pb-2">
          <button
            onClick={() => {
              if (itinerary) {
                setShowNewTripTabConfirm(true);
              } else {
                setScreen('input');
                trackPageView('Plan', '/plan');
              }
            }}
            className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors ${
              screen === 'input' ? 'chip-active' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('new_trip').replace(/^\+\s*/, '')}…
          </button>
          <button
            onClick={() => { if (itinerary) { setScreen('itinerary'); trackPageView('Itinerary', '/itinerary'); } }}
            className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors ${
              screen === 'itinerary' ? 'chip-active' : 'text-gray-500 hover:text-gray-700'
            }`}
            disabled={!itinerary}
          >
            {t('nav_itinerary')}
          </button>
          <button
            onClick={() => { setScreen('saved'); trackPageView('Saved', '/saved'); }}
            className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors ${
              screen === 'saved' ? 'chip-active' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('nav_saved')}
          </button>
        </div>
      </div>

      {/* ── Plan Screen ── */}
      {screen === 'input' && (chatMode ? (
        /* ── Chat mode UI ── */
        <div className="flex flex-col" style={{ minHeight: 'calc(100dvh - 116px)' }}>
          {/* Back bar + trip summary */}
          <div className="bg-white border-b border-gray-100">
            <div className="px-4 pt-3 pb-2 flex items-center justify-between">
              <button onClick={() => setChatMode(false)} className="text-xs text-gray-500 hover:text-gray-700 transition-colors">
                {t('chat_back')}
              </button>
              {cities.length === 0 ? (
                <span className="text-xs text-gray-400">{t('chat_no_cities')}</span>
              ) : (
                <button
                  onClick={() => setShowChatPlanSummary(v => !v)}
                  className="text-xs text-orange-500 font-medium flex items-center gap-1"
                >
                  {`${t('chat_cities_count', cities.length)} · ${t('chat_places_count', cities.reduce((n, c) => n + c.attractions.filter(a => !a.removed).length, 0))}`}
                  <span className={`transition-transform ${showChatPlanSummary ? 'rotate-180' : ''}`}>▾</span>
                </button>
              )}
            </div>
            {showChatPlanSummary && cities.length > 0 && (
              <div className="px-4 pb-3 space-y-2 max-h-48 overflow-y-auto">
                {cities.map((city, ci) => {
                  const active = city.attractions.filter(a => !a.removed);
                  return (
                    <div key={ci}>
                      <div className="text-xs font-semibold text-gray-700">{city.name}</div>
                      {active.length === 0 ? (
                        <div className="text-xs text-gray-400 ml-2">No places yet</div>
                      ) : (
                        <ul className="ml-2 space-y-0.5">
                          {active.map((a, ai) => (
                            <li key={ai} className="text-xs text-gray-500">· {a.name}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Transcript */}
          <div ref={chatScrollRef} className="flex-1 px-4 py-4 space-y-3 pb-24">
            {chatDisplayMessages.map((msg, idx) => (
              <div key={idx}>
                {msg.content && (
                  <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-orange-500 text-white rounded-br-sm'
                        : 'bg-white text-gray-800 shadow-sm rounded-bl-sm'
                    }`}>
                      {msg.content}
                      {msg.streaming && <span className="inline-block w-1.5 h-3.5 ml-0.5 bg-gray-400 rounded animate-pulse align-middle" />}
                    </div>
                  </div>
                )}
                {msg.widget && (
                  <div className="mt-2">
                    {/* Header label for every widget so users know what they're looking at */}
                    {msg.widget.type === 'suggest_attractions' && msg.widget.city && (
                      <div className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-1 px-1">
                        {msg.widget.city.split(',')[0]}
                      </div>
                    )}
                    {msg.widget.type === 'suggest_region_cities' && msg.widget.region && (
                      <div className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-1 px-1">
                        {msg.widget.region}
                      </div>
                    )}
                    {msg.widget.type === 'suggest_region_cities' && (() => {
                      const addedList = chatWidgetStates[msg.widget.id]?.added || [];
                      const addedNames = new Set(addedList.map(n => n.toLowerCase()));
                      const submitted = chatWidgetStates[msg.widget.id]?.submitted;
                      return (
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-wrap gap-2">
                            {(msg.widget.cities || []).map((city) => {
                              const isAdded = addedNames.has(city.name.toLowerCase());
                              return (
                                <button
                                  key={city.name}
                                  onClick={async () => {
                                    if (isAdded) return;
                                    // Bypass geocoding — LLM already provided the name; doAddCity handles dedup
                                    await doAddCity({ name: city.name, placeId: null, location: null, types: [] }, 'chat');
                                    setChatWidgetStates(prev => ({
                                      ...prev,
                                      [msg.widget.id]: {
                                        ...prev[msg.widget.id],
                                        added: [...(prev[msg.widget.id]?.added || []), city.name],
                                      },
                                    }));
                                  }}
                                  className={`flex flex-col items-start text-left px-3 py-2 rounded-xl border text-sm transition-colors ${
                                    isAdded
                                      ? 'bg-green-50 border-green-400 text-green-700'
                                      : 'bg-white border-gray-200 text-gray-800 active:bg-orange-50 active:border-orange-300'
                                  }`}
                                >
                                  <span className="font-semibold flex items-center gap-1">
                                    {city.name.split(',')[0]}
                                    {isAdded && <span className="text-green-500">✓</span>}
                                  </span>
                                  {(city.reason || city.distance) && (
                                    <span className="text-xs text-gray-500 mt-0.5">
                                      {[city.reason, city.distance].filter(Boolean).join(' · ')}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                          {addedList.length > 0 && !submitted && (
                            <button
                              onClick={() => {
                                const labels = addedList.map(n => n.split(',')[0]).join(', ');
                                setChatWidgetStates(prev => ({ ...prev, [msg.widget.id]: { ...prev[msg.widget.id], submitted: true } }));
                                sendChatMessage(`Added ${labels}`);
                              }}
                              className="self-start mt-1 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-semibold active:bg-orange-600"
                            >
                              Continue →
                            </button>
                          )}
                        </div>
                      );
                    })()}
                    {msg.widget.type === 'suggest_cities' && (() => {
                      const addedList = chatWidgetStates[msg.widget.id]?.chatAdded || [];
                      const submitted = chatWidgetStates[msg.widget.id]?.submitted;
                      return (
                        <>
                          <CityDiscoveryPanel
                            disc={chatWidgetStates[msg.widget.id] || IDLE_CITY}
                            setDisc={patch => setChatWidgetStates(prev => ({ ...prev, [msg.widget.id]: { ...(prev[msg.widget.id] || IDLE_CITY), ...patch } }))}
                            context={{ cities: cities.map(c => c.name), attractions: cities.flatMap(c => c.attractions.filter(a => !a.removed).map(a => a.name)), filters: attractionFilters }}
                            existingCityNames={new Set(cities.map(c => c.name.toLowerCase()))}
                            onAdd={(name) => {
                              addCity({ name, placeId: null, location: null }, 'chat');
                              const cityShort = name.split(',')[0];
                              setChatDisplayMessages(prev => [...prev, { role: 'assistant', content: `Added ${cityShort} to your trip ✓`, streaming: false }]);
                              setChatWidgetStates(prev => ({ ...prev, [msg.widget.id]: { ...prev[msg.widget.id], chatAdded: [...(prev[msg.widget.id]?.chatAdded || []), cityShort] } }));
                            }}
                            onDismiss={() => setChatWidgetStates(prev => ({ ...prev, [msg.widget.id]: IDLE_CITY }))}
                            t={t} lang={lang} autoOpen={true}
                          />
                          {addedList.length > 0 && !submitted && (
                            <button
                              onClick={() => {
                                const labels = addedList.join(', ');
                                setChatWidgetStates(prev => ({ ...prev, [msg.widget.id]: { ...prev[msg.widget.id], submitted: true } }));
                                sendChatMessage(`Added ${labels}`);
                              }}
                              className="self-start mt-1 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-semibold active:bg-orange-600"
                            >
                              Continue →
                            </button>
                          )}
                        </>
                      );
                    })()}
                    {msg.widget.type === 'suggest_attractions' && (() => {
                      const widgetCity = cities.find(c => c.name.toLowerCase().split(',')[0].trim().includes((msg.widget.city || '').toLowerCase().split(',')[0].trim()));
                      const existingAttrNames = new Set((widgetCity?.attractions || []).filter(a => !a.removed).map(a => a.name.toLowerCase()));
                      // Collect all suggestions ever shown in any widget for this city so the API excludes repeats
                      const allShownForCity = new Set(Array.from(existingAttrNames));
                      chatDisplayMessages.forEach(m => {
                        if (m.widget?.type === 'suggest_attractions' && m.widget.city === msg.widget.city) {
                          (chatWidgetStates[m.widget.id]?.suggestions || []).forEach(s => allShownForCity.add(s.name.toLowerCase()));
                        }
                      });
                      const attrAddedList = chatWidgetStates[msg.widget.id]?.chatAdded || [];
                      const attrSubmitted = chatWidgetStates[msg.widget.id]?.submitted;
                      return (
                        <>
                          <AttrDiscoveryPanel
                            disc={chatWidgetStates[msg.widget.id] || IDLE_ATTR}
                            setDisc={patch => setChatWidgetStates(prev => ({ ...prev, [msg.widget.id]: { ...(prev[msg.widget.id] || IDLE_ATTR), ...patch } }))}
                            context={{ cities: [msg.widget.city], attractions: Array.from(allShownForCity), filters: attractionFilters }}
                            primaryCity={msg.widget.city}
                            filters={attractionFilters}
                            existingAttrNames={existingAttrNames}
                            onAdd={(sug) => {
                              chatAddAttraction(msg.widget.city, sug);
                              const attrName = typeof sug === 'string' ? sug : sug?.name || '';
                              if (attrName) {
                                setChatDisplayMessages(prev => [...prev, { role: 'assistant', content: `Added ${attrName} to your plan ✓`, streaming: false }]);
                                setChatWidgetStates(prev => ({ ...prev, [msg.widget.id]: { ...(prev[msg.widget.id] || IDLE_ATTR), chatAdded: [...(prev[msg.widget.id]?.chatAdded || []), attrName] } }));
                              }
                            }}
                            onDismiss={() => setChatWidgetStates(prev => ({ ...prev, [msg.widget.id]: IDLE_ATTR }))}
                            count={msg.widget.count}
                            initialQuestion={msg.widget.context || null}
                            t={t} lang={lang} autoOpen={true}
                          />
                          {attrAddedList.length > 0 && !attrSubmitted && (
                            <button
                              onClick={() => {
                                const city = (msg.widget.city || '').split(',')[0];
                                setChatWidgetStates(prev => ({ ...prev, [msg.widget.id]: { ...prev[msg.widget.id], submitted: true } }));
                                sendChatMessage(`Done adding places in ${city}`);
                              }}
                              className="self-start mt-1 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-semibold active:bg-orange-600"
                            >
                              Continue →
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            ))}
            {chatLoading && chatDisplayMessages.every(m => !m.streaming) && (
              <div className="flex justify-start">
                <div className="bg-white px-4 py-3 rounded-2xl shadow-sm rounded-bl-sm">
                  <div className="flex gap-1 items-center">
                    {[0, 150, 300].map(delay => (
                      <span key={delay} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${delay}ms` }} />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sticky composer */}
          <div className="sticky bottom-0 z-10 px-4 py-3 bg-white border-t border-gray-100">
            <div className="flex gap-2 items-center">
              <input
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(chatInput); } }}
                placeholder={t('chat_input_placeholder')}
                className="flex-1 px-3 py-2.5 text-sm input-field"
                disabled={chatLoading}
              />
              <button
                onClick={() => sendChatMessage(chatInput)}
                disabled={!chatInput.trim() || chatLoading}
                className="btn-amber p-2.5 disabled:opacity-40"
              >
                <ArrowRight className="w-5 h-5" />
              </button>
              <button onClick={enterPhotoMode} className="p-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors">
                <Images className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="p-6 space-y-6">
          {cities.length === 0 ? (
            <>
              {/* Hero carousel — full-width, auto-rotating city photo */}
              {(() => {
                const safeIdx = heroCarouselDests.length > 0 ? heroCarouselIdx % heroCarouselDests.length : 0;
                const dest = heroCarouselDests[safeIdx] ?? null;
                const img = dest ? destImages[dest.name] : null;
                return (
                  <div className="relative -mx-6 -mt-6 overflow-hidden" style={{ height: '120px' }}>
                    {/* Whole image is the tap/swipe target */}
                    <button
                      onTouchStart={(e) => { heroTouchXRef.current = e.touches[0].clientX; }}
                      onTouchEnd={(e) => {
                        if (heroTouchXRef.current === null) return;
                        const dx = e.changedTouches[0].clientX - heroTouchXRef.current;
                        if (Math.abs(dx) > 40) {
                          setHeroCarouselIdx(prev => dx < 0 ? prev + 1 : prev - 1);
                          heroTouchXRef.current = 'swipe';
                        } else {
                          heroTouchXRef.current = null;
                        }
                      }}
                      onClick={() => {
                        if (heroTouchXRef.current === 'swipe') { heroTouchXRef.current = null; return; }
                        heroTouchXRef.current = null;
                        if (dest) handleDestinationTap(dest);
                      }}
                      className="absolute inset-0 w-full focus:outline-none"
                      disabled={!dest}
                    >
                      {img ? (
                        <img src={img} alt={dest?.name} className="absolute inset-0 w-full h-full object-cover" />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 animate-pulse" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                      {/* City name + country — bottom left */}
                      {dest && (
                        <div className="absolute bottom-7 left-4 text-left">
                          <p className="text-white font-bold text-xl leading-tight drop-shadow">{dest.key ? t(dest.key) : dest.name}</p>
                          {dest.country && <p className="text-white/75 text-xs mt-0.5">{dest.country}</p>}
                        </div>
                      )}
                      {/* Photos icon — top right, signals tapping opens photo exploration */}
                      {dest && (
                        <div className="absolute top-2.5 right-3 w-7 h-7 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center">
                          <Images className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </button>

                    {/* Dot indicators — bottom center */}
                    {heroCarouselDests.length > 1 && (
                      <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 pointer-events-none">
                        {heroCarouselDests.slice(0, 5).map((_, i) => (
                          <div key={i} className={`rounded-full transition-all duration-300 ${i === safeIdx ? 'w-2 h-2 bg-white' : 'w-1.5 h-1.5 bg-white/50'}`} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* "New trip" — only when returning to home with an existing itinerary */}
              {itinerary && (
                <div className="flex justify-end">
                  <button onClick={handleNewTrip} className="text-xs text-gray-400 hover:text-gray-600 underline">
                    {t('new_trip')}
                  </button>
                </div>
              )}

              {/* Badge + headline */}
              <div className="text-center px-2 pt-1">
                <div className="inline-flex items-center gap-1.5 border border-dashed border-blue-300 text-blue-500 text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-3">
                  <span>✈</span>
                  <span>{t('hero_badge')}</span>
                </div>
                <h1 className="text-3xl font-bold text-gray-900 leading-tight">
                  {t('hero_title_line1')}<br />
                  <span className="text-blue-500">{t('hero_title_line2')}</span>
                </h1>
              </div>

              {/* Search card — primary CTA */}
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <p className="text-gray-500 text-sm font-medium mb-3 flex items-center gap-1.5">
                  <span>📍</span>
                  <span>{t('where_going_label')}</span>
                </p>
                <div className="relative">
                  <div className="flex gap-2">
                    <input
                      ref={cityInputRef}
                      type="search"
                      value={currentCity}
                      onChange={(e) => { setCurrentCity(e.target.value); setShowCitySuggestions(true); }}
                      onKeyPress={(e) => { if (e.key === 'Enter' && !showCitySuggestions) addCity(); }}
                      onFocus={() => { setShowCitySuggestions(true); track('city_search_started'); }}
                      placeholder={t('city_placeholder')}
                      autoComplete="off" autoCorrect="off" autoCapitalize="off"
                      spellCheck="false" inputMode="text"
                      name="city-search-field" data-form-type="other"
                      className="flex-1 px-4 py-2.5 input-field"
                    />
                    <button onClick={() => addCity()} className="btn-amber p-2.5">
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  {showCitySuggestions && citySuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-y-auto" style={{ maxHeight: 'min(70vh, 500px)' }}>
                      {citySuggestions.map((city, idx) => (
                        <button
                          key={idx}
                          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); addCity(city); }}
                          className="w-full text-left px-3 py-2.5 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-800 text-sm">{city.name}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {expandingRegion && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-blue-600">
                      <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                      <span>{t('expanding_region', expandingRegion)}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="text-center">
                <button
                  onClick={enterChatMode}
                  className="text-xs text-blue-400 hover:text-blue-600 transition-colors"
                >
                  <MessageSquare className="w-3 h-3 inline mr-1 -mt-px" />
                  {t('chat_mode_link')}
                </button>
              </div>

              {attractionFilters.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowFiltersExpanded(v => !v)}
                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <span>{t('edit_interests')}</span>
                    {showFiltersExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                  {showFiltersExpanded && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {ATTRACTION_FILTERS.map(f => {
                        const active = f.label === 'All' ? attractionFilters.length === 0 : attractionFilters.includes(f.label);
                        return (
                          <button
                            key={f.label}
                            onClick={() => {
                              const next = f.label === 'All' ? [] : attractionFilters.includes(f.label) ? attractionFilters.filter(l => l !== f.label) : [...attractionFilters, f.label];
                              try { localStorage.setItem('sniptrip_filters', JSON.stringify(next)); } catch {}
                              setAttractionFilters(next);
                            }}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${active ? 'chip-active' : 'chip-inactive'}`}
                          >
                            {t('filterLabel', f.label)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex justify-end">
                <button onClick={handleNewTrip} className="text-xs text-gray-400 hover:text-gray-600 underline">
                  {t('new_trip')}
                </button>
              </div>

              {/* Add City Section — shown only when cities already exist */}
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  {t('add_city_header')}
                </h2>
                <div className="relative">
                  <div className="flex gap-2">
                    <input
                      ref={cityInputRef}
                      type="search"
                      value={currentCity}
                      onChange={(e) => { setCurrentCity(e.target.value); setShowCitySuggestions(true); }}
                      onKeyPress={(e) => { if (e.key === 'Enter' && !showCitySuggestions) addCity(); }}
                      onFocus={() => setShowCitySuggestions(true)}
                      placeholder={t('city_placeholder')}
                      autoComplete="off" autoCorrect="off" autoCapitalize="off"
                      spellCheck="false" inputMode="text"
                      name="city-search-field" data-form-type="other"
                      className="flex-1 px-4 py-2 input-field"
                    />
                    <button onClick={() => addCity()} className="btn-amber p-2">
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  {showCitySuggestions && citySuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-y-auto" style={{ maxHeight: 'min(70vh, 500px)' }}>
                      {citySuggestions.map((city, idx) => (
                        <button
                          key={idx}
                          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); addCity(city); }}
                          className="w-full text-left px-3 py-2.5 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-800 text-sm">{city.name}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {expandingRegion && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-blue-600">
                    <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                    <span>{t('expanding_region', expandingRegion)}</span>
                  </div>
                )}
              </div>
              {/* Global filter preferences */}
              <div>
                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={() => { setShowFiltersExpanded(v => !v); setPendingFilterRefresh(false); }}
                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <span>{t('edit_interests')}</span>
                    {showFiltersExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                  {pendingFilterRefresh && cities.some(c => c.attractions.some(a => !a.removed)) && (
                    <button
                      onClick={() => {
                        cities.forEach((_, i) => autoPopulateAttractions(i, null, true));
                        setPendingFilterRefresh(false);
                        setShowFiltersExpanded(false);
                      }}
                      className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      ↻ {t('refresh_attractions')}
                    </button>
                  )}
                </div>
                {showFiltersExpanded && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {ATTRACTION_FILTERS.map(f => {
                      const active = f.label === 'All' ? attractionFilters.length === 0 : attractionFilters.includes(f.label);
                      return (
                        <button
                          key={f.label}
                          onClick={() => {
                            const next = f.label === 'All' ? [] : attractionFilters.includes(f.label) ? attractionFilters.filter(l => l !== f.label) : [...attractionFilters, f.label];
                            try { localStorage.setItem('sniptrip_filters', JSON.stringify(next)); } catch {}
                            setAttractionFilters(next);
                            setPendingFilterRefresh(true);
                          }}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${active ? 'chip-active' : 'chip-inactive'}`}
                        >
                          {t('filterLabel', f.label)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>


              {/* ── Plan framing bar — CSS sticky inside scroll container ── */}
              {(() => {
                const totalActive = cities.reduce((acc, c) => acc + c.attractions.filter(a => !a.removed).length, 0);
                const isLoading = Object.values(autoAddLoading).some(Boolean);
                const summary = totalActive > 0
                  ? (cities.length === 1
                    ? t('attraction_count', totalActive)
                    : t('plan_multi_summary', cities.length, totalActive))
                  : null;
                return (
                  <div className="sticky top-0 z-30 bg-orange-50 -mx-6 px-6 pt-2 pb-3">
                    <div className="bg-white border border-gray-100 rounded-2xl shadow-md px-4 py-3 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-gray-800 text-base truncate">
                          {itinerary?.name || (cities.length === 1 ? `${cities[0].name.split(',')[0]} Trip` : `${cities[0].name.split(',')[0]} & More`)}
                        </span>
                        {isLoading
                          ? <span className="text-xs text-gray-400 flex-shrink-0">{t('finding_attractions')}</span>
                          : summary && <span className="text-sm text-blue-600 font-medium flex-shrink-0">{summary}</span>
                        }
                      </div>
                      <p className="text-xs text-gray-400 leading-snug">{t('plan_framing_msg')}</p>
                      <button
                        onClick={() => { if (itinerary) setScreen('itinerary'); else generateItinerary(); }}
                        disabled={generatingItinerary || isLoading}
                        className="w-full btn-primary py-3 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {generatingItinerary ? (
                          <span className="flex items-center justify-center gap-2">
                            <span key={generatingMsgIdx} className="animate-fade-in-up">{t(GENERATING_MESSAGES[generatingMsgIdx])}</span>
                            <span className="flex gap-1 items-center">
                              <span className="generating-dot w-1.5 h-1.5 rounded-full bg-white" />
                              <span className="generating-dot w-1.5 h-1.5 rounded-full bg-white" />
                              <span className="generating-dot w-1.5 h-1.5 rounded-full bg-white" />
                            </span>
                          </span>
                        ) : itinerary ? t('view_itinerary') : `✨ ${t('generate_itinerary')}`}
                      </button>
                    </div>
                  </div>
                );
              })()}

              <div className="space-y-4">
          {cities.map((city, cityIdx) => (
                <React.Fragment key={cityIdx}>
                <div className="card overflow-visible">
                  {/* City Header — photo background if available, blue gradient fallback */}
                  {(() => {
                    const bannerRef = dayBannerPhotos[city.name];
                    const hasPhoto = bannerRef && bannerRef !== null;
                    return (
                      <div className="relative overflow-hidden rounded-t-2xl">
                        {hasPhoto ? (
                          <img
                            src={`/api/place-photo?ref=${encodeURIComponent(bannerRef)}&maxwidth=600`}
                            alt={city.name}
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                        ) : (
                          <div className="absolute inset-0 city-card-header" />
                        )}
                        {/* Scrim — stronger at bottom so text is always readable */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/25 to-black/10" />
                        {/* Content */}
                        <div className="relative p-4 flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-lg text-white">{city.name}</h3>
                            {(() => {
                              const count = city.attractions.filter(a => !a.removed).length;
                              if (count === 0 && autoAddLoading[cityIdx]) return <p className="text-sm text-white/60">{t('finding_attractions')}</p>;
                              if (count === 0) return null;
                              return <p className="text-sm text-white/75">{t('attraction_count', count)}</p>;
                            })()}
                          </div>
                          <div className="flex items-center gap-3">
                            <button onClick={() => snipItem('city', { name: city.name })} className="p-2 text-white/60 hover:text-white rounded-lg" title="Snip city">
                              <Bookmark className="w-5 h-5" />
                            </button>
                            <button onClick={() => removeCity(cityIdx)} className="p-2 text-white/60 hover:text-white rounded-lg">
                              <Trash2 className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => setCollapsedCities(prev => ({ ...prev, [cityIdx]: !prev[cityIdx] }))}
                              className="p-2 text-white/60 hover:text-white rounded-lg"
                            >
                              {collapsedCities[cityIdx] ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {!collapsedCities[cityIdx] && (<>
                  {/* City Description */}
                  {city.description && (
                    <div className="px-4 pt-3 pb-2 border-b border-gray-100">
                      <p className={`text-sm text-gray-600 ${expandedDescriptions[`city-${cityIdx}`] ? '' : 'line-clamp-3'}`}>
                        {city.description}
                      </p>
                      <button
                        onClick={() => setExpandedDescriptions(prev => ({ ...prev, [`city-${cityIdx}`]: !prev[`city-${cityIdx}`] }))}
                        className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 mt-1"
                      >
                        {expandedDescriptions[`city-${cityIdx}`]
                          ? <><ChevronUp className="w-3 h-3" /> {t('show_less')}</>
                          : <><ChevronDown className="w-3 h-3" /> {t('read_more')}</>}
                      </button>
                    </div>
                  )}

                  {/* Add Attraction Input */}
                  <div className="p-4 border-b border-gray-100">
                    <div
                      className="relative"
                      ref={(el) => {
                        if (el) attractionInputRefs.current[cityIdx] = el;
                      }}
                    >
                      <div className="flex gap-2">
                        <input
                          type="search"
                          value={selectedCityIndex === cityIdx ? currentAttraction : ''}
                          onChange={(e) => {
                            setSelectedCityIndex(cityIdx);
                            setCurrentAttraction(e.target.value);
                            setShowAttractionSuggestions(true);
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !showAttractionSuggestions) {
                              addAttraction(cityIdx);
                            }
                          }}
                          onClick={() => {
                            setSelectedCityIndex(cityIdx);
                            setShowAttractionSuggestions(true);
                            if (!currentAttraction || currentAttraction.length === 0) {
                              searchAttractions('', cityIdx, attractionFilters);
                            }
                          }}
                          onFocus={() => {
                            setSelectedCityIndex(cityIdx);
                            setShowAttractionSuggestions(true);
                            if (!currentAttraction || currentAttraction.length === 0) {
                              searchAttractions('', cityIdx, attractionFilters);
                            }
                          }}
                          placeholder={t('add_attraction_placeholder', city.name)}
                          autoComplete="off"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck="false"
                          inputMode="text"
                          name={`attraction-search-${cityIdx}`}
                          data-form-type="other"
                          className="flex-1 px-3 py-2 text-sm input-field"
                        />
                        <button
                          onClick={() => {
                            setSelectedCityIndex(cityIdx);
                            addAttraction(cityIdx);
                          }}
                          className={`px-3 py-2 rounded-lg transition-colors ${addedAttractionCity === cityIdx ? 'bg-green-500 text-white' : 'btn-primary'}`}
                        >
                          {addedAttractionCity === cityIdx ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                        </button>
                      </div>

                      {/* Attraction Autocomplete Dropdown */}
                      {selectedCityIndex === cityIdx && showAttractionSuggestions && attractionSuggestions.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-y-auto" style={{ maxHeight: 'min(70vh, 500px)' }}>
                          {attractionSuggestions.map((attraction, idx) => (
                            <button
                              key={idx}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('Attraction button mousedown:', attraction);
                                addAttraction(cityIdx, attraction);
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium text-gray-800 text-sm leading-tight">{attraction.name}</div>
                              <div className="text-xs text-gray-500 mt-0.5 leading-tight">
                                {attraction.fullName && attraction.fullName !== attraction.name && (
                                  <span>{attraction.fullName} • </span>
                                )}
                                <span className="text-blue-500">~{attraction.duration}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Attractions List */}
                  <div className="p-4">
                    {city.attractions.length === 0 ? (
                      autoAddLoading[cityIdx] ? (
                        <div className="flex items-center justify-center gap-2 py-3 text-sm text-blue-600">
                          <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                          <span key={autoAddMsgIdx} className="animate-fade-in-up">{t(AUTO_ADD_MESSAGES[autoAddMsgIdx])}</span>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400 text-center py-2">{t('no_attractions')}</p>
                      )
                    ) : (
                      <div className="space-y-2">
                        {city.attractions.map((attraction, attrIdx) => {
                          const attractionName = typeof attraction === 'string' ? attraction : attraction.name;
                          const photoRefs = (typeof attraction === 'object' && attraction.photoRefs) || [];
                          const photoKey = `${cityIdx}-${attrIdx}`;
                          const justAdded = lastAddedKey === photoKey;
                          const isRemoved = typeof attraction === 'object' && attraction.removed;

                          if (isRemoved) {
                            return (
                              <div key={attrIdx} className="flex items-center gap-2 py-1.5 px-2 rounded-lg bg-gray-50">
                                <span className="flex-1 text-sm text-gray-400 line-through">{attractionName}</span>
                                <button
                                  onClick={() => restoreAttraction(cityIdx, attrIdx)}
                                  className="text-xs text-blue-500 hover:text-blue-700 font-medium flex-shrink-0"
                                >
                                  {t('add_back')}
                                </button>
                              </div>
                            );
                          }

                          return (
                            <div
                              key={attrIdx}
                              className={`rounded-lg overflow-hidden border-2 transition-colors duration-500 ${justAdded ? 'border-green-400 bg-green-50' : 'border-blue-300 bg-blue-50'}`}
                            >
                              <AttractionCard
                                name={attractionName}
                                cityName={city.name}
                                placeId={typeof attraction === 'object' ? attraction.placeId : null}
                                rating={typeof attraction === 'object' ? (attraction.rating ?? null) : null}
                                ratingCount={typeof attraction === 'object' ? (attraction.ratingCount ?? null) : null}
                                description={typeof attraction === 'object' ? (attraction.description || null) : null}
                                photoRefs={photoRefs}
                                photoKey={photoKey}
                                source="plan"
                                photoIndices={photoIndices} setPhotoIndices={setPhotoIndices}
                                fetchingPhotos={fetchingPhotos} setFetchingPhotos={setFetchingPhotos}
                                detailsFetched={detailsFetched} setDetailsFetched={setDetailsFetched}
                                expandedDescriptions={expandedDescriptions} setExpandedDescriptions={setExpandedDescriptions}
                                onMorePhotoRefs={typeof attraction === 'object' && attraction.placeId
                                  ? newRefs => setCities(prev => prev.map((c, ci) => ci !== cityIdx ? c : {
                                      ...c, attractions: c.attractions.map((a, ai) => ai !== attrIdx ? a : { ...a, photoRefs: newRefs })
                                    }))
                                  : null}
                                t={t}
                                header={
                                  <div className="px-3 py-2.5">
                                    {/* Row 1: name + action icon — name can use full width */}
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                      <span className={`text-sm font-medium leading-snug ${justAdded ? 'text-green-700' : 'text-gray-800'}`}>
                                        {attractionName}
                                      </span>
                                      {justAdded
                                        ? <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                        : <button
                                            onClick={() => removeAttraction(cityIdx, attrIdx)}
                                            className="w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors flex-shrink-0 -mt-0.5"
                                          >
                                            <X className="w-4 h-4" />
                                          </button>
                                      }
                                    </div>
                                    {/* Row 2: Added badge only */}
                                    {!justAdded && (
                                      <div className="flex items-center gap-2">
                                        <span className="inline-flex items-center gap-1 bg-blue-500 text-white text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0">
                                          <Check className="w-2.5 h-2.5" /> {t('added_label')}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                }
                                mediaRowEnd={!justAdded && (
                                  <button
                                    onClick={() => snipItem('attraction', { name: attractionName, cityName: city.name, types: typeof attraction === 'object' ? attraction.types : [], rating: attraction.rating ?? null, ratingCount: attraction.ratingCount ?? null })}
                                    className="ml-auto p-1.5 text-gray-400 hover:text-blue-500 transition-colors rounded-lg flex-shrink-0"
                                    title="Snip"
                                  >
                                    <Bookmark className="w-4 h-4" />
                                  </button>
                                )}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Attraction discovery panel — inside city card */}
                  <AttrDiscoveryPanel
                    disc={attrDiscovery[cityIdx]}
                    setDisc={patch => patchAttrDiscovery(cityIdx, patch)}
                    context={{ cities: [city.name], attractions: city.attractions.map(a => typeof a === 'string' ? a : a.name) }}
                    primaryCity={city.name}
                    filters={(attractionFilters || []).filter(l => l !== 'All')}
                    existingAttrNames={new Set(city.attractions.map(a => (typeof a === 'string' ? a : a.name).toLowerCase()))}
                    addingName={addingDiscoveredAttr?.cityIdx === cityIdx ? addingDiscoveredAttr.name : null}
                    onAdd={s => addDiscoveredAttraction(cityIdx, s)}
                    onSnip={snipItem}
                    theme="light"
                    t={t}
                    lang={lang}
                  />
                  </>)}
                </div>
                </React.Fragment>
              ))}
            </div>

            {/* Photo explore entry */}
            <button
              onClick={() => { track('explore_nearby_clicked'); enterPhotoMode(); }}
              className="w-full flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-xl p-4 text-left hover:bg-blue-100 transition-colors"
            >
              <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Images className="w-5 h-5 text-blue-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-800">{t('explore_nearby')}</p>
                <p className="text-xs text-gray-500">{t('explore_nearby_sub')}</p>
              </div>
              <span className="text-gray-300 text-xl leading-none">›</span>
            </button>

            {/* City discovery panel */}
            <CityDiscoveryPanel
              disc={cityDiscovery}
              setDisc={patchCityDiscovery}
              context={discoveryContext()}
              existingCityNames={new Set(cities.map(c => c.name.toLowerCase()))}
              onAdd={addDiscoveredCity}
              onSnip={snipItem}
              theme="light"
              triggerLabel={t('where_to_next_trigger', cities[cities.length - 1]?.name.split(',')[0] ?? null)}
              t={t}
              lang={lang}
            />
            </>
          )}
          </div>
        </>
      ))}

      {/* ── Itinerary Screen ── */}
      {screen === 'itinerary' && itinerary && (
        <div className="p-6 space-y-4">
          <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                {editingItineraryName ? (
                  <div className="flex items-center gap-2">
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          const name = renameValue.trim() || itinerary.name;
                          track('itinerary_renamed', { itinerary_name: name });
                          setItinerary(prev => ({ ...prev, name }));
                          if (currentTripId) setSavedTrips(prev => prev.map(t => t._id === currentTripId ? { ...t, name } : t));
                          setEditingItineraryName(false);
                        } else if (e.key === 'Escape') {
                          setEditingItineraryName(false);
                        }
                      }}
                      className="flex-1 text-xl font-bold text-gray-800 border-b-2 border-blue-400 focus:outline-none bg-transparent"
                    />
                    <button onClick={() => {
                      const name = renameValue.trim() || itinerary.name;
                      track('itinerary_renamed', { itinerary_name: name });
                      setItinerary(prev => ({ ...prev, name }));
                      if (currentTripId) setSavedTrips(prev => prev.map(t => t._id === currentTripId ? { ...t, name } : t));
                      setEditingItineraryName(false);
                    }} className="text-green-600 hover:text-green-700 flex-shrink-0">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => setEditingItineraryName(false)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-gray-800 truncate">{itinerary.name}</h2>
                    <button
                      onClick={() => { setRenameValue(itinerary.name || ''); setEditingItineraryName(true); }}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded flex-shrink-0"
                      title={t('rename')}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <p className="text-gray-500 text-sm">{t('days', itinerary.days.length)}</p>
                  <span className="text-gray-300 text-sm">·</span>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="text-xs text-gray-400">{t('trip_start_date')}:</span>
                    <input
                      type="date"
                      value={itinerary.startDate || ''}
                      onChange={e => setItinerary(prev => ({ ...prev, startDate: e.target.value || undefined }))}
                      className="text-xs text-gray-600 border-0 bg-transparent cursor-pointer focus:outline-none w-32"
                    />
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {hasUnsavedChanges && (
                  <span className="w-2 h-2 rounded-full bg-orange-400" title={t('unsaved_dot')} />
                )}
                <button
                  onClick={handleShare}
                  disabled={shareLoading}
                  className="p-1.5 text-gray-400 hover:text-blue-600 rounded transition-colors"
                  title={t('share_itinerary')}
                >
                  {shareLoading ? <span className="text-xs text-gray-400">…</span> : <Share2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {shareUrl && (
              <div className="mt-3 flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-3 py-2">
                <span className="text-xs text-blue-700 truncate flex-1">{shareUrl}</span>
                <button
                  onClick={() => { track('share_link_copied', { itinerary_name: itinerary?.name }); navigator.clipboard.writeText(shareUrl).then(() => { setShareCopied(true); setTimeout(() => setShareCopied(false), 2000); }); }}
                  className="flex-shrink-0 text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  {shareCopied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  <span className="text-xs">{shareCopied ? t('copied') : t('copy')}</span>
                </button>
              </div>
            )}
          </div>

          {/* Global filter preferences — itinerary header */}
          <div className="mb-3">
            <button
              onClick={() => setShowFiltersExpanded(v => !v)}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              <span>{t('edit_interests')}</span>
              {showFiltersExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {showFiltersExpanded && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {ATTRACTION_FILTERS.map(f => {
                  const active = f.label === 'All' ? attractionFilters.length === 0 : attractionFilters.includes(f.label);
                  return (
                    <button
                      key={f.label}
                      onClick={() => {
                        const next = f.label === 'All' ? [] : attractionFilters.includes(f.label) ? attractionFilters.filter(l => l !== f.label) : [...attractionFilters, f.label];
                        try { localStorage.setItem('sniptrip_filters', JSON.stringify(next)); } catch {}
                        setAttractionFilters(next);
                      }}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${active ? 'chip-active' : 'chip-inactive'}`}
                    >
                      {t('filterLabel', f.label)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Photo teaser strip — primary CTA into photo mode */}
          {(() => {
            const cityKey = name => (name || '').split(',')[0].trim().toLowerCase();

            // Scan up to 8 unique cities from the itinerary as candidates.
            // We need more than 4 because nearby cities (e.g. Yosemite Valley + Yosemite NP)
            // share the same Google Place and must be skipped — having extra candidates lets
            // us fill all 4 slots with genuinely distinct places.
            const seenCityKeys = new Set();
            const candidates = [];
            for (const day of itinerary.days) {
              if (candidates.length >= 8) break;
              const cn = day.city || day.activities.find(a => a.cityName)?.cityName;
              const ck = cityKey(cn);
              if (!ck || seenCityKeys.has(ck)) continue;
              seenCityKeys.add(ck);
              candidates.push(cn);
            }

            // Show a single shimmer until every candidate has resolved.
            // Parallel fetches mean total wait ≈ one fetch, not eight.
            const teaserReady = candidates.length > 0 &&
              candidates.every(cn => dayBannerAlternatives[cn] !== undefined);

            // Once loaded: walk candidates in order, skipping any whose top Google Place
            // was already claimed by an earlier slot. This means Yosemite Valley fills
            // slot N, Yosemite NP is skipped entirely, and the next distinct city fills
            // slot N+1 — no duplicate photos, no blank gradients.
            const seenPlaceIds = new Set();
            const slots = [];
            for (const cn of candidates) {
              if (slots.length >= 4) break;
              const alts = dayBannerAlternatives[cn] || [];
              const chosen = alts.find(({ placeId }) => !placeId || !seenPlaceIds.has(placeId));
              if (!chosen) continue; // all this city's places already shown — skip it
              if (chosen.placeId) seenPlaceIds.add(chosen.placeId);
              slots.push(chosen.ref);
            }
            while (slots.length < 4) slots.push(null);

            const DAY_COLORS = ['from-blue-400 to-indigo-500', 'from-amber-400 to-orange-500', 'from-emerald-400 to-teal-500', 'from-rose-400 to-pink-500'];
            return (
              <button
                onClick={() => enterPhotoMode({ sourceOverride: 'itinerary_cta' })}
                className="w-full rounded-2xl overflow-hidden shadow-sm text-left bg-white"
              >
                {/* Photo strip — single shimmer while loading, all-at-once reveal when ready */}
                <div className="flex h-28 gap-0.5">
                  {!teaserReady
                    ? <div className="flex-1 bg-gray-200 animate-pulse" />
                    : slots.map((ref, i) => (
                        <div key={i} className="flex-1 overflow-hidden">
                          {ref
                            ? <img src={`/api/place-photo?ref=${ref}&maxwidth=400`} alt="" className="w-full h-full object-cover" />
                            : <div className={`w-full h-full bg-gradient-to-br ${DAY_COLORS[i % DAY_COLORS.length]}`} />
                          }
                        </div>
                      ))
                  }
                </div>
                {/* CTA row */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="bg-amber-50 rounded-xl p-2 flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm leading-tight">{t('itinerary_photo_cta')}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{t('itinerary_photo_sub')}</p>
                  </div>
                  <div className="btn-primary rounded-full p-2 flex-shrink-0">
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </button>
            );
          })()}

          {/* Add More Attractions Panel */}
          <div className="rounded-2xl shadow-sm overflow-visible">
            <button
              onClick={() => {
                const opening = !showAddMorePanel;
                setShowAddMorePanel(opening);
                if (opening) {
                  track('add_more_panel_opened');
                  // Determine which city to select. Prefer the current valid index; fall back to 0.
                  // If cities (Plan state) is empty but the loaded itinerary has cities, auto-add the
                  // first itinerary city so the search field has something to work with — same as
                  // clicking its pill manually.
                  let targetCities = cities;
                  let idx = (typeof addMoreCityIdx === 'number' && cities[addMoreCityIdx])
                    ? addMoreCityIdx
                    : (cities.length > 0 ? 0 : null);
                  if (idx === null && itinerary) {
                    const seen = new Set(cities.map(c => c.name.toLowerCase()));
                    let firstName = null;
                    for (const d of itinerary.days) {
                      for (const name of [d.city, ...d.activities.map(a => a.cityName)].filter(Boolean)) {
                        if (!seen.has(name.toLowerCase())) { firstName = name; break; }
                      }
                      if (firstName) break;
                    }
                    if (firstName) {
                      targetCities = [...cities, { name: firstName, attractions: [] }];
                      setCities(targetCities);
                      idx = targetCities.length - 1;
                    }
                  }
                  setAddMoreCityIdx(idx);
                  if (idx !== null) {
                    setSelectedCityIndex(idx);
                  }
                }
              }}
              className={`w-full flex items-center justify-between p-4 btn-secondary ${showAddMorePanel ? 'rounded-t-2xl' : 'rounded-2xl'}`}
            >
              <span className="font-semibold text-gray-600 flex items-center gap-2">
                <Plus className="w-4 h-4 text-gray-500" />
                {t('add_more_attractions')}
              </span>
              {showAddMorePanel ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            {showAddMorePanel && (
              <div className="border-t border-gray-200 bg-white rounded-b-2xl p-4 space-y-3">
                {/* City selector chips — include cities from loaded itinerary not already in cities state */}
                {(() => {
                  const normalize = s => s.split(',')[0].toLowerCase().trim();
                  const existingNames = new Set(cities.map(c => normalize(c.name)));
                  const itineraryCityNames = [];
                  if (itinerary) {
                    const seen = new Set(existingNames);
                    itinerary.days.forEach(day => {
                      const candidates = [day.city, ...day.activities.map(a => a.cityName)].filter(Boolean);
                      candidates.forEach(name => {
                        const key = normalize(name);
                        if (!seen.has(key)) { seen.add(key); itineraryCityNames.push(name); }
                      });
                    });
                  }
                  return (
                    <div className="flex flex-wrap gap-1.5">
                      {cities.map((city, cityIdx) => (
                        <button
                          key={cityIdx}
                          onClick={() => {
                            setAddMoreCityIdx(cityIdx);
                            setSelectedCityIndex(cityIdx);
                            setCurrentAttraction('');
                            setShowAttractionSuggestions(true);
                            searchAttractions('', cityIdx, attractionFilters);
                          }}
                          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${addMoreCityIdx === cityIdx ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        >
                          {city.name.split(',')[0]}
                        </button>
                      ))}
                      {itineraryCityNames.map((name, i) => (
                        <button
                          key={`itin-${i}`}
                          onClick={() => {
                            const newIdx = cities.length;
                            setCities(prev => [...prev, { name, attractions: [] }]);
                            setAddMoreCityIdx(newIdx);
                            setSelectedCityIndex(newIdx);
                            setCurrentAttraction('');
                            setShowAttractionSuggestions(true);
                            searchAttractions('', newIdx, []);
                          }}
                          className="px-3 py-1 rounded-full text-sm font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200"
                        >
                          {name.split(',')[0]}
                        </button>
                      ))}
                      <button
                        onClick={() => { setAddMoreCityIdx('new'); setCurrentCity(''); setShowCitySuggestions(false); }}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${addMoreCityIdx === 'new' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        {t('plus_city')}
                      </button>
                    </div>
                  );
                })()}

                {/* New city search */}
                {addMoreCityIdx === 'new' && (
                  <div className="relative" ref={cityInputRef}>
                    <div className="flex gap-2">
                      <input
                        type="search"
                        value={currentCity}
                        onChange={e => { setCurrentCity(e.target.value); setShowCitySuggestions(true); searchCities(e.target.value); }}
                        onFocus={() => setShowCitySuggestions(true)}
                        placeholder={t('search_city_placeholder')}
                        autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"
                        className="flex-1 px-3 py-2 text-sm input-field"
                      />
                      <button
                        onClick={() => { addCity(); setAddMoreCityIdx(cities.length); }}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    {showCitySuggestions && citySuggestions.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-y-auto" style={{ maxHeight: '200px' }}>
                        {citySuggestions.map((city, idx) => (
                          <button
                            key={idx}
                            onMouseDown={e => {
                              e.preventDefault();
                              const newIdx = cities.length;
                              addCity(city);
                              setAddMoreCityIdx(newIdx);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b border-gray-100 last:border-b-0"
                          >
                            {city.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Attraction search for the selected city */}
                {typeof addMoreCityIdx === 'number' && cities[addMoreCityIdx] && (
                  <>
                    {/* Attraction search field */}
                    <div
                      className="relative"
                      ref={el => { if (el) attractionInputRefs.current[addMoreCityIdx] = el; }}
                    >
                      <div className="flex gap-2">
                        <input
                          type="search"
                          value={selectedCityIndex === addMoreCityIdx ? currentAttraction : ''}
                          onChange={e => { setSelectedCityIndex(addMoreCityIdx); setCurrentAttraction(e.target.value); setShowAttractionSuggestions(true); }}
                          onKeyPress={e => { if (e.key === 'Enter' && !showAttractionSuggestions) addAttraction(addMoreCityIdx); }}
                          onClick={() => { setSelectedCityIndex(addMoreCityIdx); setShowAttractionSuggestions(true); if (!currentAttraction) searchAttractions('', addMoreCityIdx, attractionFilters); }}
                          onFocus={() => { setSelectedCityIndex(addMoreCityIdx); setShowAttractionSuggestions(true); if (!currentAttraction) searchAttractions('', addMoreCityIdx, attractionFilters); }}
                          placeholder={t('add_attraction_placeholder', cities[addMoreCityIdx].name.split(',')[0])}
                          autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"
                          className="flex-1 px-3 py-2 text-sm input-field"
                        />
                        <button
                          onClick={() => { setSelectedCityIndex(addMoreCityIdx); addAttraction(addMoreCityIdx); }}
                          className={`px-3 py-2 rounded-lg transition-colors ${addedAttractionCity === addMoreCityIdx ? 'bg-green-500 text-white' : 'btn-primary'}`}
                        >
                          {addedAttractionCity === addMoreCityIdx ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => autoPopulateAttractions(addMoreCityIdx)}
                          className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                          title={t('auto_add_title')}
                          disabled={!!autoAddLoading[addMoreCityIdx]}
                        >
                          {autoAddLoading[addMoreCityIdx]
                            ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            : <Zap className="w-4 h-4" />}
                        </button>
                      </div>

                      {selectedCityIndex === addMoreCityIdx && showAttractionSuggestions && attractionSuggestions.length > 0 && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-y-auto" style={{ maxHeight: 'min(60vh, 300px)' }}>
                          {attractionSuggestions.map((attraction, idx) => (
                            <button
                              key={idx}
                              onMouseDown={e => { e.preventDefault(); e.stopPropagation(); addAttraction(addMoreCityIdx, attraction); }}
                              className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium text-gray-800 text-sm">{attraction.name}</div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {attraction.fullName && attraction.fullName !== attraction.name && <span>{attraction.fullName} • </span>}
                                <span className="text-blue-500">~{attraction.duration}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Attraction discovery panel */}
                    <AttrDiscoveryPanel
                      disc={attrDiscovery[addMoreCityIdx]}
                      setDisc={patch => patchAttrDiscovery(addMoreCityIdx, patch)}
                      context={{ cities: [cities[addMoreCityIdx]?.name].filter(Boolean), attractions: (cities[addMoreCityIdx]?.attractions || []).map(a => typeof a === 'string' ? a : a.name) }}
                      primaryCity={cities[addMoreCityIdx]?.name}
                      filters={(attractionFilters || []).filter(l => l !== 'All')}
                      existingAttrNames={new Set((cities[addMoreCityIdx]?.attractions || []).map(a => (typeof a === 'string' ? a : a.name).toLowerCase()))}
                      addingName={addingDiscoveredAttr?.cityIdx === addMoreCityIdx ? addingDiscoveredAttr.name : null}
                      onAdd={s => addDiscoveredAttraction(addMoreCityIdx, s)}
                      onSnip={snipItem}
                      theme="dark"
                      t={t}
                      lang={lang}
                    />
                  </>
                )}

                {/* City discovery panel */}
                <div className="border-t border-white/10 pt-3 space-y-2">
                  <CityDiscoveryPanel
                    disc={cityDiscovery}
                    setDisc={patchCityDiscovery}
                    context={itineraryDiscoveryContext()}
                    existingCityNames={new Set(cities.map(c => c.name.toLowerCase()))}
                    onAdd={addDiscoveredCity}
                    onSnip={snipItem}
                    theme="dark"
                    triggerLabel={t('where_to_next_trigger', cities[cities.length - 1]?.name.split(',')[0] ?? null)}
                    t={t}
                    lang={lang}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Unscheduled Attractions Pool */}
          {(() => {
            // Find all attractions from cities that aren't in any day
            const scheduledAttractionNames = new Set();
            itinerary.days.forEach(day => {
              day.activities.forEach(activity => {
                scheduledAttractionNames.add(activity.name.toLowerCase());
              });
            });
            
            const unscheduledAttractions = [];
            cities.forEach(city => {
              city.attractions.filter(a => !a.removed).forEach(attr => {
                if (!scheduledAttractionNames.has(attr.name.toLowerCase())) {
                  unscheduledAttractions.push({
                    ...attr,
                    id: `unscheduled-${city.name}-${attr.name}`,
                    cityName: city.name
                  });
                }
              });
            });
            
            if (unscheduledAttractions.length > 0) {
              return (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 mb-4">
                  <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {t('unscheduled_count', unscheduledAttractions.length)}
                  </h3>
                  <p className="text-xs text-amber-700 mb-3">{t('drag_to_schedule')}</p>
                  <ReactSortable
                    list={unscheduledAttractions}
                    setList={() => {}}
                    group="activities"
                    sort={false}
                    animation={200}
                    forceFallback={true}
                    fallbackTolerance={3}
                    touchStartThreshold={5}
                    delayOnTouchOnly={true}
                    delay={100}
                    className="space-y-2"
                  >
                    {unscheduledAttractions.map((attraction, idx) => {
                      const expandKey = `unscheduled-${attraction.id}`;
                      const isExpanded = !!expandedActivities[expandKey];
                      const description = attraction.description || activityDescriptions[expandKey];
                      const isFetchingDesc = !!fetchingDescriptions[expandKey];
                      const photoRefs = attraction.photoRefs || [];
                      const photoIdx = photoIndices[expandKey] || 0;

                      const handleExpandToggle = async (e) => {
                        e.stopPropagation();
                        const nowExpanded = !isExpanded;
                        setExpandedActivities(prev => ({ ...prev, [expandKey]: nowExpanded }));
                        if (nowExpanded && !description && !isFetchingDesc) {
                          setFetchingDescriptions(prev => ({ ...prev, [expandKey]: true }));
                          const fetched = await fetchWikipediaDescription(attraction.name, 'direct', lang);
                          setFetchingDescriptions(prev => ({ ...prev, [expandKey]: false }));
                          if (fetched) {
                            setCities(prev => prev.map(c =>
                              c.name !== attraction.cityName ? c : {
                                ...c,
                                attractions: c.attractions.map(a =>
                                  a.name.toLowerCase() !== attraction.name.toLowerCase() ? a : { ...a, description: fetched }
                                )
                              }
                            ));
                          }
                        }
                      };

                      return (
                        <div key={attraction.id || idx} className="bg-white rounded-lg border border-amber-200 overflow-hidden">
                          <div className="p-2 flex items-center gap-2">
                            <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-800 text-sm">{attraction.name}</div>
                              <div className="text-xs text-gray-500">~{attraction.duration}</div>
                            </div>
                            <button onClick={handleExpandToggle} className="p-1.5 text-gray-400 hover:text-gray-600 rounded flex-shrink-0" title={t('photos_details')}>
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                setCities(prev => prev.map(c =>
                                  c.name !== attraction.cityName ? c : {
                                    ...c,
                                    attractions: c.attractions.filter(a => a.name.toLowerCase() !== attraction.name.toLowerCase())
                                  }
                                ));
                              }}
                              className="text-red-400 hover:text-red-600 flex-shrink-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          {isExpanded && (
                            <div className="border-t border-amber-100 px-3 py-3 space-y-2">
                              {isFetchingDesc && !description && photoRefs.length === 0 && (
                                <p className="text-xs text-gray-400 text-center">{t('loading_details')}</p>
                              )}
                              {photoRefs.length > 0 && (
                                <div className="relative rounded overflow-hidden bg-gray-100 h-36 lg:h-auto lg:aspect-[3/1]">
                                  <img src={`/api/place-photo?ref=${encodeURIComponent(photoRefs[photoIdx])}&maxwidth=400`} alt={attraction.name} className="w-full h-full object-cover" />
                                  {(photoRefs.length > 1 || (!detailsFetched[expandKey] && attraction.placeId)) && (
                                    <>
                                      <button disabled={photoIdx === 0} onClick={e => { e.stopPropagation(); setPhotoIndices(prev => ({ ...prev, [expandKey]: photoIdx - 1 })); }} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold disabled:opacity-25">‹</button>
                                      <button
                                        disabled={!!fetchingPhotos[expandKey]}
                                        onClick={async e => {
                                          e.stopPropagation();
                                          if (photoIdx < photoRefs.length - 1) {
                                            setPhotoIndices(prev => ({ ...prev, [expandKey]: photoIdx + 1 }));
                                          } else if (!detailsFetched[expandKey] && attraction.placeId) {
                                            setFetchingPhotos(prev => ({ ...prev, [expandKey]: true }));
                                            try {
                                              const r = await fetch(`/api/place-photo?placeId=${encodeURIComponent(attraction.placeId)}`);
                                              const { photoRefs: more } = await r.json();
                                              setDetailsFetched(prev => ({ ...prev, [expandKey]: true }));
                                              if (more?.length > 1) {
                                                setCities(prev => prev.map(c => c.name !== attraction.cityName ? c : { ...c, attractions: c.attractions.map(a => a.name.toLowerCase() !== attraction.name.toLowerCase() ? a : { ...a, photoRefs: more }) }));
                                                setPhotoIndices(prev => ({ ...prev, [expandKey]: photoIdx + 1 }));
                                              }
                                            } catch { setDetailsFetched(prev => ({ ...prev, [expandKey]: true })); }
                                            finally { setFetchingPhotos(prev => ({ ...prev, [expandKey]: false })); }
                                          }
                                        }}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold disabled:opacity-25"
                                      >{fetchingPhotos[expandKey] ? '…' : '›'}</button>
                                    </>
                                  )}
                                </div>
                              )}
                              {description && (
                                <div>
                                  <p className={`text-sm text-gray-600 ${expandedDescriptions[expandKey] ? '' : 'line-clamp-3'}`}>{description}</p>
                                  <button onClick={e => { e.stopPropagation(); setExpandedDescriptions(prev => ({ ...prev, [expandKey]: !prev[expandKey] })); }} className="text-xs text-blue-500 hover:underline mt-1 flex items-center gap-0.5">
                                    {expandedDescriptions[expandKey] ? <><ChevronUp className="w-3 h-3" /> {t('less')}</> : <><ChevronDown className="w-3 h-3" /> {t('more')}</>}
                                  </button>
                                </div>
                              )}
                              {attraction.rating != null && (
                                attraction.placeId
                                  ? <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(attraction.name)}&query_place_id=${attraction.placeId}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 pt-1 hover:opacity-70 transition-opacity">
                                      <span className="text-xs text-amber-500">★ {attraction.rating.toFixed(1)}</span>
                                      {attraction.ratingCount != null && <span className="text-xs text-gray-400">({attraction.ratingCount.toLocaleString()})</span>}
                                    </a>
                                  : <div className="flex items-center gap-1 pt-1">
                                      <span className="text-xs text-amber-500">★ {attraction.rating.toFixed(1)}</span>
                                      {attraction.ratingCount != null && <span className="text-xs text-gray-400">({attraction.ratingCount.toLocaleString()})</span>}
                                    </div>
                              )}
                              {itinerary.days.length > 0 && (
                                <div className="pt-1 border-t border-amber-100">
                                  <div className="relative bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                                    <select
                                      value=""
                                      onChange={e => { e.stopPropagation(); addUnscheduledToDay(attraction, parseInt(e.target.value)); }}
                                      onClick={e => e.stopPropagation()}
                                      className="appearance-none bg-transparent text-xs text-gray-500 py-1.5 pl-2.5 pr-7 cursor-pointer border-0 focus:outline-none w-full"
                                    >
                                      <option value="" disabled>{t('move_to_day_picker')}</option>
                                      {itinerary.days.map((d, i) => (
                                        <option key={i} value={String(i)}>
                                          {t('day_label', d.day)}{d.city ? ` — ${d.city.split(',')[0]}` : ''}{formatDayDate(i) ? ` (${formatDayDate(i)})` : ''}
                                        </option>
                                      ))}
                                    </select>
                                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </ReactSortable>
                </div>
              );
            }
            return null;
          })()}

          <ReactSortable
            list={itinerary.days}
            setList={(newList) => {
              const renumbered = newList.map((day, idx) => ({
                ...day,
                day: idx + 1
              }));
              setItinerary({
                ...itinerary,
                days: renumbered
              });
            }}
            animation={200}
            handle=".day-drag-handle"
            className="space-y-4"
            forceFallback={true}
            fallbackTolerance={3}
            touchStartThreshold={5}
            delayOnTouchOnly={true}
            delay={100}
          >
            {itinerary.days.map((day, dayIdx) => {
              // Find cover photo: activity → cities state → lazy-fetched banner → gradient
              const cityForBanner = day.city || day.activities.find(a => a.cityName)?.cityName;
              const coverPhotoRef = (() => {
                if (!bannerErrDays.has(day.id)) {
                  const fromActivity = day.activities.find(a => a.photoRefs?.[0])?.photoRefs?.[0];
                  if (fromActivity) return fromActivity;
                  for (const c of cities) {
                    const a = c.attractions.find(at => day.activities.some(da => da.name.toLowerCase() === at.name.toLowerCase()) && at.photoRefs?.[0]);
                    if (a) return a.photoRefs[0];
                  }
                }
                return (cityForBanner && dayBannerPhotos[cityForBanner]) || null;
              })();
              const gradient = DAY_GRADIENTS[dayIdx % DAY_GRADIENTS.length];
              const cities_in_day = [...new Set([day.city, ...day.activities.map(a => a.cityName)].filter(Boolean))].join(' · ');

              return (
                <div
                  key={day.id}
                  className="card overflow-hidden"
                >
                {/* Banner header */}
                <div className="relative h-28">
                  {coverPhotoRef ? (
                    <img
                      src={`/api/place-photo?ref=${encodeURIComponent(coverPhotoRef)}&maxwidth=600`}
                      alt={cities_in_day || t('day_label', day.day)}
                      className="w-full h-full object-cover"
                      onError={() => {
                        if (bannerErrDays.has(day.id)) return;
                        setBannerErrDays(prev => new Set([...prev, day.id]));
                        if (!cityForBanner || dayBannerFetchingRef.current.has(cityForBanner) || dayBannerPhotos[cityForBanner] !== undefined) return;
                        dayBannerFetchingRef.current.add(cityForBanner);
                        fetchTopAttractions(cityForBanner, lang)
                          .then(results => {
                            const ref = results?.find(r => r.photos?.[0]?.photo_reference)?.photos?.[0]?.photo_reference;
                            setDayBannerPhotos(prev => ({ ...prev, [cityForBanner]: ref || null }));
                          })
                          .catch(() => setDayBannerPhotos(prev => ({ ...prev, [cityForBanner]: null })))
                          .finally(() => dayBannerFetchingRef.current.delete(cityForBanner));
                      }}
                    />
                  ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${gradient}`} />
                  )}
                  {/* Scrim */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  {/* Drag handle */}
                  <div className="day-drag-handle absolute top-2 left-2 cursor-move text-white/70 hover:text-white" title={t('drag_to_reorder')}>
                    <GripVertical className="w-5 h-5" />
                  </div>
                  {/* Delete button */}
                  <button
                    onClick={() => removeDay(dayIdx)}
                    className="absolute top-2 right-2 text-white/70 hover:text-white"
                    title="Delete this day"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {/* Text overlay + expand toggle */}
                  <button
                    onClick={() => toggleDay(dayIdx)}
                    className="absolute inset-x-0 bottom-0 p-3 text-left flex items-end justify-between"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-bold text-lg">{t('day_label', day.day)}{formatDayDate(dayIdx) ? ` — ${formatDayDate(dayIdx)}` : ''}</span>
                        <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full">{t('activity_count', day.activities.length)}</span>
                        {day.note && <span className="text-xs bg-amber-400/80 text-white px-2 py-0.5 rounded-full">📝</span>}
                      </div>
                      {cities_in_day && <p className="text-white/80 text-sm">{cities_in_day}</p>}
                    </div>
                    {day.expanded
                      ? <ChevronUp className="w-5 h-5 text-white/80 flex-shrink-0 mb-0.5" />
                      : <ChevronDown className="w-5 h-5 text-white/80 flex-shrink-0 mb-0.5" />}
                  </button>
                </div>

              {day.expanded && (
                <div className="px-4 pb-4">

                  {/* Day Note */}
                  {editingNoteDay === dayIdx ? (
                    <div className="mb-4">
                      <textarea
                        autoFocus
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder={t('note_placeholder')}
                        className="w-full px-3 py-2 text-sm border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 bg-amber-50 resize-none"
                        rows={5}
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => {
                            track('note_saved', { day_number: itinerary.days[dayIdx]?.day });
                            setItinerary(cur => ({
                              ...cur,
                              days: cur.days.map((d, i) => i === dayIdx ? { ...d, note: noteText.trim() || undefined } : d)
                            }));
                            setEditingNoteDay(null);
                          }}
                          className="bg-amber-500 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-amber-600"
                        >
                          {t('save_note')}
                        </button>
                        <button
                          onClick={() => setEditingNoteDay(null)}
                          className="bg-gray-100 text-gray-700 px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-200"
                        >
                          {t('cancel')}
                        </button>
                      </div>
                    </div>
                  ) : day.note ? (
                    <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 group relative">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{day.note}</p>
                      <div className="flex gap-3 mt-2">
                        <button
                          onClick={() => { setNoteText(day.note); setEditingNoteDay(dayIdx); }}
                          className="text-amber-500 hover:text-amber-700"
                          title="Edit note"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { track('note_deleted', { day_number: itinerary.days[dayIdx]?.day }); setItinerary(cur => ({ ...cur, days: cur.days.map((d, i) => i === dayIdx ? { ...d, note: undefined } : d) })); }}
                          className="text-red-400 hover:text-red-600"
                          title="Delete note"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setNoteText(''); setEditingNoteDay(dayIdx); }}
                      className="w-full mb-4 border border-dashed border-gray-300 rounded-xl py-2 text-sm text-gray-400 hover:border-amber-400 hover:text-amber-500 hover:bg-amber-50 transition-colors"
                    >
                      {t('add_note')}
                    </button>
                  )}

                  {/* ReactSortable — wrapped in drop zone styling when empty so it is the first visible target */}
                  {(() => {
                    const sortable = (
                      <ReactSortable
                        list={day.activities}
                        setList={(newList) => {
                          setItinerary((currentItinerary) => {
                            const updatedDays = [...currentItinerary.days];
                            if (newList.length > 0) {
                              const prevIds = new Set(currentItinerary.days[dayIdx].activities.map(a => a.id));
                              // Clear pinnedTime on activities dragged in from another day
                              const cleaned = newList.map(a => (!prevIds.has(a.id) && a.pinnedTime) ? { ...a, pinnedTime: false } : a);
                              const recalculated = recalculateTimes(cleaned);
                              updatedDays[dayIdx] = { ...updatedDays[dayIdx], activities: recalculated };
                              if (!updatedDays[dayIdx].city || updatedDays[dayIdx].city === '') {
                                const activityWithCity = recalculated.find(a => a.cityName);
                                if (activityWithCity) {
                                  updatedDays[dayIdx].city = activityWithCity.cityName;
                                } else {
                                  const firstActivity = recalculated[0];
                                  if (firstActivity && firstActivity.fullName) {
                                    const parts = firstActivity.fullName.split(',').map(p => p.trim());
                                    if (parts.length >= 2) updatedDays[dayIdx].city = parts[parts.length - 2] || parts[parts.length - 1];
                                  }
                                }
                              }
                            } else {
                              updatedDays[dayIdx] = { ...updatedDays[dayIdx], activities: [] };
                            }
                            return { ...currentItinerary, days: updatedDays };
                          });
                        }}
                        group="activities"
                        animation={200}
                        handle=".drag-handle"
                        className={day.activities.length === 0 ? 'min-h-14' : 'space-y-3'}
                        forceFallback={true}
                        fallbackTolerance={3}
                        touchStartThreshold={5}
                        delayOnTouchOnly={true}
                        delay={100}
                      >
                    {day.activities.map((activity, actIdx) => {
                      const isEditing = editingActivity?.dayIndex === dayIdx && 
                                       editingActivity?.activityIndex === actIdx;
                      
                      return (
                        <div
                          key={activity.id}
                          className="bg-gray-50 rounded-lg overflow-hidden"
                        >
                        {isEditing ? (
                          // Edit Mode
                          <div className="p-3 space-y-3">
                            {activity.type !== 'hotel' && <div className="font-medium text-gray-800">{activity.name}</div>}
                            <div className="space-y-2">
                              {/* Name search — hotel and meal */}
                              {(activity.type === 'hotel' || activity.type === 'meal') && (
                                <div className="relative">
                                  <label className="text-xs text-gray-600 block mb-1">
                                    {activity.type === 'hotel' ? t('hotel_name_label') : t('restaurant_label')}
                                  </label>
                                  <input
                                    autoFocus={activity.type === 'hotel'}
                                    type="text"
                                    value={editNameQuery}
                                    onChange={e => { setEditNameQuery(e.target.value); searchEditName(e.target.value, activity.type); }}
                                    placeholder={activity.type === 'hotel' ? t('search_hotel') : t('search_restaurant')}
                                    className="w-full px-3 py-2 text-sm input-field"
                                  />
                                  {(editNameResults.length > 0 || editNameLoading) && (
                                    <div className="absolute z-50 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                                      {editNameLoading && <div className="px-3 py-2 text-xs text-gray-400">{t('searching')}</div>}
                                      {editNameResults.map((place, i) => (
                                        <button
                                          key={i}
                                          className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-0"
                                          onClick={() => { setEditNameQuery(place.name); setEditAddress(place.formatted_address || ''); setEditNameResults([]); }}
                                        >
                                          <div className="text-sm font-medium text-gray-800">{place.name}</div>
                                          {place.formatted_address && <div className="text-xs text-gray-500 truncate">{place.formatted_address}</div>}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                              {/* Address — hotel and meal */}
                              {(activity.type === 'hotel' || activity.type === 'meal') && (
                                <div>
                                  <label className="text-xs text-gray-600 block mb-1">{t('address_label')}</label>
                                  <input
                                    type="text"
                                    value={editAddress}
                                    onChange={e => setEditAddress(e.target.value)}
                                    placeholder={t('address_label')}
                                    className="w-full px-3 py-2 text-sm input-field"
                                  />
                                </div>
                              )}
                              {/* Time + Duration — not for hotel */}
                              {activity.type !== 'hotel' && (
                                <>
                                  <div>
                                    <label className="text-xs text-gray-600 block mb-1">{t('time_label')}</label>
                                    <input type="text" value={editTime} onChange={e => setEditTime(e.target.value)} placeholder={t('time_placeholder')} className="w-full px-3 py-2 text-sm input-field" />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-600 block mb-1">{t('duration_label')}</label>
                                    <input type="text" value={editDuration} onChange={e => setEditDuration(e.target.value)} placeholder={t('duration_placeholder')} className="w-full px-3 py-2 text-sm input-field" />
                                  </div>
                                </>
                              )}
                              {/* Nights — hotel only */}
                              {activity.type === 'hotel' && (
                                <div>
                                  <label className="text-xs text-gray-600 block mb-1">{t('hotel_nights_label')}</label>
                                  <input
                                    type="number"
                                    min="1"
                                    max="30"
                                    value={editHotelNights}
                                    onChange={e => { const n = parseInt(e.target.value, 10); setEditHotelNights(isNaN(n) ? '' : Math.min(30, Math.max(1, n))); }}
                                    onBlur={() => { if (!editHotelNights || editHotelNights < 1) setEditHotelNights(1); }}
                                    className="w-24 px-3 py-2 text-sm input-field"
                                  />
                                </div>
                              )}
                              {/* Notes — hotel and meal */}
                              {(activity.type === 'hotel' || activity.type === 'meal') && (
                                <div>
                                  <label className="text-xs text-gray-600 block mb-1">{t('notes_label')}</label>
                                  <textarea
                                    value={editActivityNote}
                                    onChange={e => setEditActivityNote(e.target.value)}
                                    placeholder={t('notes_placeholder')}
                                    rows={3}
                                    className="w-full px-3 py-2 text-sm input-field resize-none"
                                  />
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button onClick={saveActivityEdit} className="flex-1 bg-green-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-green-700">{t('save')}</button>
                              <button onClick={cancelEdit} className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-400">{t('cancel')}</button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          (() => {
                            const expandKey = `act-${activity.id}`;
                            const isExpanded = !!expandedActivities[expandKey];
                            const showToggle = activity.type !== 'transportation';

                            // Look up matching attraction for photos + description
                            let attrData = null, attrCityIdx = -1, attrAttrIdx = -1;
                            for (let ci = 0; ci < cities.length; ci++) {
                              const ai = cities[ci].attractions.findIndex(a => a.name.toLowerCase() === activity.name.toLowerCase());
                              if (ai !== -1) { attrData = cities[ci].attractions[ai]; attrCityIdx = ci; attrAttrIdx = ai; break; }
                            }

                            const photoRefs = attrData?.photoRefs || activity.photoRefs || [];
                            const placeId = attrData?.placeId || activity.placeId;
                            const description = attrData?.description || activityDescriptions[expandKey] || activity.description;
                            const isFetchingDesc = !!fetchingDescriptions[expandKey];

                            const handleExpandToggle = async () => {
                              const nowExpanded = !isExpanded;
                              setExpandedActivities(prev => ({ ...prev, [expandKey]: nowExpanded }));
                              if (!nowExpanded || activity.type === 'meal' || activity.type === 'hotel' || activity.type === 'transportation') return;

                              // Fetch Wikipedia description if missing
                              if (!description && !isFetchingDesc) {
                                setFetchingDescriptions(prev => ({ ...prev, [expandKey]: true }));
                                const fetched = await fetchWikipediaDescription(activity.name, 'direct', lang);
                                setFetchingDescriptions(prev => ({ ...prev, [expandKey]: false }));
                                if (fetched) {
                                  if (attrCityIdx !== -1) {
                                    setCities(prev => prev.map((c, ci) => ci !== attrCityIdx ? c : {
                                      ...c,
                                      attractions: c.attractions.map((a, ai) => ai !== attrAttrIdx ? a : { ...a, description: fetched })
                                    }));
                                  } else {
                                    setActivityDescriptions(prev => ({ ...prev, [expandKey]: fetched }));
                                  }
                                }
                              }

                              // Lazily fetch rating + placeId for activities from old saved trips
                              const hasRating = (attrData?.rating ?? activity.rating) != null;
                              if (!hasRating && !activity.ratingFetched) {
                                const cityCtx = activity.cityName || day.city || '';
                                const query = cityCtx ? `${activity.name} ${cityCtx}` : activity.name;
                                try {
                                  const res = await fetch(`/api/search-attractions?query=${encodeURIComponent(query)}&language=${encodeURIComponent(lang)}`);
                                  if (res.ok) {
                                    const data = await res.json();
                                    const place = data.results?.[0];
                                    setItinerary(prev => ({
                                      ...prev,
                                      days: prev.days.map(d => ({
                                        ...d,
                                        activities: d.activities.map(a => a.id !== activity.id ? a : {
                                          ...a,
                                          ratingFetched: true,
                                          cityName: a.cityName || day.city || null,
                                          placeId: a.placeId || place?.place_id || null,
                                          rating: place?.rating ?? null,
                                          ratingCount: place?.user_ratings_total ?? null,
                                        })
                                      }))
                                    }));
                                  }
                                } catch { /* silent */ }
                              }
                            };

                            return (
                              <div>
                                <div className="flex items-start gap-3 p-3">
                                  <div className="drag-handle cursor-move text-gray-400 hover:text-gray-600 mt-1 flex-shrink-0" title={t('drag_to_reorder')}>
                                    <GripVertical className="w-5 h-5" />
                                  </div>
                                  {activity.type === 'transportation' ? (
                                    <div className="w-5 h-5 mt-1 flex-shrink-0">🚗</div>
                                  ) : activity.type === 'meal' ? (
                                    <div className="w-5 h-5 mt-1 flex-shrink-0">🍽️</div>
                                  ) : activity.type === 'hotel' ? (
                                    <div className="w-5 h-5 mt-1 flex-shrink-0">🏨</div>
                                  ) : (
                                    <Clock className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-800">
                                      {activity.type === 'hotel' ? (activity.name || t('hotel')) : activity.name}
                                      {activity.type === 'meal' && activity.restaurant && (
                                        <span className="text-sm text-gray-600 ml-2">{t('meal_at', activity.restaurant)}</span>
                                      )}
                                    </div>
                                    {activity.type === 'hotel' && activity.address ? (
                                      <div className="text-xs text-gray-400 truncate">{activity.address}</div>
                                    ) : activity.type !== 'hotel' ? (
                                      <div className="text-sm text-gray-500">{activity.time} • {activity.duration}</div>
                                    ) : null}
                                  </div>
                                  {/* Expandable: chevron only. Transport: edit + delete always visible */}
                                  {showToggle ? (
                                    <button onClick={handleExpandToggle} className="p-1.5 text-gray-400 hover:text-gray-600 rounded flex-shrink-0" title={t('details')}>
                                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                    </button>
                                  ) : (
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      <button onClick={() => startEditingActivity(dayIdx, actIdx)} className="p-1.5 text-blue-600 hover:bg-blue-100 rounded" title={t('edit')}>
                                        <Edit2 className="w-4 h-4" />
                                      </button>
                                      <button onClick={() => removeActivity(dayIdx, actIdx)} className="p-1.5 text-red-500 hover:bg-red-100 rounded" title={t('remove')}>
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  )}
                                </div>

                                {showToggle && isExpanded && (
                                  <div className="border-t border-gray-200">
                                    {(activity.type === 'hotel' || activity.type === 'meal') && (
                                      <div className="px-3 py-2 space-y-1">
                                        {activity.address && <p className="text-xs text-gray-500">{activity.address}</p>}
                                        {activity.note && <p className="text-sm text-gray-700 whitespace-pre-wrap">{activity.note}</p>}
                                        <div className="action-row pt-1">
                                          <button onClick={() => startEditingActivity(dayIdx, actIdx)} className="btn-action-primary">
                                            <Edit2 className="w-4 h-4" /> {t('edit')}
                                          </button>
                                          <button onClick={() => removeActivity(dayIdx, actIdx)} className="btn-action-danger">
                                            <Trash2 className="w-4 h-4" /> {t('remove')}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                    {activity.type !== 'hotel' && activity.type !== 'meal' && (
                                      <AttractionCard
                                        name={activity.name}
                                        cityName={attrData?.cityName || activity.cityName || day.city}
                                        placeId={placeId}
                                        rating={attrData?.rating ?? activity.rating}
                                        ratingCount={attrData?.ratingCount ?? activity.ratingCount}
                                        description={description}
                                        photoRefs={photoRefs}
                                        photoKey={expandKey}
                                        source="itinerary"
                                        photoIndices={photoIndices} setPhotoIndices={setPhotoIndices}
                                        fetchingPhotos={fetchingPhotos} setFetchingPhotos={setFetchingPhotos}
                                        detailsFetched={detailsFetched} setDetailsFetched={setDetailsFetched}
                                        expandedDescriptions={expandedDescriptions} setExpandedDescriptions={setExpandedDescriptions}
                                        onMorePhotoRefs={attrCityIdx !== -1
                                          ? newRefs => setCities(prev => prev.map((c, ci) => ci !== attrCityIdx ? c : { ...c, attractions: c.attractions.map((a, ai) => ai !== attrAttrIdx ? a : { ...a, photoRefs: newRefs }) }))
                                          : newRefs => setItinerary(prev => ({ ...prev, days: prev.days.map(d => ({ ...d, activities: d.activities.map(a => a.id !== activity.id ? a : { ...a, photoRefs: newRefs }) })) }))}
                                        loading={isFetchingDesc && !description && photoRefs.length === 0}
                                        t={t}
                                        actions={
                                          <>
                                            {(attrData?.fullName || activity.fullName) && (
                                              <p className="text-xs text-gray-400 px-3 pb-1">{attrData?.fullName || activity.fullName}</p>
                                            )}
                                            <div className="action-row">
                                              <button onClick={() => startEditingActivity(dayIdx, actIdx)} className="btn-action-primary">
                                                <Edit2 className="w-4 h-4" /> {t('edit')}
                                              </button>
                                              {!activity.type && (
                                                <button onClick={() => snipItem('attraction', { name: activity.name, cityName: activity.cityName, types: activity.types || [], rating: activity.rating ?? null, ratingCount: activity.ratingCount ?? null })} className="btn-action-secondary">
                                                  <Bookmark className="w-4 h-4" /> {t('snip')}
                                                </button>
                                              )}
                                              {itinerary.days.length > 1 && (
                                                <div className="relative btn-action-secondary p-0">
                                                  <select
                                                    value=""
                                                    onChange={e => moveActivityToDay(dayIdx, actIdx, parseInt(e.target.value))}
                                                    className="appearance-none bg-transparent text-sm text-gray-500 py-2.5 pl-3 pr-7 cursor-pointer border-0 focus:outline-none"
                                                  >
                                                    <option value="" disabled>{t('move_to_day_picker')}</option>
                                                    {itinerary.days.map((d, i) => i !== dayIdx && (
                                                      <option key={i} value={String(i)}>
                                                        {t('day_label', d.day)}{d.city ? ` — ${d.city.split(',')[0]}` : ''}{formatDayDate(i) ? ` (${formatDayDate(i)})` : ''}
                                                      </option>
                                                    ))}
                                                  </select>
                                                  <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                                </div>
                                              )}
                                              <button onClick={() => removeActivity(dayIdx, actIdx)} className="btn-action-danger">
                                                <Trash2 className="w-4 h-4" /> {t('remove')}
                                              </button>
                                            </div>
                                          </>
                                        }
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })()
                        )}
                      </div>
                    );
                  })}
                  </ReactSortable>
                  );
                  if (day.activities.length === 0) {
                    return (
                      <div className="relative border-2 border-dashed border-gray-200 rounded-xl mb-3">
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <p className="text-xs text-gray-400">{t('drag_here')}</p>
                        </div>
                        {sortable}
                      </div>
                    );
                  }
                  return sortable;
                })()}

                  {/* Empty-day discovery panel */}
                  {day.activities.length === 0 && (() => {
                    const disc = emptyDayDisc[day.id] || {};
                    const existingCityNames = new Set(cities.map(c => c.name.toLowerCase()));
                    return (
                      <div className="mb-4 bg-gray-50 rounded-xl p-4 space-y-3">
                        {disc.selectedDest && (
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-700">📍 {disc.selectedDest.split(',')[0]}</p>
                            <button onClick={() => setEmptyDayDisc(prev => ({ ...prev, [day.id]: { ...prev[day.id], selectedDest: null, attrDisc: null } }))} className="text-xs text-gray-400 hover:text-gray-600">{t('change_back')}</button>
                          </div>
                        )}
                        {!disc.selectedDest && (
                          <div className="space-y-2">
                            {(() => {
                              // Derive city chips from the itinerary's other days, not from Plan-tab cities state
                              const seen = new Set();
                              const itineraryCities = [];
                              itinerary.days.forEach((d, i) => {
                                if (i === dayIdx) return;
                                const candidates = [d.city, ...d.activities.map(a => a.cityName)].filter(Boolean);
                                candidates.forEach(name => {
                                  const key = name.toLowerCase();
                                  if (!seen.has(key)) { seen.add(key); itineraryCities.push(name); }
                                });
                              });
                              if (itineraryCities.length === 0) return null;
                              return (
                                <div>
                                  <p className="text-xs text-gray-400 mb-1.5">{t('continue_in_city')}</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {itineraryCities.map((name, idx) => (
                                      <button key={idx} onClick={() => selectEmptyDayDest(day.id, dayIdx, name)} className="text-xs px-2.5 py-1 bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700 rounded-full transition-colors">
                                        {name.split(',')[0]}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()}
                            <CityDiscoveryPanel
                              disc={disc.cityDisc}
                              setDisc={patch => patchEdd(day.id, 'cityDisc', patch)}
                              context={itineraryDiscoveryContext()}
                              existingCityNames={existingCityNames}
                              onAdd={cityName => selectEmptyDayDest(day.id, dayIdx, cityName)}
                              onDismiss={() => setEmptyDayDisc(prev => ({ ...prev, [day.id]: {} }))}
                              onSnip={snipItem}
                              theme="light"
                              triggerLabel={(() => {
                                const hasOtherCities = itinerary.days.some((d, i) => i !== dayIdx && (d.city || d.activities.some(a => a.cityName)));
                                if (hasOtherCities) return t('or_discover_new');
                                const prevCity = (() => {
                                  for (let i = dayIdx - 1; i >= 0; i--) {
                                    const d = itinerary.days[i];
                                    if (d.city) return d.city.split(',')[0];
                                    const act = d.activities.find(a => a.cityName);
                                    if (act) return act.cityName.split(',')[0];
                                  }
                                  return cities[0]?.name.split(',')[0] ?? null;
                                })();
                                return t('where_to_next_trigger', prevCity);
                              })()}
                              addLabel={t('select')}
                              t={t}
                              lang={lang}
                            />
                          </div>
                        )}
                        {disc.selectedDest && (
                          <AttrDiscoveryPanel
                            disc={disc.attrDisc}
                            setDisc={patch => patchEdd(day.id, 'attrDisc', patch)}
                            context={{ cities: [disc.selectedDest].filter(Boolean), attractions: [] }}
                            primaryCity={disc.selectedDest?.split(',')[0]}
                            filters={[]}
                            existingAttrNames={new Set()}
                            addingName={addingEmptyDayAttr?.dayId === day.id ? addingEmptyDayAttr.name : null}
                            onAdd={s => addEmptyDayAttraction(day.id, dayIdx, s)}
                            onDismiss={() => setEmptyDayDisc(prev => ({ ...prev, [day.id]: { ...prev[day.id], selectedDest: null, attrDisc: null } }))}
                            onSnip={snipItem}
                            theme="light"
                            t={t}
                            lang={lang}
                          />
                        )}
                      </div>
                    );
                  })()}

                  {/* Add Transportation / Meal / Hotel */}
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => addTransportation(dayIdx)} className="flex items-center gap-1 pl-2 pr-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-sm" title="Add Transportation">
                      <Plus className="w-3 h-3 text-gray-500" /><span>🚗</span>
                    </button>
                    <button onClick={() => addMeal(dayIdx)} className="flex items-center gap-1 pl-2 pr-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-sm" title="Add Meal">
                      <Plus className="w-3 h-3 text-gray-500" /><span>🍽️</span>
                    </button>
                    <button onClick={() => addHotel(dayIdx)} className="flex items-center gap-1 pl-2 pr-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-sm" title="Add Hotel">
                      <Plus className="w-3 h-3 text-gray-500" /><span>🏨</span>
                    </button>
                  </div>

                  {/* Per-day attraction discovery panel */}
                  {day.activities.length > 0 && (() => {
                    // Derive city for AI context: day.city → activity cityName → prev day → first day
                    const dayPrimaryCity = (
                      day.city ||
                      day.activities.find(a => a.cityName)?.cityName ||
                      itinerary.days.slice(0, dayIdx).reverse().find(d => d.city)?.city ||
                      itinerary.days.find(d => d.city)?.city ||
                      ''
                    ).split(',')[0].trim();
                    if (!dayPrimaryCity) return null;
                    const dayDisc = dayAddDiscovery[day.id];
                    return (
                      <div className="mt-3 flex flex-col gap-2">
                        <AttrDiscoveryPanel
                          key={dayAutoOpenKey[day.id] || 'stable'}
                          disc={dayDisc}
                          setDisc={patch => patchDayAddDisc(day.id, patch)}
                          context={{
                            cities: [dayCityOverride[day.id] || dayPrimaryCity].filter(Boolean),
                            attractions: itinerary.days.flatMap(d => d.activities.filter(a => !a.type).map(a => a.name)),
                          }}
                          primaryCity={dayCityOverride[day.id] || dayPrimaryCity}
                          filters={attractionFilters}
                          existingAttrNames={new Set(itinerary.days.flatMap(d => d.activities.filter(a => !a.type).map(a => a.name.toLowerCase())))}
                          addingName={addingDayAttr?.dayId === day.id ? addingDayAttr.name : null}
                          onAdd={s => addDiscoveredAttractionToDay(dayIdx, s)}
                          onSnip={snipItem}
                          onDismiss={() => {
                            setDayAutoOpenKey(prev => { const n = { ...prev }; delete n[day.id]; return n; });
                            setDayCityOverride(prev => { const n = { ...prev }; delete n[day.id]; return n; });
                            patchDayAddDisc(day.id, { phase: 'idle', questions: [], suggestions: [], freeformVisible: false, freeformText: '' });
                          }}
                          theme="light"
                          triggerLabel={dayCityOverride[day.id] ? t('what_else_trigger', dayCityOverride[day.id].split(',')[0]) : t('what_else_trigger', dayPrimaryCity)}
                          onTriggerClick={() => setDayAutoOpenKey(prev => ({ ...prev, [day.id]: Date.now() }))}
                          autoOpen={!!dayAutoOpenKey[day.id]}
                          t={t}
                          lang={lang}
                        />
                        {/* Inline city-add search */}
                        {dayChipPicker === day.id ? (
                          <div>
                            <div className="flex gap-2 items-center">
                              <input
                                type="search"
                                value={dayInlineCityQuery}
                                autoFocus
                                onChange={e => { setDayInlineCityQuery(e.target.value); searchDayInlineCity(e.target.value); }}
                                placeholder={t('search_city_placeholder')}
                                autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"
                                className="flex-1 px-3 py-2 text-sm input-field"
                              />
                              <button
                                onClick={() => { setDayChipPicker(null); setDayInlineCityQuery(''); setDayInlineCitySuggestions([]); }}
                                className="text-sm text-gray-500 hover:text-gray-700 px-2 py-2"
                              >
                                {t('cancel')}
                              </button>
                            </div>
                            {dayInlineCitySuggestions.length > 0 && (
                              <div className="mt-1 bg-white border border-gray-300 rounded-lg overflow-hidden">
                                {dayInlineCitySuggestions.map((city, idx) => (
                                  <button
                                    key={idx}
                                    onMouseDown={e => {
                                      e.preventDefault();
                                      setDayChipPicker(null);
                                      setDayInlineCityQuery('');
                                      setDayInlineCitySuggestions([]);
                                      setDayCityOverride(prev => ({ ...prev, [day.id]: city.name }));
                                      setDayAutoOpenKey(prev => ({ ...prev, [day.id]: Date.now() }));
                                      patchDayAddDisc(day.id, { phase: 'idle', questions: [], suggestions: [], freeformVisible: false, freeformText: '' });
                                    }}
                                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b border-gray-100 last:border-b-0"
                                  >
                                    {city.name}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={() => { setDayChipPicker(day.id); setDayInlineCityQuery(''); setDayInlineCitySuggestions([]); }}
                            className="text-xs text-gray-400 hover:text-gray-600 text-left"
                          >
                            {t('day_chip_add_city')}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
          </ReactSortable>

          {/* Action Buttons */}
          <div className="space-y-3 mt-4">
            <button
              onClick={addDay}
              className="w-full btn-secondary py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              {t('add_day')}
            </button>

            <button
              onClick={openPrintView}
              className="w-full btn-secondary py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
            >
              {t('print_export')}
            </button>

            <button
              onClick={handleSave}
              className="w-full btn-primary py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              {currentTripId ? (hasUnsavedChanges ? t('save_changes') : t('saved_check')) : t('save_itinerary')}
            </button>
          </div>
        </div>
      )}

      {/* ── Saved Screen ── */}
      {screen === 'saved' && (
        <div className="p-6 space-y-6">
          {!user ? (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-3">{t('sign_in_to_save')}</p>
              <button
                onClick={() => { setAuthMode('login'); setShowAuthModal(true); track('auth_modal_opened'); }}
                className="text-blue-600 text-sm font-medium hover:underline"
              >
                {t('sign_in_or_create')}
              </button>
            </div>
          ) : (
            <>
              {/* ── Snips section ─────────────────────────────────────── */}
              {snips.length > 0 && (() => {
                const STATE_NAMES = { AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',DC:'Washington D.C.' };
                const getRegion = (snip) => {
                  const str = snip.type === 'attraction' ? (snip.cityName || snip.name) : snip.name;
                  const parts = str.split(',').map(p => p.trim()).filter(Boolean);
                  // For "City, State, Country" take the state; for "City, Country" take the country
                  const raw = parts.length >= 3 ? parts[parts.length - 2] : parts.length === 2 ? parts[parts.length - 1] : parts[0];
                  return STATE_NAMES[raw] || raw;
                };
                const grouped = snips.reduce((acc, snip) => {
                  const region = getRegion(snip);
                  if (!acc[region]) acc[region] = [];
                  acc[region].push(snip);
                  return acc;
                }, {});
                return (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Bookmark className="w-4 h-4 text-gray-400" />
                      <h2 className="text-sm font-semibold text-gray-700">{t('snips')}</h2>
                    </div>
                    <div className="space-y-2">
                      {Object.entries(grouped).map(([region, items]) => {
                        const isOpen = !!expandedFolders[region];
                        return (
                          <div key={region} className="card overflow-hidden">
                            {/* Folder header */}
                            <button
                              onClick={() => { if (!expandedFolders[region]) track('snip_folder_expanded', { region }); setExpandedFolders(prev => ({ ...prev, [region]: !prev[region] })); }}
                              className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
                            >
                              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isOpen ? 'bg-blue-100' : 'bg-gray-100'}`}>
                                <MapPin className={`w-4 h-4 ${isOpen ? 'text-blue-600' : 'text-gray-400'}`} />
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-gray-800">{region}</p>
                                <p className="text-xs text-gray-400">{t('place_count', items.length)}</p>
                              </div>
                              {isOpen ? <ChevronUp className="w-4 h-4 text-gray-300 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-300 flex-shrink-0" />}
                            </button>

                            {/* Folder contents */}
                            {isOpen && (
                              <div className="border-t border-gray-100 divide-y divide-gray-50">
                                {items.map(snip => {
                                  const isExpanded = !!expandedSnips[snip._id];
                                  const details = snipDetails[snip._id];
                                  return (
                                    <div key={snip._id}>
                                      <button
                                        onClick={() => toggleSnipExpand(snip)}
                                        className="w-full flex items-center gap-2 px-4 py-3 text-left"
                                      >
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-gray-800 truncate">{snip.name}</p>
                                          <div className="flex items-center gap-2">
                                            {snip.type === 'attraction' && snip.cityName && (
                                              <p className="text-xs text-gray-400 truncate">{snip.cityName.split(',')[0]}</p>
                                            )}
                                            {snip.type === 'attraction' && snip.rating != null && (
                                              snip.placeId
                                                ? <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(snip.name)}&query_place_id=${snip.placeId}`} target="_blank" rel="noopener noreferrer" className="text-xs text-amber-500 flex-shrink-0 hover:opacity-70 transition-opacity">★ {snip.rating.toFixed(1)}{snip.ratingCount != null && <span className="text-gray-400"> ({snip.ratingCount.toLocaleString()})</span>}</a>
                                                : <p className="text-xs text-amber-500 flex-shrink-0">★ {snip.rating.toFixed(1)}{snip.ratingCount != null && <span className="text-gray-400"> ({snip.ratingCount.toLocaleString()})</span>}</p>
                                            )}
                                          </div>
                                        </div>
                                        <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full ${snip.type === 'city' ? 'bg-blue-50 text-blue-500' : 'bg-blue-50 text-blue-500'}`}>
                                          {snip.type}
                                        </span>
                                        {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />}
                                      </button>

                                      {isExpanded && (
                                        snip.type === 'attraction'
                                          ? <AttractionCard
                                              name={snip.name}
                                              cityName={snip.cityName}
                                              placeId={snip.placeId}
                                              rating={snip.rating}
                                              ratingCount={snip.ratingCount}
                                              description={details?.description || null}
                                              photoRefs={details?.photoRef ? [details.photoRef] : []}
                                              photoKey={snip._id}
                                              source="snips"
                                              photoIndices={photoIndices} setPhotoIndices={setPhotoIndices}
                                              fetchingPhotos={fetchingPhotos} setFetchingPhotos={setFetchingPhotos}
                                              detailsFetched={detailsFetched} setDetailsFetched={setDetailsFetched}
                                              expandedDescriptions={expandedDescriptions} setExpandedDescriptions={setExpandedDescriptions}
                                              onMorePhotoRefs={null}
                                              loading={!!details?.loading}
                                              t={t}
                                              actions={
                                                <div className="action-row">
                                                  <button onClick={() => addSnipToPlan(snip)} className="btn-action-primary">
                                                    <Plus className="w-4 h-4" /> {t('add_to_plan')}
                                                  </button>
                                                  {itinerary && (
                                                    <button onClick={() => addSnipToItinerary(snip)} className="btn-action-secondary">
                                                      <Plus className="w-4 h-4" /> {t('add_to_itinerary_btn')}
                                                    </button>
                                                  )}
                                                  <button onClick={() => { track('snip_deleted', { snip_name: snip.name }); deleteSnip(snip._id); }} className="btn-action-danger">
                                                    <Trash2 className="w-4 h-4" /> {t('remove')}
                                                  </button>
                                                </div>
                                              }
                                            />
                                          : <div className="action-row">
                                              <button onClick={() => addSnipToPlan(snip)} className="btn-action-primary">
                                                <Plus className="w-4 h-4" /> {t('add_to_plan')}
                                              </button>
                                              {itinerary && (
                                                <button onClick={() => addSnipToItinerary(snip)} className="btn-action-secondary">
                                                  <Plus className="w-4 h-4" /> {t('add_to_itinerary_btn')}
                                                </button>
                                              )}
                                              <button onClick={() => { track('snip_deleted', { snip_name: snip.name }); deleteSnip(snip._id); }} className="btn-action-danger">
                                                <Trash2 className="w-4 h-4" /> {t('remove')}
                                              </button>
                                            </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* ── Trips section ─────────────────────────────────────── */}
              <div>
                {snips.length > 0 && <h2 className="text-sm font-semibold text-gray-700 mb-3">{t('trips')}</h2>}
                {tripsLoading ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400 text-sm">{t('loading_trips')}</p>
                  </div>
                ) : savedTrips.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">{t('no_saved_trips')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                  {savedTrips.map((trip) => (
              <div key={trip._id} className="bg-white rounded-2xl p-4 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  {renamingTripId === trip._id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleRenameTrip(trip._id)}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                      <button onClick={() => handleRenameTrip(trip._id)} className="text-green-600 hover:text-green-700">
                        <Check className="w-4 h-4" />
                      </button>
                      <button onClick={() => setRenamingTripId(null)} className="text-gray-400 hover:text-gray-600">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 truncate">{trip.name || t('untitled_trip')}</h3>
                      <p className="text-xs text-gray-400">
                        {trip.days?.length || 0} days · {trip.savedAt ? new Date(trip.savedAt).toLocaleDateString() : ''}
                      </p>
                    </div>
                  )}
                  {renamingTripId !== trip._id && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => { setRenamingTripId(trip._id); setRenameValue(trip.name || ''); }}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                        title={t('rename')}
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleCloneTrip(trip)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                        title="Clone"
                      >
                        <Copy className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteTrip(trip._id)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
                {renamingTripId !== trip._id && (
                  <button
                    onClick={() => handleLoadTrip(trip)}
                    className="mt-3 w-full bg-blue-50 text-blue-600 py-2 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors"
                  >
                    {t('open')}
                  </button>
                )}
              </div>
            ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
      {/* ── User Menu + New Trip Confirmation ── */}
      {showUserMenu && user && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
          <div className="fixed right-4 top-20 bg-white rounded-xl shadow-xl z-50 overflow-hidden w-52">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
              <p className="text-xs text-gray-500">{t('signed_in_as')}</p>
              <p className="text-sm font-medium text-gray-800 truncate">{user.email}</p>
            </div>
            {user.email === 'jtclick@hotmail.com' && (
              <>
                <button
                  onClick={() => {
                    const next = !trackingEnabled;
                    setAnalyticsEnabled(next);
                    setTrackingEnabled(next);
                  }}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100"
                >
                  <span>Analytics</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${trackingEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                    {trackingEnabled ? 'ON' : 'OFF'}
                  </span>
                </button>
                <button
                  onClick={openChangePassword}
                  className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100"
                >
                  {t('change_password')}
                </button>
                <button
                  onClick={openDeleteUser}
                  className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 border-b border-gray-100"
                >
                  {t('delete_user_menu')}
                </button>
              </>
            )}
            <button
              onClick={() => { handleLogout(); setShowUserMenu(false); }}
              className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              {t('sign_out')}
            </button>
          </div>
        </>
      )}

      {/* New Trip tab confirmation — always shown when itinerary exists, regardless of save state */}
      {showNewTripTabConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-lg font-bold text-gray-800 mb-2">{t('new_trip_tab_title')}</h2>
            <p className="text-sm text-gray-600 mb-5">{t('new_trip_tab_msg')}</p>
            <div className="flex gap-3">
              <button onClick={() => setShowNewTripTabConfirm(false)} className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-200">
                {t('cancel')}
              </button>
              <button onClick={() => { setShowNewTripTabConfirm(false); clearCurrentTrip(); }} className="flex-1 bg-red-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-red-700">
                {t('start_fresh')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Trip Confirmation */}
      {showNewTripConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-lg font-bold text-gray-800 mb-2">{t('unsaved_changes_title')}</h2>
            <p className="text-sm text-gray-600 mb-5">{t('unsaved_changes_msg')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowNewTripConfirm(false)}
                className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                {t('cancel')}
              </button>
              <button
                onClick={clearCurrentTrip}
                className="flex-1 bg-red-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-red-700"
              >
                {t('discard_start_new')}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingLoadTrip && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-lg font-bold text-gray-800 mb-2">{t('load_trip_confirm_title')}</h2>
            <p className="text-sm text-gray-600 mb-5">{t('load_trip_confirm_msg')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setPendingLoadTrip(null)}
                className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => { doLoadTrip(pendingLoadTrip); setPendingLoadTrip(null); }}
                className="flex-1 bg-orange-500 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-orange-600"
              >
                {t('open')}
              </button>
            </div>
          </div>
        </div>
      )}

      {dayToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-green-600 text-white text-sm font-medium px-4 py-2.5 rounded-full shadow-lg pointer-events-none animate-fade-in-up">
          <Check className="w-4 h-4 flex-shrink-0" />
          {dayToast}
        </div>
      )}

      {filterOnboardingCity !== null && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-lg font-bold text-gray-800 mb-1">{t('filter_onboarding_msg')}</h2>
            <p className="text-sm text-gray-500 mb-4">{t('filter_onboarding_sub')}</p>
            <div className="flex flex-wrap gap-2 mb-5">
              {ATTRACTION_FILTERS.filter(f => f.label !== 'All').map(f => {
                const active = filterOnboardingPending.includes(f.label);
                return (
                  <button
                    key={f.label}
                    onClick={() => setFilterOnboardingPending(prev =>
                      prev.includes(f.label) ? prev.filter(l => l !== f.label) : [...prev, f.label]
                    )}
                    className={`text-sm px-3 py-1.5 rounded-full font-medium transition-colors ${active ? 'chip-active' : 'chip-inactive'}`}
                  >
                    {t('filterLabel', f.label)}
                  </button>
                );
              })}
            </div>
            <button
              disabled={filterOnboardingPending.length === 0}
              onClick={() => {
                const next = filterOnboardingPending;
                try { localStorage.setItem('sniptrip_filters', JSON.stringify(next)); } catch {}
                try { localStorage.setItem('sniptrip_filter_onboarding', '1'); } catch {}
                filterOnboardingDoneRef.current = true;
                setAttractionFilters(next);
                const idx = filterOnboardingCity;
                setFilterOnboardingCity(null);
                autoPopulateAttractions(idx, null, true);
              }}
              className="btn-primary w-full mb-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t('save')}
            </button>
            <button
              onClick={() => {
                try { localStorage.setItem('sniptrip_filter_onboarding', '1'); } catch {}
                filterOnboardingDoneRef.current = true;
                setFilterOnboardingCity(null);
              }}
              className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              {t('skip')}
            </button>
          </div>
        </div>
      )}

      {pendingCityAdd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-lg font-bold text-gray-800 mb-2">Add to existing trip?</h2>
            <p className="text-sm text-gray-600 mb-5">
              You have <span className="font-semibold">&ldquo;{itinerary?.name || t('untitled_trip')}&rdquo;</span> loaded. Add <span className="font-semibold">{pendingCityAdd.cityData?.name}</span> to it, or start a new trip?
            </p>
            <div className="flex gap-3">
              <button
                onClick={confirmStartNewTrip}
                className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                New trip
              </button>
              <button
                onClick={confirmAddToExisting}
                className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                Add to trip
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Auto-populate prompt ── */}
      {pendingAutoPopulate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <p className="text-base font-semibold text-gray-900 mb-1">
              {t('auto_populate_prompt', pendingAutoPopulate.cityToAdd.name.split(',')[0])}
            </p>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => { autoPopulateAttractions(pendingAutoPopulate.cityIndex, pendingAutoPopulate.cityToAdd); setPendingAutoPopulate(null); }}
                className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                {t('auto_populate_yes')}
              </button>
              <button
                onClick={() => setPendingAutoPopulate(null)}
                className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                {t('auto_populate_no')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Auth Modal ── */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">
                {authMode === 'login' ? t('sign_in') : t('create_account')}
              </h2>
              <button
                onClick={() => { setShowAuthModal(false); setAuthError(''); setAuthReason(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {authReason === 'snip' && (
              <p className="text-sm text-blue-600 bg-blue-50 rounded-lg px-3 py-2 mb-3">{t('snip_requires_account')}</p>
            )}
            <div className="space-y-3">
              <input
                type="email"
                value={authEmail}
                onChange={(e) => setAuthEmail(e.target.value)}
                placeholder={t('email_placeholder')}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="password"
                value={authPassword}
                onChange={(e) => setAuthPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAuth(authMode)}
                placeholder={t('password_placeholder')}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {authMode === 'register' && (
                <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAgreed}
                    onChange={e => setTermsAgreed(e.target.checked)}
                    className="mt-0.5 flex-shrink-0 accent-blue-600"
                  />
                  <span>
                    {t('i_agree_to')}{' '}
                    <button type="button" onClick={() => openLegal('terms')} className="text-blue-600 hover:underline">{t('terms_of_service')}</button>
                    {' '}{t('and')}{' '}
                    <button type="button" onClick={() => openLegal('privacy')} className="text-blue-600 hover:underline">{t('privacy_policy')}</button>
                  </span>
                </label>
              )}
              {authError && <p className="text-red-600 text-xs">{authError}</p>}
              <button
                onClick={() => handleAuth(authMode)}
                disabled={authLoading || (authMode === 'register' && !termsAgreed)}
                className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {authLoading ? '...' : authMode === 'login' ? t('sign_in') : t('create_account')}
              </button>
            </div>
            <p className="text-center text-sm text-gray-500 mt-4">
              {authMode === 'login' ? t('no_account') : t('have_account')}
              <button
                onClick={() => { setAuthMode(authMode === 'login' ? 'register' : 'login'); setAuthError(''); setTermsAgreed(true); }}
                className="text-blue-600 hover:underline font-medium"
              >
                {authMode === 'login' ? t('create_account') : t('sign_in')}
              </button>
            </p>
          </div>
        </div>
      )}

      {/* ── Change Password Modal (admin only) ── */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">{t('change_password')}</h2>
              <button
                onClick={() => { setShowChangePassword(false); setCpError(''); setCpSuccess(''); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <select
                value={cpSelectedUserId}
                onChange={(e) => setCpSelectedUserId(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">{t('select_user')}</option>
                {cpUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.email}</option>
                ))}
              </select>
              <input
                type="password"
                value={cpNewPassword}
                onChange={(e) => setCpNewPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleChangePassword()}
                placeholder={t('new_password_placeholder')}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {cpError && <p className="text-red-600 text-xs">{cpError}</p>}
              {cpSuccess && <p className="text-green-600 text-xs">{cpSuccess}</p>}
              <button
                onClick={handleChangePassword}
                disabled={cpLoading}
                className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {cpLoading ? '...' : t('update_password')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Delete User Modal (admin only) ── */}
      {showDeleteUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800">{t('delete_user_menu')}</h2>
              <button
                onClick={() => { setShowDeleteUser(false); setDuError(''); setDuSuccess(''); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <select
                value={duSelectedEmail}
                onChange={(e) => setDuSelectedEmail(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
              >
                <option value="">{t('select_user')}</option>
                {duUsers.map(u => (
                  <option key={u.id} value={u.email}>{u.email}</option>
                ))}
              </select>
              {duError && <p className="text-red-600 text-xs">{duError}</p>}
              {duSuccess && <p className="text-green-600 text-xs">{duSuccess}</p>}
              <button
                onClick={handleDeleteUser}
                disabled={duLoading || !duSelectedEmail}
                className="w-full bg-red-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {duLoading ? '...' : t('delete_user_trips')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Attraction add toast ── */}
      {attrAddToast && (
        <div className="fixed bottom-24 left-0 right-0 flex justify-center z-50 pointer-events-none px-4">
          <div className="bg-gray-800 text-white text-sm rounded-xl px-4 py-2.5 shadow-lg animate-fade-in-up">
            {attrAddToast}
          </div>
        </div>
      )}

      {/* ── Photo Mode Overlay ── */}
      {photoMode && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          {/* Top bar */}
          <div className="absolute top-0 left-0 right-0 z-20 flex items-center gap-2 p-4 pt-5">
            <div className="flex gap-1 flex-1 overflow-hidden">
              {photoFeed.map((_, i) => (
                <div key={i} className={`h-0.5 rounded-full flex-1 max-w-10 transition-colors ${i <= photoFeedIdx ? 'bg-white' : 'bg-white/25'}`} />
              ))}
            </div>
            <button onClick={() => { track('photo_mode_closed', { attractions_seen: photoFeedIdx }); setPhotoMode(false); }} className="p-2 bg-black/40 rounded-full text-white flex-shrink-0 ml-1">
              <X className="w-5 h-5" />
            </button>
          </div>

          {photoFeedLoading ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-10 h-10 border-2 border-white/25 border-t-white rounded-full animate-spin mb-4" />
              <span key={photoFeedLoadingMsgIdx} className="animate-fade-in-up text-white/60 text-sm">{t(PHOTO_LOADING_MESSAGES[photoFeedLoadingMsgIdx])}</span>
              <span className="flex gap-1 items-center mt-3">
                <span className="generating-dot w-1.5 h-1.5 rounded-full bg-white/40" />
                <span className="generating-dot w-1.5 h-1.5 rounded-full bg-white/40" />
                <span className="generating-dot w-1.5 h-1.5 rounded-full bg-white/40" />
              </span>
            </div>
          ) : (photoFeed.length === 0 || photoFeed[0]?._error) ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <Images className="w-14 h-14 text-white/20 mb-4" />
              <p className="text-white text-lg font-semibold mb-2">
                {photoFeed[0]?._error ? t('could_not_load_places') : t('no_places')}
              </p>
              <p className="text-white/50 text-sm mb-6">
                {photoFeed[0]?._error
                  ? t('ai_error')
                  : t('add_cities_suggestions')}
              </p>
              <div className="flex gap-3">
                {photoFeed[0]?._error && (
                  <button onClick={enterPhotoMode} className="px-5 py-3 bg-blue-500/80 text-white rounded-xl text-sm">{t('retry')}</button>
                )}
                <button onClick={() => { track('photo_mode_closed', { attractions_seen: photoFeedIdx }); setPhotoMode(false); }} className="px-5 py-3 bg-white/15 text-white rounded-xl text-sm">{t('close')}</button>
              </div>
            </div>
          ) : photoFeedIdx >= photoFeed.length ? (
            photoFeedFetchingMore ? (
              <div className="flex-1 flex flex-col items-center justify-center">
                <div className="w-10 h-10 border-2 border-white/25 border-t-white rounded-full animate-spin mb-4" />
                <p className="text-white/60 text-sm">{t('finding_more_places')}</p>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <Check className="w-14 h-14 text-white/20 mb-4" />
                <p className="text-white text-lg font-semibold mb-2">{t('seen_it_all')}</p>
                <p className="text-white/50 text-sm mb-6">
                  {photoFeed.length >= 50 ? t('photo_limit_msg') : t('check_snips')}
                </p>
                <button onClick={() => { track('photo_mode_closed', { attractions_seen: photoFeedIdx }); setPhotoMode(false); }} className="px-6 py-3 bg-white/15 text-white rounded-xl text-sm">{t('close')}</button>
              </div>
            )
          ) : (() => {
            const item = photoFeed[photoFeedIdx];
            return (
              <div
                className="flex-1 relative select-none touch-none"
                onTouchStart={handlePhotoTouchStart}
                onTouchEnd={handlePhotoTouchEnd}
              >
                {item.photoRef ? (
                  <img
                    key={item.id}
                    src={`/api/place-photo?ref=${encodeURIComponent(item.photoRef)}&maxwidth=800`}
                    alt={item.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
                    {photoFeedFetchingPhoto ? (
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-white/20 border-t-white/60 rounded-full animate-spin mx-auto mb-2" />
                        <p className="text-white/30 text-xs">{t('loading')}</p>
                      </div>
                    ) : (
                      <p className="text-white/25 text-sm">{t('photo_unavailable')}</p>
                    )}
                  </div>
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-black/40 pointer-events-none" />

                <div className="absolute bottom-0 left-0 right-0 p-5 pb-10">
                  <p className="text-white/55 text-sm mb-0.5">{item.cityName}</p>
                  <h2 className="text-white text-2xl font-bold mb-1.5 leading-snug">{item.name}</h2>
                  {item.reason && <p className="text-white/65 text-sm mb-3 leading-snug">{item.reason}</p>}
                  {item.types?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {item.types.slice(0, 3).map(type => (
                        <span key={type} className="text-xs bg-white/15 text-white/85 rounded-full px-2.5 py-0.5">{t('filterLabel', type)}</span>
                      ))}
                    </div>
                  )}
                  <a
                    href={`https://www.youtube.com/results?search_query=${encodeURIComponent(`${item.name} ${item.cityName || ''}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => track('youtube_clicked', { attraction_name: item.name, source: 'photo_mode' })}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-red-400 bg-red-950/60 hover:bg-red-900/70 py-2 px-2.5 rounded-lg transition-colors mb-3"
                  >
                    ▶ YouTube
                  </a>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { track('photo_attraction_skipped', { attraction_name: item.name }); photoFeedNext(); }}
                      className="flex-1 py-3.5 bg-white/12 border border-white/20 text-white rounded-2xl text-sm font-medium"
                    >
                      {t('skip')}
                    </button>
                    <button
                      onClick={() => handlePhotoSnip(item)}
                      className="flex-1 py-3.5 bg-blue-500/85 text-white rounded-2xl text-sm font-medium flex items-center justify-center gap-1.5"
                    >
                      <Bookmark className="w-3.5 h-3.5" /> {t('snip')}
                    </button>
                    {screen === 'itinerary' && itinerary ? (
                      <button
                        onClick={() => { track('photo_attraction_added_to_itinerary', { attraction_name: item.name }); addSnipToItinerary({ type: 'attraction', name: item.name, cityName: item.cityName, types: item.types, placeId: item.placeId }); photoFeedNext(); }}
                        className="flex-1 py-3.5 bg-blue-500/85 text-white rounded-2xl text-sm font-medium"
                      >
                        {t('plus_itinerary')}
                      </button>
                    ) : (
                      <button
                        onClick={() => handlePhotoAddToPlan(item)}
                        className="flex-1 py-3.5 bg-blue-500/85 text-white rounded-2xl text-sm font-medium"
                      >
                        {t('plus_plan')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* First-time tutorial */}
          {showPhotoTutorial && (
            <div className="absolute inset-0 z-30 bg-black/80 flex items-center justify-center p-6" onClick={dismissPhotoTutorial}>
              <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 max-w-xs w-full text-center" onClick={e => e.stopPropagation()}>
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Images className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-white font-bold text-lg mb-1">{t('photo_explore_title')}</h3>
                <p className="text-white/50 text-sm mb-5">{t('photo_explore_sub')}</p>
                <div className="space-y-3 text-sm text-left mb-4">
                  <div className="flex items-start gap-3">
                    <span className="w-24 flex-shrink-0 text-white/55 text-xs font-medium pt-0.5">{t('skip')}</span>
                    <span className="text-white/75">{t('tutorial_skip_desc')}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-24 flex-shrink-0 flex items-center gap-1 text-blue-400 text-xs font-medium pt-0.5">
                      <Bookmark className="w-3 h-3" /> {t('snip')}
                    </span>
                    <span className="text-white/75">{t('tutorial_snip_desc')}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-24 flex-shrink-0 text-white/55 text-xs font-medium pt-0.5">
                      {screen === 'itinerary' && itinerary ? t('plus_itinerary') : t('plus_plan')}
                    </span>
                    <span className="text-white/75">
                      {screen === 'itinerary' && itinerary ? t('tutorial_add_itinerary_desc') : t('tutorial_add_plan_desc')}
                    </span>
                  </div>
                </div>
                <p className="text-white/30 text-xs mb-5">{t('tutorial_swipe_hint')}</p>
                <button
                  className="w-full py-3 btn-primary rounded-xl text-sm font-medium"
                  onClick={dismissPhotoTutorial}
                >
                  {t('lets_go')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {/* ── Footer (language switcher + legal links) ── */}
      <footer className="text-center py-6 px-4 text-xs text-gray-400 border-t border-gray-100 mt-4 space-y-2">
        <div className="space-x-3">
          <span>{t('copyright')}</span>
          <button onClick={() => openLegal('terms')} className="hover:text-gray-600 hover:underline">{t('terms')}</button>
          <button onClick={() => openLegal('privacy')} className="hover:text-gray-600 hover:underline">{t('privacy')}</button>
        </div>
        <details className="group">
          <summary className="cursor-pointer list-none hover:text-gray-600 inline-flex items-center gap-1 select-none">
            {t('sample_itineraries')}
            <span className="text-[10px] group-open:rotate-180 transition-transform inline-block">▾</span>
          </summary>
          <div className="mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1">
            {HARDCODED_DESTINATIONS.map(dest => {
              const slug = dest.name.toLowerCase().replace(/\s+/g, '-');
              return (
                <a key={slug} href={`/itineraries/${slug}`} className="hover:text-gray-600 hover:underline">
                  {t(dest.key)}
                </a>
              );
            })}
            <a href="/itineraries" className="hover:text-gray-600 hover:underline">{t('browse_all_itineraries')}</a>
          </div>
        </details>
        <div>
          <select
            value={lang}
            onChange={e => {
              const next = e.target.value;
              setLang(next);
              try { document.cookie = `sniptrip_lang=${next};path=/;max-age=31536000`; } catch { /* ignore */ }
              track('language_switched', { language: next });
            }}
            className="text-xs text-gray-400 bg-transparent border border-gray-200 rounded px-2 py-0.5 focus:outline-none cursor-pointer"
          >
            <option value="en">English</option>
            <option value="de">Deutsch</option>
            <option value="fr">Français</option>
            <option value="es">Español</option>
            <option value="zh-TW">繁體中文</option>
            <option value="zh-CN">简体中文</option>
            <option value="ja">日本語</option>
            <option value="ko">한국어</option>
          </select>
        </div>
      </footer>

      {/* ── Legal Page Overlay ── */}
      {legalPage && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-6 py-8">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-xl font-bold text-gray-800">
                {legalPage === 'terms' ? t('terms_of_service') : t('privacy_policy')}
              </h1>
              <button
                onClick={() => setLegalPage(null)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {legalHtml
              ? <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed space-y-2" dangerouslySetInnerHTML={{ __html: legalHtml }} />
              : <p className="text-gray-400 text-sm">{t('loading')}</p>
            }
            <div className="mt-8 pt-4 border-t border-gray-100">
              <button
                onClick={() => setLegalPage(null)}
                className="text-sm text-blue-600 hover:underline"
              >
                {t('back_to_app')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Feedback button + modal ── */}
      {!photoMode && !showCookieBanner && (
        <button
          onClick={() => setShowFeedback(true)}
          className={`fixed right-4 z-40 bg-blue-600 text-white pl-3 pr-3.5 py-2 rounded-full shadow-lg text-sm font-medium flex items-center gap-1.5 hover:bg-blue-700 transition-colors ${chatMode ? 'bottom-20' : 'bottom-6'}`}
        >
          <MessageSquare className="w-4 h-4" />
          Feedback
        </button>
      )}
      {showFeedback && (
        <>
          <div className="fixed inset-0 z-50 bg-black/50" onClick={() => setShowFeedback(false)} />
          <div className="fixed inset-x-4 top-16 bottom-4 z-50 bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden" style={{ maxWidth: 520, margin: '0 auto' }}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
              <h2 className="font-semibold text-gray-800">Share Feedback</h2>
              <button onClick={() => setShowFeedback(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <iframe
              src="https://form.jotform.com/261315698104053"
              className="flex-1 w-full"
              style={{ border: 'none' }}
              title="Feedback Form"
            />
          </div>
        </>
      )}

      {/* ── Cookie consent banner ── */}
      {showCookieBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg px-4 py-4">
          <div className="max-w-xl mx-auto">
            <p className="text-sm text-gray-700 mb-3">
              We use cookies to understand how you use SnipTrip and improve the experience.{' '}
              <button onClick={() => openLegal('privacy')} className="underline text-blue-600 hover:text-blue-800">Privacy Policy</button>
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => { setCookieConsent(true); setShowCookieBanner(false); }}
                className="btn-primary px-5 py-2 rounded-lg text-sm font-medium"
              >
                Accept
              </button>
              <button
                onClick={() => { setCookieConsent(false); setShowCookieBanner(false); }}
                className="px-5 py-2 rounded-lg text-sm font-medium text-gray-500 bg-gray-100 hover:bg-gray-200"
              >
                Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ErrorBoundary>
  );
}
